import { useEffect, useState, useRef, useMemo } from 'react';
import DataTable from '../components/DataTable';
import HorizontalBarChart from '../components/HorizontalBarChart';
import FleetStyleKpiCard from '../components/FleetStyleKpiCard';
import { Download, HelpCircle, Pencil } from 'lucide-react';
import { usePeriod } from '../contexts/PeriodContext';
import { useUseCase } from '../contexts/UseCaseContext';
import { useDemoMode } from '../contexts/DemoModeContext';
import { useOptimizationScope } from '../contexts/OptimizationScopeContext';
import { logger } from '../utils/logger';
import { createPortal } from 'react-dom';
import { parseCSV, formatCurrency, formatNumber, formatPercentage, CSVRow } from '../utils/csvParser';
import { API_ENDPOINTS } from '../config/api';
import { STATUS_COLORS } from '../config/colors';
import { redactVehicleId, redactVehicleType, redactStationName, resetRedactionMaps } from '../utils/demoRedaction';

export default function Optimization() {
  const { selectedPeriod } = usePeriod();
  const { useCase } = useUseCase();
  const { isDemoMode } = useDemoMode();
  // ✅ Convert useCase to API serviceType: 'emergency' -> 'emergency_services', 'logistics' -> 'logistics'
  const serviceType = useCase === 'emergency' ? 'emergency_services' : 'logistics';
  
  // Reset redaction maps when demo mode changes
  useEffect(() => {
    resetRedactionMaps();
  }, [isDemoMode]);
  
  // Optimization data state (Phase 1 MVP) - Final validation metrics only
  const [optimizationData, setOptimizationData] = useState<{
    aggregateCostReport: CSVRow[];
    simulationSummary: CSVRow[];
    initialUtilization: CSVRow[];
    finalUtilization: CSVRow[];
    optimizationLog: CSVRow[];
    loading: boolean;
    error: string | null;
  }>({
    aggregateCostReport: [],
    simulationSummary: [],
    initialUtilization: [],
    finalUtilization: [],
    optimizationLog: [],
    loading: false,
    error: null,
  });

  // State for optimization metadata (user configuration)
  const [optimizationMetadata, setOptimizationMetadata] = useState<{
    optimization_metadata?: {
      timestamp?: string;
      num_runs?: number;
      region?: string;
      optimization_goal?: string;
      optimization_goal_description?: string;
      optimization_scope?: {
        selected_vehicles?: string | string[];
        selected_vehicle_types?: string | string[];
        selected_stations?: string | string[];
      };
      optimization_weights?: {
        utilization_weight?: number;
        compatibility_weight?: number;
        utilization_weight_percentage?: number;
        compatibility_weight_percentage?: number;
      };
      operational_constraints?: {
        min_vehicles_per_station?: number;
        target_response_time_minutes?: number;
      };
      simulation_settings?: {
        use_historical_data?: boolean;
        historical_start_date?: string | null;
        historical_end_date?: string | null;
        incident_scaling_factor?: number;
        pumping_incident_ratio?: number;
        process_inactive_vehicles?: boolean;
        process_workload_redistribution?: boolean;
      };
      confidence_thresholds?: {
        strong_threshold?: number;
        moderate_threshold?: number;
        potential_threshold?: number;
      };
    };
  } | null>(null);

  // State for selected simulation run (defaults to best run)
  const [selectedRunIndex, setSelectedRunIndex] = useState<number>(0);

  // State for run-specific data (fetched when a specific run is selected)
  const [runSpecificData, setRunSpecificData] = useState<{
    initialUtilization: CSVRow[];
    finalUtilization: CSVRow[];
    costReport: CSVRow[];
    optimizationLog: CSVRow[];
    loading: boolean;
    error: string | null;
  }>({
    initialUtilization: [],
    finalUtilization: [],
    costReport: [],
    optimizationLog: [],
    loading: false,
    error: null,
  });

  // State for incident and event log data
  const [incidentData, setIncidentData] = useState<{
    incidentLog: CSVRow[];
    initialEventLog: CSVRow[];
    optimizedEventLog: CSVRow[];
    dispatchComparison: {
      incident_id: string;
      initial_dispatch: string;
      optimized_dispatch: string;
      has_change: boolean;
      added_vehicles: string;
      removed_vehicles: string;
    }[];
    loading: boolean;
    error: string | null;
  }>({
    incidentLog: [],
    initialEventLog: [],
    optimizedEventLog: [],
    dispatchComparison: [],
    loading: false,
    error: null,
  });

  // State for fuel consumption by vehicle type data
  const [, setFuelByVehicleType] = useState<{
    initial: Record<string, number>;  // {vehicle_type: fuel_consumed_litres}
    optimized: Record<string, number>;
    loading: boolean;
    error: string | null;
  }>({
    initial: {},
    optimized: {},
    loading: false,
    error: null,
  });

  // State for incident summary view (original or optimized dispatch)
  const [incidentViewType, setIncidentViewType] = useState<'original' | 'optimized'>('original');

  const [selectedInterchangeabilityVehicle, setSelectedInterchangeabilityVehicle] = useState<string | null>(null);
  const [interchangeabilityCSV, setInterchangeabilityCSV] = useState<CSVRow[]>([]);
  const [optimizationScenario, setOptimizationScenario] = useState<'historical' | 'future'>('historical');
  const [optimizationObjective, setOptimizationObjective] = useState<string>('minimise_fleet_size');
  
  // Use shared optimization scope context (shared with Electrification tab)
  const {
    selectedVehicles,
    setSelectedVehicles,
    selectedVehicleTypes,
    setSelectedVehicleTypes,
    selectedStations,
    setSelectedStations,
    availableVehicles,
    availableVehicleTypes,
    availableStations,
    addAvailableVehicles,
    addAvailableVehicleTypes,
    addAvailableStations,
  } = useOptimizationScope();
  
  // State for optimization parameters (from backend)
  const [optimizationParams, setOptimizationParams] = useState({
    utilizationWeight: 30, // 0-100, represents 0.0-1.0 (default matches "Minimise Fleet Size" goal)
    compatibilityWeight: 70, // 0-100, represents 0.0-1.0 (default matches "Minimise Fleet Size" goal)
    minVehiclesPerStation: 1, // Minimum TOTAL vehicles per station (across all types)
    targetResponseTimeMinutes: 10.0,
    maxStationClosures: 3, // Maximum single-vehicle stations that can be closed
    criticalVehicleTypes: ['Pumping Appliance'] as string[], // Vehicle types that must maintain at least 1 per station
  });
  const defaultWeights = {
    geographicOverlap: 25,
    temporalCompatibility: 25,
    activitySimilarity: 25,
    redistributionImpact: 25,
  };
  const [weights, setWeights] = useState({ ...defaultWeights });
  const [hoveredMetric, setHoveredMetric] = useState<string | null>(null);
  
  // State for editable weight percentages
  const [editingWeight, setEditingWeight] = useState<'geographicOverlap' | 'temporalCompatibility' | 'activitySimilarity' | 'redistributionImpact' | null>(null);
  
  // State for editable unit costs
  const [unitCosts, setUnitCosts] = useState<Record<string, number>>({});
  const [editingUnitCost, setEditingUnitCost] = useState<{ vehicleType: string } | null>(null);
  
  // State for fleet filter (Active/Inactive)
  const [fleetFilter, setFleetFilter] = useState<'active' | 'inactive'>('active');

  // State for incident scaling factor
  // Historical mode default: 0.05 (5% of incidents for faster simulation)
  // Future mode default: 1.0 (100% of incidents)
  const [incidentScalingFactor, setIncidentScalingFactor] = useState<number>(0.05);
  // State for pumping incident ratio (only for future scenario)
  const [pumpingIncidentRatio, setPumpingIncidentRatio] = useState<number>(1.0);

  // State for available optimization result folders (filtered by scenario)
  const [availableFolders, setAvailableFolders] = useState<string[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [foldersLoading, setFoldersLoading] = useState<boolean>(false);

  // Fetch available folders when scenario changes
  useEffect(() => {
    let isCancelled = false;
    
    const fetchFolders = async () => {
      setFoldersLoading(true);
      try {
        // Determine suffix based on scenario: '_past' for historical, '_future' for future
        const suffix = optimizationScenario === 'historical' ? '_past' : '_future';
        const response = await fetch(API_ENDPOINTS.optimizationResults(suffix));
        
        if (!isCancelled && response.ok) {
          const data = await response.json();
          setAvailableFolders(data.results || []);
          // Auto-select the most recent folder (first in the list, already sorted by server)
          if (data.latest) {
            setSelectedFolder(data.latest);
            logger.info(`[Optimization] Auto-selected most recent ${suffix} folder: ${data.latest}`);
          } else {
            setSelectedFolder(null);
            logger.warn(`[Optimization] No ${suffix} folders found`);
          }
        }
      } catch (error) {
        if (!isCancelled) {
          logger.error('[Optimization] Failed to fetch folders:', error);
          setAvailableFolders([]);
          setSelectedFolder(null);
        }
      } finally {
        if (!isCancelled) {
          setFoldersLoading(false);
        }
      }
    };
    
    fetchFolders();
    
    return () => {
      isCancelled = true;
    };
  }, [optimizationScenario]);

  useEffect(() => {
    // Reset all state when switching modes or periods
    setInterchangeabilityCSV([]);
    setSelectedInterchangeabilityVehicle(null);

    if (!selectedPeriod) {
      return;
    }

    const period = selectedPeriod as string;
    let isCancelled = false; // Flag to prevent state updates if component unmounts or mode changes

    import('../config/api').then(({ API_ENDPOINTS }) => {
      // ✅ Load data with serviceType for data isolation
      fetch(API_ENDPOINTS.vehicleInterchangeabilityPairsCSV(period, serviceType))
        .then((res: Response) => {
          if (isCancelled) return;
          return res.text();
        })
        .then((text: string | undefined) => {
          if (isCancelled || !text) return;
          setInterchangeabilityCSV(parseCSV(text));
        })
        .catch(err => {
          if (isCancelled) return;
          logger.error('Error loading interchangeability CSV:', err);
          setInterchangeabilityCSV([]);
        });
    });
    
    // Cleanup function to cancel in-flight requests when mode/period changes
    return () => {
      isCancelled = true;
    };
  }, [selectedPeriod, serviceType]); // ✅ Include serviceType in dependencies

  // Fetch optimization data - Final validation metrics only
  useEffect(() => {
    let isCancelled = false;

    const fetchOptimizationData = async () => {
      // Wait for selectedFolder to be determined from the folder list
      if (!selectedFolder) {
        logger.info('[Optimization] Waiting for folder selection...');
        return;
      }
      
      setOptimizationData(prev => ({ ...prev, loading: true, error: null }));

      try {
        // Use the dynamically selected folder based on scenario
        const folderName: string | undefined = selectedFolder;
        logger.info(`[Optimization] Loading data from folder: ${folderName}`);

        // Fetch aggregate cost report (final validation metrics)
        const costReportRes = await fetch(API_ENDPOINTS.optimizationAggregateCostReport(folderName));
        const costReportText = costReportRes.ok ? await costReportRes.text() : '';
        const aggregateCostReport = costReportText ? parseCSV(costReportText) : [];
        
        // Debug: Log the actual data structure
        if (aggregateCostReport.length > 0) {
          logger.info('Aggregate Cost Report columns:', Object.keys(aggregateCostReport[0]));
          logger.info('Aggregate Cost Report sample:', aggregateCostReport[0]);
        }

        // Fetch simulation summary (final validation metrics)
        const summaryRes = await fetch(API_ENDPOINTS.optimizationSimulationSummary(folderName));
        const summaryText = summaryRes.ok ? await summaryRes.text() : '';
        const simulationSummary = summaryText ? parseCSV(summaryText) : [];
        
        // Debug: Log the actual data structure
        if (simulationSummary.length > 0) {
          logger.info('Simulation Summary columns:', Object.keys(simulationSummary[0]));
          logger.info('Simulation Summary sample:', simulationSummary[0]);
        }

        // Fetch initial utilization data to count active vehicles per vehicle type
        const initialUtilRes = await fetch(API_ENDPOINTS.optimizationInitialUtilization(folderName));
        const initialUtilText = initialUtilRes.ok ? await initialUtilRes.text() : '';
        const initialUtilization = initialUtilText ? parseCSV(initialUtilText) : [];
        
        // Debug: Log the actual data structure
        if (initialUtilization.length > 0) {
          logger.info('Initial Utilization columns:', Object.keys(initialUtilization[0]));
          logger.info('Initial Utilization sample:', initialUtilization[0]);
          logger.info('Initial Utilization row count:', initialUtilization.length);
        } else {
          logger.warn('No initial utilisation data loaded');
        }

        // Fetch final utilization data to count active vehicles after optimization
        const finalUtilRes = await fetch(API_ENDPOINTS.optimizationFinalUtilization(folderName));
        const finalUtilText = finalUtilRes.ok ? await finalUtilRes.text() : '';
        const finalUtilization = finalUtilText ? parseCSV(finalUtilText) : [];
        
        // Debug: Log the actual data structure
        if (finalUtilization.length > 0) {
          logger.info('Final Utilization columns:', Object.keys(finalUtilization[0]));
          logger.info('Final Utilization sample:', finalUtilization[0]);
          logger.info('Final Utilization row count:', finalUtilization.length);
        } else {
          logger.warn('No final utilisation data loaded');
        }

        // Fetch optimization log to identify vehicles that received redistributed workload
        const optimizationLogRes = await fetch(API_ENDPOINTS.optimizationLog(folderName));
        const optimizationLogText = optimizationLogRes.ok ? await optimizationLogRes.text() : '';
        const optimizationLog = optimizationLogText ? parseCSV(optimizationLogText) : [];
        
        // Debug: Log the actual data structure
        if (optimizationLog.length > 0) {
          logger.info('Optimization Log columns:', Object.keys(optimizationLog[0]));
          logger.info('Optimization Log sample:', optimizationLog[0]);
          logger.info('Optimization Log row count:', optimizationLog.length);
        } else {
          logger.warn('No optimization log data loaded');
        }

        // Fetch optimization metadata (user configuration)
        let metadata = null;
        try {
          const metadataRes = await fetch(API_ENDPOINTS.optimizationMetadata(folderName));
          if (metadataRes.ok) {
            metadata = await metadataRes.json();
            logger.info('Optimization metadata loaded:', metadata);
          } else {
            logger.warn('Optimization metadata not found (this is OK for older optimization runs)');
          }
        } catch (error) {
          logger.warn('Error loading optimization metadata:', error);
        }

        if (!isCancelled) {
          setOptimizationData({
            aggregateCostReport,
            simulationSummary,
            initialUtilization,
            finalUtilization,
            optimizationLog,
            loading: false,
            error: null
          });
          setOptimizationMetadata(metadata);
        }
      } catch (error) {
        if (!isCancelled) {
          logger.error('Error loading optimization data:', error);
          setOptimizationData(prev => ({
            ...prev,
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to load optimization data'
          }));
        }
      }
    };

    fetchOptimizationData();

    return () => {
      isCancelled = true;
    };
  }, [selectedFolder]); // Fetch data when selected folder changes

  // Automatically select the best run based on optimization objective when simulation summary data loads
  useEffect(() => {
    if (optimizationData.simulationSummary.length === 0) {
      setSelectedRunIndex(0);
      return;
    }

    // Helper function to get column value
    const getColumnValue = (row: CSVRow, possibleNames: string[]): string => {
      for (const name of possibleNames) {
        const value = row[name];
        if (value !== undefined && value !== null && value !== '') {
          return String(value);
        }
      }
      return '';
    };

    // Find the best run based on the selected optimization objective
    let bestRunIndex = 0;
    let bestScore = -Infinity;

    optimizationData.simulationSummary.forEach((run, index) => {
      let score = -Infinity;

      switch (optimizationObjective) {
        case 'minimise_fleet_size': {
          // Select run with smallest final fleet size
          // Calculate final fleet as: total_active_fleet - active_vehicles_removed
          const totalActiveFleet = parseFloat(getColumnValue(run, [
            'total_active_fleet',
            'TOTAL ACTIVE FLEET'
          ])) || 0;
          
          const activeRemoved = parseFloat(getColumnValue(run, [
            'active_vehicles_removed',
            'ACTIVE VEHICLES REMOVED'
          ])) || 0;
          
          const finalFleet = totalActiveFleet - activeRemoved;
          
          // Smaller is better, so use negative value for comparison
          score = -finalFleet;
          break;
        }

        case 'maximise_utilisation': {
          // Select run with most vehicles removed (proxy for higher utilization of remaining fleet)
          const activeRemoved = parseFloat(getColumnValue(run, [
            'active_vehicles_removed',
            'ACTIVE VEHICLES REMOVED'
          ])) || 0;
          
          const inactiveRemoved = parseFloat(getColumnValue(run, [
            'inactive_vehicles_removed',
            'INACTIVE VEHICLES REMOVED'
          ])) || 0;
          
          const totalRemoved = activeRemoved + inactiveRemoved;
          
          // Also try to get vehicles removed from other column names
          const vehiclesRemoved = parseFloat(getColumnValue(run, [
            'vehicles_removed',
            'VEHICLES REMOVED',
            'num_vehic',
            'num_vehicles',
            'total_vehicles_removed'
          ])) || totalRemoved;
          
          score = vehiclesRemoved;
          break;
        }

        case 'balance_cost_service': {
          // Select run with highest cost savings (best balance of cost and service)
          const costSavings = parseFloat(getColumnValue(run, [
            'cost_savings',
            'COST SAVINGS',
            'total_cost_savings',
            'TOTAL COST SAVINGS'
          ])) || 0;
          
          score = costSavings;
          break;
        }

        case 'optimise_response_time': {
          // Select run with best response time (lowest average response time, or best improvement)
          // Prefer optimized_avg_response_time_minutes if available, otherwise use improvement
          const optimizedResponseTime = parseFloat(getColumnValue(run, [
            'optimized_avg_response_time_minutes',
            'OPTIMIZED_AVG_RESPONSE_TIME_MINUTES',
            'avg_response_time_minutes',
            'AVG_RESPONSE_TIME_MINUTES'
          ]));
          
          if (!isNaN(optimizedResponseTime) && optimizedResponseTime > 0) {
            // Lower is better, so use negative value for comparison
            score = -optimizedResponseTime;
          } else {
            // Fallback to response time improvement (positive improvement is better)
            const improvement = parseFloat(getColumnValue(run, [
              'response_time_improvement_minutes',
              'RESPONSE_TIME_IMPROVEMENT_MINUTES'
            ])) || 0;
            score = improvement;
          }
          break;
        }

        case 'minimise_response_time': {
          // Select run with lowest response time (minimize response time)
          const optimizedResponseTime = parseFloat(getColumnValue(run, [
            'optimized_avg_response_time_minutes',
            'OPTIMIZED_AVG_RESPONSE_TIME_MINUTES',
            'avg_response_time_minutes',
            'AVG_RESPONSE_TIME_MINUTES'
          ]));
          
          if (!isNaN(optimizedResponseTime) && optimizedResponseTime > 0) {
            // Lower is better, so use negative value for comparison
            score = -optimizedResponseTime;
          } else {
            // Fallback to response time improvement (positive improvement is better)
            const improvement = parseFloat(getColumnValue(run, [
              'response_time_improvement_minutes',
              'RESPONSE_TIME_IMPROVEMENT_MINUTES'
            ])) || 0;
            score = improvement;
          }
          break;
        }

        default: {
          // Default: use cost savings
          const costSavings = parseFloat(getColumnValue(run, [
            'cost_savings',
            'COST SAVINGS',
            'total_cost_savings',
            'TOTAL COST SAVINGS'
          ])) || 0;
          
          score = costSavings;
          break;
        }
      }

      if (score > bestScore) {
        bestRunIndex = index;
        bestScore = score;
      }
    });

    setSelectedRunIndex(bestRunIndex);
  }, [optimizationData.simulationSummary, optimizationObjective]);

  // Helper function to find column value by trying multiple name variations
  const getColumnValue = (row: CSVRow, possibleNames: string[]): string => {
    for (const name of possibleNames) {
      if (row[name] !== undefined && row[name] !== '') {
        return row[name];
      }
    }
    // Try case-insensitive match
    const rowKeys = Object.keys(row);
    for (const name of possibleNames) {
      const found = rowKeys.find(key => key.toLowerCase() === name.toLowerCase());
      if (found && row[found] !== undefined && row[found] !== '') {
        return row[found];
      }
    }
    return '0';
  };

  // Compute the best run index separately for display purposes
  const bestRunIndexForDisplay = useMemo(() => {
    if (optimizationData.simulationSummary.length === 0) return 0;

    let bestIndex = 0;
    let bestScore = -Infinity;

    optimizationData.simulationSummary.forEach((run, index) => {
      let score = -Infinity;

      switch (optimizationObjective) {
        case 'minimise_fleet_size': {
          // Calculate final fleet as: total_active_fleet - active_vehicles_removed
          const totalActiveFleet = parseFloat(getColumnValue(run, [
            'total_active_fleet',
            'TOTAL ACTIVE FLEET'
          ])) || 0;
          const activeRemoved = parseFloat(getColumnValue(run, [
            'active_vehicles_removed',
            'ACTIVE VEHICLES REMOVED'
          ])) || 0;
          const finalFleet = totalActiveFleet - activeRemoved;
          // Smaller is better, so use negative value for comparison
          score = -finalFleet;
          break;
        }
        case 'maximise_utilisation': {
          const activeRemoved = parseFloat(getColumnValue(run, [
            'active_vehicles_removed',
            'ACTIVE VEHICLES REMOVED'
          ])) || 0;
          const inactiveRemoved = parseFloat(getColumnValue(run, [
            'inactive_vehicles_removed',
            'INACTIVE VEHICLES REMOVED'
          ])) || 0;
          const totalRemoved = activeRemoved + inactiveRemoved;
          const vehiclesRemoved = parseFloat(getColumnValue(run, [
            'vehicles_removed',
            'VEHICLES REMOVED',
            'num_vehic',
            'num_vehicles',
            'total_vehicles_removed'
          ])) || totalRemoved;
          score = vehiclesRemoved;
          break;
        }
        case 'optimise_response_time': {
          // Select run with best response time (lowest average response time, or best improvement)
          const optimizedResponseTime = parseFloat(getColumnValue(run, [
            'optimized_avg_response_time_minutes',
            'OPTIMIZED_AVG_RESPONSE_TIME_MINUTES',
            'avg_response_time_minutes',
            'AVG_RESPONSE_TIME_MINUTES'
          ]));
          
          if (!isNaN(optimizedResponseTime) && optimizedResponseTime > 0) {
            // Lower is better, so use negative value for comparison
            score = -optimizedResponseTime;
          } else {
            // Fallback to response time improvement (positive improvement is better)
            const improvement = parseFloat(getColumnValue(run, [
              'response_time_improvement_minutes',
              'RESPONSE_TIME_IMPROVEMENT_MINUTES'
            ])) || 0;
            score = improvement;
          }
          break;
        }
        case 'minimise_response_time': {
          // Select run with lowest response time (minimize response time)
          const optimizedResponseTime = parseFloat(getColumnValue(run, [
            'optimized_avg_response_time_minutes',
            'OPTIMIZED_AVG_RESPONSE_TIME_MINUTES',
            'avg_response_time_minutes',
            'AVG_RESPONSE_TIME_MINUTES'
          ]));
          
          if (!isNaN(optimizedResponseTime) && optimizedResponseTime > 0) {
            // Lower is better, so use negative value for comparison
            score = -optimizedResponseTime;
          } else {
            // Fallback to response time improvement (positive improvement is better)
            const improvement = parseFloat(getColumnValue(run, [
              'response_time_improvement_minutes',
              'RESPONSE_TIME_IMPROVEMENT_MINUTES'
            ])) || 0;
            score = improvement;
          }
          break;
        }
        case 'balance_cost_service':
        default: {
          const costSavings = parseFloat(getColumnValue(run, [
            'cost_savings',
            'COST SAVINGS',
            'total_cost_savings',
            'TOTAL COST SAVINGS'
          ])) || 0;
          score = costSavings;
          break;
        }
      }

      if (score > bestScore) {
        bestIndex = index;
        bestScore = score;
      }
    });

    return bestIndex;
  }, [optimizationData.simulationSummary, optimizationObjective]);

  // Fetch run-specific data when selected run changes
  useEffect(() => {
    let isCancelled = false;

    const fetchRunSpecificData = async () => {
      if (optimizationData.simulationSummary.length === 0) {
        setRunSpecificData({
          initialUtilization: [],
          finalUtilization: [],
          costReport: [],
          optimizationLog: [],
          loading: false,
          error: null,
        });
        return;
      }

      setRunSpecificData(prev => ({ ...prev, loading: true, error: null }));

      try {
        // Get the selected run
        const selectedRun = optimizationData.simulationSummary[selectedRunIndex] || optimizationData.simulationSummary[0];
        if (!selectedRun) {
          setRunSpecificData({
            initialUtilization: [],
            finalUtilization: [],
            costReport: [],
            optimizationLog: [],
            loading: false,
            error: null,
          });
          return;
        }

        // Get run_id from the selected run
        const runId = getColumnValue(selectedRun, ['run_id', 'RUN_ID', 'runId']) || (selectedRunIndex + 1).toString();
        
        // Use the dynamically selected folder
        const folderName: string | undefined = selectedFolder || undefined;

        // Fetch run-specific utilization (initial + final) - both come from the same run
        const utilRes = await fetch(`${API_ENDPOINTS.optimizationUtilization(runId)}${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`);
        let initialUtilization: CSVRow[] = [];
        let finalUtilization: CSVRow[] = [];
        if (utilRes.ok) {
          const utilData = await utilRes.json();
          const initialUtilText = utilData.initial || '';
          const finalUtilText = utilData.final || '';
          initialUtilization = initialUtilText ? parseCSV(initialUtilText) : [];
          finalUtilization = finalUtilText ? parseCSV(finalUtilText) : [];
        }

        // Fetch run-specific cost analysis
        const costRes = await fetch(`${API_ENDPOINTS.optimizationCostAnalysis(runId)}${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`);
        let costReport: CSVRow[] = [];
        if (costRes.ok) {
          const costText = await costRes.text();
          costReport = costText ? parseCSV(costText) : [];
        }

        // Note: Optimization log is typically aggregate across all runs
        // Individual run folders may have optimization_log.csv, but it's usually aggregated
        // For now, we'll use the aggregate optimization log as it's the authoritative source
        // that tracks all workload redistributions across all runs
        const optimizationLog: CSVRow[] = [];

        if (!isCancelled) {
          setRunSpecificData({
            initialUtilization,
            finalUtilization,
            costReport,
            optimizationLog,
            loading: false,
            error: null,
          });
        }
      } catch (error) {
        if (!isCancelled) {
          logger.error('Error loading run-specific data:', error);
          setRunSpecificData(prev => ({
            ...prev,
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to load run-specific data'
          }));
        }
      }
    };

    fetchRunSpecificData();

    return () => {
      isCancelled = true;
    };
  }, [selectedRunIndex, optimizationData.simulationSummary, selectedFolder]);

  // Fetch fuel consumption by vehicle type data when selected run changes
  useEffect(() => {
    let isCancelled = false;

    const fetchFuelByVehicleType = async () => {
      if (optimizationData.simulationSummary.length === 0) {
        setFuelByVehicleType({
          initial: {},
          optimized: {},
          loading: false,
          error: null,
        });
        return;
      }

      setFuelByVehicleType(prev => ({ ...prev, loading: true, error: null }));

      try {
        // Get the selected run
        const selectedRun = optimizationData.simulationSummary[selectedRunIndex] || optimizationData.simulationSummary[0];
        if (!selectedRun) {
          setFuelByVehicleType({
            initial: {},
            optimized: {},
            loading: false,
            error: null,
          });
          return;
        }

        // Get run_id from the selected run
        const runId = getColumnValue(selectedRun, ['run_id', 'RUN_ID', 'runId']) || (selectedRunIndex + 1).toString();
        
        // Use the dynamically selected folder
        const folderName: string | undefined = selectedFolder || undefined;

        // Fetch metrics data which contains fuel consumption information
        const metricsRes = await fetch(`${API_ENDPOINTS.optimizationMetrics(runId)}${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`);
        let initialFuel: Record<string, number> = {};
        let optimizedFuel: Record<string, number> = {};
        
        if (metricsRes.ok) {
          const metricsData = await metricsRes.json();
          const initialMetricsText = metricsData.initial || '';
          const optimizedMetricsText = metricsData.optimized || '';
          
          // Parse initial metrics
          if (initialMetricsText) {
            const initialMetrics = parseCSV(initialMetricsText);
            initialMetrics.forEach((row: CSVRow) => {
              const vehicleType = getColumnValue(row, ['vehicle_type', 'VEHICLE_TYPE', 'vehicleType']);
              const fuel = parseFloat(getColumnValue(row, ['fuel_consumed', 'FUEL_CONSUMED', 'fuelConsumed', 'fuel_litres', 'FUEL_LITRES']).replace(/[^0-9.-]/g, '')) || 0;
              if (vehicleType) {
                initialFuel[vehicleType] = (initialFuel[vehicleType] || 0) + fuel;
              }
            });
          }
          
          // Parse optimized metrics
          if (optimizedMetricsText) {
            const optimizedMetrics = parseCSV(optimizedMetricsText);
            optimizedMetrics.forEach((row: CSVRow) => {
              const vehicleType = getColumnValue(row, ['vehicle_type', 'VEHICLE_TYPE', 'vehicleType']);
              const fuel = parseFloat(getColumnValue(row, ['fuel_consumed', 'FUEL_CONSUMED', 'fuelConsumed', 'fuel_litres', 'FUEL_LITRES']).replace(/[^0-9.-]/g, '')) || 0;
              if (vehicleType) {
                optimizedFuel[vehicleType] = (optimizedFuel[vehicleType] || 0) + fuel;
              }
            });
          }
        }

        if (!isCancelled) {
          setFuelByVehicleType({
            initial: initialFuel,
            optimized: optimizedFuel,
            loading: false,
            error: null,
          });
        }
      } catch (error) {
        if (!isCancelled) {
          logger.error('Error loading fuel by vehicle type data:', error);
          setFuelByVehicleType(prev => ({
            ...prev,
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to load fuel data'
          }));
        }
      }
    };

    fetchFuelByVehicleType();

    return () => {
      isCancelled = true;
    };
  }, [selectedRunIndex, optimizationData.simulationSummary, selectedFolder]);

  // Fetch incident and event log data when optimization data is loaded or selected run changes
  useEffect(() => {
    let isCancelled = false;

    const fetchIncidentData = async () => {
      // Wait for simulation summary to be loaded
      if (optimizationData.simulationSummary.length === 0) {
        logger.info('[Incident Summary] Waiting for simulation summary to load...');
        setIncidentData({
          incidentLog: [],
          initialEventLog: [],
          optimizedEventLog: [],
          dispatchComparison: [],
          loading: true,
          error: null,
        });
        return;
      }

      setIncidentData(prev => ({ ...prev, loading: true, error: null }));

      try {
        // Use the dynamically selected folder
        const folderName: string | undefined = selectedFolder || undefined;

        // Get the selected run ID (use validRunIndex to match the Simulation Summary)
        const validRunIndex = Math.max(0, Math.min(selectedRunIndex, optimizationData.simulationSummary.length - 1));
        const selectedRun = optimizationData.simulationSummary[validRunIndex] || optimizationData.simulationSummary[0];
        
        // Try multiple ways to extract runId
        let runId: string | undefined = undefined;
        if (selectedRun) {
          runId = getColumnValue(selectedRun, ['run_id', 'RUN_ID', 'runId', 'Run_ID', 'Run ID']);
          if (!runId) {
            // Fallback: use the index + 1 as runId
            runId = (validRunIndex + 1).toString();
          }
        } else {
          // If no run selected, default to run 1
          runId = '1';
        }

        logger.info(`[Incident Summary] Fetching data for run ${runId} (selectedRunIndex: ${selectedRunIndex}, validRunIndex: ${validRunIndex}, simulationSummary length: ${optimizationData.simulationSummary.length})`);

        // Fetch incident log (from final_validation or first run - same for all runs)
        const incidentRes = await fetch(`${API_ENDPOINTS.optimizationIncidentLog()}${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`);
        if (!incidentRes.ok) {
          logger.warn(`[Incident Summary] Failed to fetch incident log: ${incidentRes.status} ${incidentRes.statusText}`);
        }
        const incidentText = incidentRes.ok ? await incidentRes.text() : '';
        const incidentLog = incidentText ? parseCSV(incidentText) : [];
        
        // Debug: Log incident log structure
        if (incidentLog.length > 0) {
          logger.info(`[Incident Summary] Incident log loaded - ${incidentLog.length} rows`);
          logger.info('[Incident Summary] Incident log columns:', Object.keys(incidentLog[0]));
          logger.info('[Incident Summary] First incident row:', incidentLog[0]);
        } else {
          logger.warn('[Incident Summary] No incident log data loaded');
        }

        // Fetch initial event log from the selected run (or final_validation if no runId)
        const initialEventUrl = `${API_ENDPOINTS.optimizationEventLog('initial', runId, folderName)}`;
        logger.info(`[Incident Summary] Fetching initial event log from: ${initialEventUrl}`);
        const initialEventRes = await fetch(initialEventUrl);
        if (!initialEventRes.ok) {
          logger.warn(`[Incident Summary] Failed to fetch initial event log for run ${runId}: ${initialEventRes.status} ${initialEventRes.statusText}`);
        }
        const initialEventText = initialEventRes.ok ? await initialEventRes.text() : '';
        const initialEventLog = initialEventText ? parseCSV(initialEventText) : [];

        // Fetch optimized event log from the selected run (or final_validation if no runId)
        const optimizedEventUrl = `${API_ENDPOINTS.optimizationEventLog('optimized', runId, folderName)}`;
        logger.info(`[Incident Summary] Fetching optimized event log from: ${optimizedEventUrl}`);
        const optimizedEventRes = await fetch(optimizedEventUrl);
        if (!optimizedEventRes.ok) {
          logger.warn(`[Incident Summary] Failed to fetch optimized event log for run ${runId}: ${optimizedEventRes.status} ${optimizedEventRes.statusText}`);
        }
        const optimizedEventText = optimizedEventRes.ok ? await optimizedEventRes.text() : '';
        const optimizedEventLog = optimizedEventText ? parseCSV(optimizedEventText) : [];

        // Fetch pre-computed dispatch comparison from the backend (for fast switching between Original/Optimised views)
        const dispatchComparisonUrl = API_ENDPOINTS.optimizationDispatchComparison(runId || '1', folderName);
        logger.info(`[Incident Summary] Fetching dispatch comparison from: ${dispatchComparisonUrl}`);
        let dispatchComparison: { incident_id: string; initial_dispatch: string; optimized_dispatch: string; has_change: boolean; added_vehicles: string; removed_vehicles: string; }[] = [];
        try {
          const dispatchComparisonRes = await fetch(dispatchComparisonUrl);
          if (dispatchComparisonRes.ok) {
            dispatchComparison = await dispatchComparisonRes.json();
            logger.info(`[Incident Summary] Dispatch comparison loaded - ${dispatchComparison.length} incidents`);
          } else {
            logger.warn(`[Incident Summary] Dispatch comparison not available (status: ${dispatchComparisonRes.status}) - will compute on-the-fly`);
          }
        } catch (dispatchErr) {
          logger.warn('[Incident Summary] Failed to fetch dispatch comparison:', dispatchErr);
        }

        logger.info(`[Incident Summary] Fetched data - Incident log: ${incidentLog.length} rows, Initial event log: ${initialEventLog.length} rows, Optimized event log: ${optimizedEventLog.length} rows, Dispatch comparison: ${dispatchComparison.length} incidents`);

        if (!isCancelled) {
          setIncidentData({
            incidentLog,
            initialEventLog,
            optimizedEventLog,
            dispatchComparison,
            loading: false,
            error: null,
          });
        }
      } catch (error) {
        if (!isCancelled) {
          logger.error('[Incident Summary] Error fetching data:', error);
          setIncidentData(prev => ({
            ...prev,
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch incident data',
          }));
        }
      }
    };

    fetchIncidentData();

    return () => {
      isCancelled = true;
    };
    // Include optimizationData.simulationSummary in dependencies so it fetches when data is first loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRunIndex, selectedFolder, optimizationData.simulationSummary.length]);

  // Process incident summary data (memoized to update when view type or selected run changes)
  const incidentSummaryData = useMemo(() => {
    if (incidentData.incidentLog.length === 0) return [];
    
    logger.info(`[Incident Summary] Processing data - View type: ${incidentViewType}, Selected run index: ${selectedRunIndex}, Initial event log rows: ${incidentData.initialEventLog.length}, Optimized event log rows: ${incidentData.optimizedEventLog.length}`);
    
    // Select the correct event log based on view type:
    // - 'original' → use initial_event_log.csv (for "Original Dispatch" button)
    // - 'optimized' → use optimized_event_log.csv (for "Optimised Dispatch" button)
    const eventLog = incidentViewType === 'original' 
      ? incidentData.initialEventLog 
      : incidentData.optimizedEventLog;
    
    logger.info(`[Incident Summary] View type: ${incidentViewType}, Using ${incidentViewType === 'original' ? 'initial' : 'optimized'} event log with ${eventLog.length} rows`);
    logger.info(`[Incident Summary] Initial event log rows: ${incidentData.initialEventLog.length}, Optimized event log rows: ${incidentData.optimizedEventLog.length}`);
    
    // Filter incident log to only include incidents that were actually simulated
    // Backend guarantee: The optimization uses the SAME incidents for both initial and optimized simulations.
    // The optimized simulation replays the exact incidents from initial_incident_log.csv.
    // Therefore, we use the initial event log as the source of truth for which incidents were simulated.
    const simulatedIncidentIds = new Set<string>();
    
    // Extract unique incident IDs from initial event log (source of truth for simulated incidents)
    incidentData.initialEventLog.forEach(event => {
      const incidentId = getColumnValue(event, [
        'incident_id', 'INCIDENT_ID', 'incidentId', 'Incident_ID',
        'incident', 'INCIDENT', 'Incident'
      ]);
      if (incidentId && incidentId !== '0' && incidentId !== '') {
        const normalizedId = String(incidentId).trim();
        if (normalizedId) {
          simulatedIncidentIds.add(normalizedId);
        }
      }
    });
    
    // Also extract from optimized event log for validation and comparison
    const optimizedEventLogIds = new Set<string>();
    incidentData.optimizedEventLog.forEach(event => {
      const incidentId = getColumnValue(event, [
        'incident_id', 'INCIDENT_ID', 'incidentId', 'Incident_ID',
        'incident', 'INCIDENT', 'Incident'
      ]);
      if (incidentId && incidentId !== '0' && incidentId !== '') {
        const normalizedId = String(incidentId).trim();
        if (normalizedId) {
          optimizedEventLogIds.add(normalizedId);
        }
      }
    });
    
    // Filter incident log to only include incidents that were actually simulated
    const simulatedIncidents = incidentData.incidentLog.filter(incident => {
      const incidentId = getColumnValue(incident, [
        'incident_id', 'INCIDENT_ID', 'incidentId', 'Incident_ID',
        'incident', 'INCIDENT', 'Incident'
      ]);
      // Normalize the incident ID (trim whitespace, convert to string) for comparison
      const normalizedId = String(incidentId || '').trim();
      return normalizedId && simulatedIncidentIds.has(normalizedId);
    });
    
    logger.info(`[Incident Summary] Filtered incidents - View: ${incidentViewType}, Total in log: ${incidentData.incidentLog.length}, Unique IDs in initial event log (source of truth): ${simulatedIncidentIds.size}, Unique IDs in optimized event log: ${optimizedEventLogIds.size}, Filtered incidents: ${simulatedIncidents.length}`);
    
    // Validate that both event logs have the same incidents (backend guarantee)
    if (simulatedIncidentIds.size !== optimizedEventLogIds.size) {
      logger.warn(`[Incident Summary] WARNING: Initial and optimized event logs have different numbers of unique incident IDs! Initial: ${simulatedIncidentIds.size}, Optimized: ${optimizedEventLogIds.size}. This may indicate a backend issue.`);
    }
    
    // Debug: Log some sample incident IDs from both sources for comparison
    if (simulatedIncidentIds.size > 0 && incidentData.incidentLog.length > 0) {
      const sampleEventLogIds = Array.from(simulatedIncidentIds).slice(0, 5);
      const sampleIncidentLogIds = incidentData.incidentLog.slice(0, 5).map(inc => {
        const id = getColumnValue(inc, ['incident_id', 'INCIDENT_ID', 'incidentId', 'Incident_ID', 'incident', 'INCIDENT', 'Incident']);
        return String(id || '').trim();
      });
      logger.info(`[Incident Summary] Sample IDs from intersection of both event logs (simulated):`, sampleEventLogIds);
      logger.info(`[Incident Summary] Sample IDs from incident log:`, sampleIncidentLogIds);
    }
    
    // Debug: Check if event logs are different (for first few incidents to detect patterns)
    if (simulatedIncidents.length > 0 && incidentData.initialEventLog.length > 0 && incidentData.optimizedEventLog.length > 0) {
      const incidentsToCheck = simulatedIncidents.slice(0, Math.min(5, simulatedIncidents.length));
      let sameVehiclesCount = 0;
      let differentVehiclesCount = 0;
      let noOptimizedDataCount = 0;
      
      incidentsToCheck.forEach((incident, idx) => {
        const incidentId = getColumnValue(incident, [
          'incident_id', 'INCIDENT_ID', 'incidentId', 'Incident_ID',
          'incident', 'INCIDENT', 'Incident'
        ]) || '';
        const normalizedId = String(incidentId).trim();
        
        const initialEvents = incidentData.initialEventLog.filter(e => {
          const eId = getColumnValue(e, ['incident_id', 'INCIDENT_ID', 'incidentId', 'Incident_ID', 'incident', 'INCIDENT', 'Incident']) || '';
          return String(eId).trim() === normalizedId;
        });
        const optimizedEvents = incidentData.optimizedEventLog.filter(e => {
          const eId = getColumnValue(e, ['incident_id', 'INCIDENT_ID', 'incidentId', 'Incident_ID', 'incident', 'INCIDENT', 'Incident']) || '';
          return String(eId).trim() === normalizedId;
        });
        
        if (optimizedEvents.length === 0) {
          noOptimizedDataCount++;
          logger.warn(`[Incident Summary] Incident ${normalizedId} (${idx + 1}/${incidentsToCheck.length}): No optimized events found!`);
        } else if (initialEvents.length > 0) {
          // Extract vehicle types from dispatch events (skip "Incident Generated")
          const initialVehicles = initialEvents
            .filter(e => {
              const eventType = getColumnValue(e, ['event', 'EVENT', 'Event', 'event_type', 'EVENT_TYPE', 'Event_Type']) || '';
              return !eventType.toLowerCase().includes('incident generated') && !eventType.toLowerCase().includes('generated');
            })
            .map(e => getColumnValue(e, ['vehicle_type', 'VEHICLE_TYPE', 'vehicleType', 'Vehicle_Type', 'type', 'TYPE', 'Type', 'mapped_vehicle_type', 'MAPPED_VEHICLE_TYPE', 'Mapped Vehicle Type']))
            .filter(v => v && v !== '0' && v !== '' && v.toLowerCase() !== 'none');
          
          const optimizedVehicles = optimizedEvents
            .filter(e => {
              const eventType = getColumnValue(e, ['event', 'EVENT', 'Event', 'event_type', 'EVENT_TYPE', 'Event_Type']) || '';
              return !eventType.toLowerCase().includes('incident generated') && !eventType.toLowerCase().includes('generated');
            })
            .map(e => getColumnValue(e, ['vehicle_type', 'VEHICLE_TYPE', 'vehicleType', 'Vehicle_Type', 'type', 'TYPE', 'Type', 'mapped_vehicle_type', 'MAPPED_VEHICLE_TYPE', 'Mapped Vehicle Type']))
            .filter(v => v && v !== '0' && v !== '' && v.toLowerCase() !== 'none');
          
          const initialVehiclesSorted = [...initialVehicles].sort();
          const optimizedVehiclesSorted = [...optimizedVehicles].sort();
          
          if (JSON.stringify(initialVehiclesSorted) === JSON.stringify(optimizedVehiclesSorted)) {
            sameVehiclesCount++;
            if (idx === 0) {
              logger.warn(`[Incident Summary] Incident ${normalizedId} (${idx + 1}/${incidentsToCheck.length}): SAME vehicles in both logs! Initial: [${initialVehicles.join(', ')}], Optimized: [${optimizedVehicles.join(', ')}]`);
            }
          } else {
            differentVehiclesCount++;
            if (idx === 0) {
              logger.info(`[Incident Summary] Incident ${normalizedId} (${idx + 1}/${incidentsToCheck.length}): DIFFERENT vehicles! Initial: [${initialVehicles.join(', ')}], Optimized: [${optimizedVehicles.join(', ')}]`);
            }
          }
        }
      });
      
      // Summary warning
      if (sameVehiclesCount === incidentsToCheck.length) {
        logger.error(`[Incident Summary] ⚠️ CRITICAL: All ${incidentsToCheck.length} checked incidents have IDENTICAL dispatched vehicles in both initial and optimized logs!`);
        logger.error(`[Incident Summary] This suggests the optimized simulation is NOT using a different fleet configuration.`);
        logger.error(`[Incident Summary] Possible causes:`);
        logger.error(`[Incident Summary] 1. Backend is using the same fleet config for both simulations`);
        logger.error(`[Incident Summary] 2. Optimized event log is not being generated correctly`);
        logger.error(`[Incident Summary] 3. Fleet optimization didn't actually change vehicle assignments`);
      } else if (sameVehiclesCount > 0) {
        logger.warn(`[Incident Summary] ⚠️ WARNING: ${sameVehiclesCount}/${incidentsToCheck.length} incidents have identical vehicles, ${differentVehiclesCount} have different vehicles, ${noOptimizedDataCount} have no optimized data`);
      } else if (differentVehiclesCount > 0) {
        logger.info(`[Incident Summary] ✅ Good: ${differentVehiclesCount}/${incidentsToCheck.length} incidents show different vehicle assignments (optimization is working)`);
      }
    }
    
    // Debug: Log first event to see what columns we have
    if (eventLog.length > 0 && simulatedIncidents.length > 0) {
      logger.info('Event log columns:', Object.keys(eventLog[0]));
      logger.info('First event log row:', eventLog[0]);
    }
    
    // Group events by incident_id - try multiple column name variations
    const eventsByIncident = new Map<string, any[]>();
    eventLog.forEach(event => {
      const incidentId = getColumnValue(event, [
        'incident_id', 'INCIDENT_ID', 'incidentId', 'Incident_ID',
        'incident', 'INCIDENT', 'Incident'
      ]);
      if (incidentId && incidentId !== '0' && incidentId !== '') {
        // Normalize incident ID - trim whitespace and convert to string for consistent comparison
        const idStr = String(incidentId).trim();
        if (idStr) {
          if (!eventsByIncident.has(idStr)) {
            eventsByIncident.set(idStr, []);
          }
          eventsByIncident.get(idStr)!.push(event);
        }
      }
    });
    
    // Debug: Log sample incident IDs from event log for comparison
    if (incidentViewType === 'optimized' && eventsByIncident.size > 0) {
      const sampleEventLogIds = Array.from(eventsByIncident.keys()).slice(0, 10);
      logger.info(`[Incident Summary] Sample incident IDs from ${incidentViewType} event log:`, sampleEventLogIds);
      
      // Also check if the first incident ID from simulated incidents matches
      if (simulatedIncidents.length > 0) {
        const firstSimulatedId = String(getColumnValue(simulatedIncidents[0], [
          'incident_id', 'INCIDENT_ID', 'incidentId', 'Incident_ID',
          'incident', 'INCIDENT', 'Incident'
        ]) || '').trim();
        logger.info(`[Incident Summary] First simulated incident ID: "${firstSimulatedId}" (type: ${typeof firstSimulatedId})`);
        logger.info(`[Incident Summary] Is "${firstSimulatedId}" in ${incidentViewType} event log?`, eventsByIncident.has(firstSimulatedId));
        
        // Check for type mismatches - try number version too
        const firstSimulatedIdNum = Number(firstSimulatedId);
        if (!isNaN(firstSimulatedIdNum)) {
          logger.info(`[Incident Summary] Trying numeric version: "${firstSimulatedIdNum}" in event log?`, eventsByIncident.has(String(firstSimulatedIdNum)));
        }
      }
    }

    // Process each incident (only simulated incidents)
    return simulatedIncidents.map((incident, incidentIndex) => {
      const incidentId = getColumnValue(incident, [
        'incident_id', 'INCIDENT_ID', 'incidentId', 'Incident_ID',
        'incident', 'INCIDENT', 'Incident'
      ]) || '';
      const incidentIdStr = String(incidentId);
      
      const incidentType = getColumnValue(incident, ['incident_type', 'INCIDENT_TYPE', 'incidentType', 'Incident_Type']) || 'Unknown';
      const category = getColumnValue(incident, ['category', 'CATEGORY', 'Category']) || '';
      const severity = getColumnValue(incident, ['severity', 'SEVERITY', 'Severity']) || 'Unknown';
      // Get required vehicles - try multiple column name variations
      // For historical data: required_pumping, required_other, required_rc, required_sv, required_lv
      // For future data: might have different column names or structure
      // Also try variations with underscores, spaces, and different cases
      const requiredPumping = parseFloat(getColumnValue(incident, [
        'required_pumping', 'REQUIRED_PUMPING', 'requiredPumping', 'Required_Pumping', 'Required Pumping', 'REQUIRED PUMPING',
        'required_pa', 'REQUIRED_PA', 'requiredPa', 'Required_PA', 'Required PA', 'REQUIRED PA',
        'pumping_required', 'PUMPING_REQUIRED', 'pumpingRequired', 'Pumping_Required', 'Pumping Required',
        'pa_required', 'PA_REQUIRED', 'paRequired', 'PA_Required', 'PA Required'
      ]) || '0');
      const requiredOther = parseFloat(getColumnValue(incident, [
        'required_other', 'REQUIRED_OTHER', 'requiredOther', 'Required_Other', 'Required Other', 'REQUIRED OTHER',
        'other_required', 'OTHER_REQUIRED', 'otherRequired', 'Other_Required', 'Other Required'
      ]) || '0');
      const requiredRC = parseFloat(getColumnValue(incident, [
        'required_rc', 'REQUIRED_RC', 'requiredRc', 'Required_RC', 'Required RC', 'REQUIRED RC',
        'required_response_car', 'REQUIRED_RESPONSE_CAR', 'requiredResponseCar', 'Required_Response_Car', 'Required Response Car',
        'rc_required', 'RC_REQUIRED', 'rcRequired', 'RC_Required', 'RC Required',
        'response_car_required', 'RESPONSE_CAR_REQUIRED', 'responseCarRequired', 'Response_Car_Required', 'Response Car Required'
      ]) || '0');
      const requiredSV = parseFloat(getColumnValue(incident, [
        'required_sv', 'REQUIRED_SV', 'requiredSv', 'Required_SV', 'Required SV', 'REQUIRED SV',
        'required_small_van', 'REQUIRED_SMALL_VAN', 'requiredSmallVan', 'Required_Small_Van', 'Required Small Van',
        'sv_required', 'SV_REQUIRED', 'svRequired', 'SV_Required', 'SV Required',
        'small_van_required', 'SMALL_VAN_REQUIRED', 'smallVanRequired', 'Small_Van_Required', 'Small Van Required'
      ]) || '0');
      const requiredLV = parseFloat(getColumnValue(incident, [
        'required_lv', 'REQUIRED_LV', 'requiredLv', 'Required_LV', 'Required LV', 'REQUIRED LV',
        'required_large_van', 'REQUIRED_LARGE_VAN', 'requiredLargeVan', 'Required_Large_Van', 'Required Large Van',
        'lv_required', 'LV_REQUIRED', 'lvRequired', 'LV_Required', 'LV Required',
        'large_van_required', 'LARGE_VAN_REQUIRED', 'largeVanRequired', 'Large_Van_Required', 'Large Van Required'
      ]) || '0');
      
      // Debug: Log first incident to see what columns we have (only for first incident to avoid spam)
      if (incidentIndex === 0) {
        logger.info('[Incident Summary] First incident columns:', Object.keys(incident));
        logger.info('[Incident Summary] First incident data:', incident);
        logger.info(`[Incident Summary] Required vehicles - PA: ${requiredPumping}, Other: ${requiredOther}, RC: ${requiredRC}, SV: ${requiredSV}, LV: ${requiredLV}`);
        
        // Also log all column names that contain "required" to help debug future mode
        const requiredColumns = Object.keys(incident).filter(key => 
          key.toLowerCase().includes('required') || key.toLowerCase().includes('pumping') || 
          key.toLowerCase().includes('rc') || key.toLowerCase().includes('sv') || 
          key.toLowerCase().includes('lv') || key.toLowerCase().includes('van')
        );
        if (requiredColumns.length > 0) {
          logger.info('[Incident Summary] Columns that might contain required vehicle info:', requiredColumns);
        }
      }
      
      const requiredVehicles: string[] = [];
      if (requiredPumping > 0) requiredVehicles.push(`PA x ${requiredPumping}`);
      if (requiredOther > 0) requiredVehicles.push(`Other x ${requiredOther}`);
      if (requiredRC > 0) requiredVehicles.push(`RC x ${requiredRC}`);
      if (requiredSV > 0) requiredVehicles.push(`SV x ${requiredSV}`);
      if (requiredLV > 0) requiredVehicles.push(`LV x ${requiredLV}`);
      
      // Get dispatched vehicles from event log
      // Try both string and number versions of the incident ID to handle type mismatches
      let incidentEvents = eventsByIncident.get(incidentIdStr) || [];
      
      // If no events found, try numeric version (in case of type mismatch)
      if (incidentEvents.length === 0) {
        const incidentIdNum = Number(incidentIdStr);
        if (!isNaN(incidentIdNum)) {
          incidentEvents = eventsByIncident.get(String(incidentIdNum)) || [];
          if (incidentEvents.length > 0 && incidentIndex < 3) {
            logger.info(`[Incident Summary] Found events using numeric ID "${incidentIdNum}" for incident "${incidentIdStr}"`);
          }
        }
        
        // Also try searching with leading zeros removed (e.g., "069754" vs "69754")
        if (incidentEvents.length === 0 && incidentIdStr.length > 0) {
          const trimmedId = incidentIdStr.replace(/^0+/, ''); // Remove leading zeros
          if (trimmedId !== incidentIdStr) {
            incidentEvents = eventsByIncident.get(trimmedId) || [];
            if (incidentEvents.length > 0 && incidentIndex < 3) {
              logger.info(`[Incident Summary] Found events using trimmed ID "${trimmedId}" for incident "${incidentIdStr}"`);
            }
          }
        }
      }
      
      // Track vehicle IDs grouped by type: vehicle_type -> Set<vehicle_id>
      const dispatchedVehiclesByType = new Map<string, Set<string>>();
      const responsibleStations = new Set<string>();
      
      // Debug: Log if no events found for this incident
      if (incidentEvents.length === 0) {
        if (incidentIndex < 5) { // Log first 5 incidents with no events
          logger.warn(`[Incident Summary] No events found for incident ID: ${incidentIdStr} (View: ${incidentViewType})`);
          if (incidentIndex === 0) {
            const availableIds = Array.from(eventsByIncident.keys()).slice(0, 20); // Show more IDs
            logger.warn(`[Incident Summary] Available incident IDs in ${incidentViewType} event log (first 20):`, JSON.stringify(availableIds));
            logger.warn(`[Incident Summary] Looking for: "${incidentIdStr}" (exact match: ${eventsByIncident.has(incidentIdStr)})`);
            
            // Try to find a close match
            const numericId = Number(incidentIdStr);
            if (!isNaN(numericId)) {
              logger.warn(`[Incident Summary] Numeric version: "${numericId}" (exact match: ${eventsByIncident.has(String(numericId))})`);
            }
            
            // Check if any of the first few simulated incident IDs are in the event log
            const firstFewSimulatedIds = simulatedIncidents.slice(0, 5).map(inc => {
              const id = getColumnValue(inc, ['incident_id', 'INCIDENT_ID', 'incidentId', 'Incident_ID', 'incident', 'INCIDENT', 'Incident']);
              return String(id || '').trim();
            });
            logger.warn(`[Incident Summary] First 5 simulated incident IDs:`, JSON.stringify(firstFewSimulatedIds));
            const matchingCount = firstFewSimulatedIds.filter(id => eventsByIncident.has(id)).length;
            logger.warn(`[Incident Summary] How many of first 5 simulated IDs are in ${incidentViewType} event log: ${matchingCount}/5`);
            
            // Show which ones match and which don't
            firstFewSimulatedIds.forEach((id, idx) => {
              const matches = eventsByIncident.has(id);
              logger.warn(`[Incident Summary] Simulated ID ${idx + 1}: "${id}" - ${matches ? 'FOUND' : 'NOT FOUND'} in ${incidentViewType} event log`);
            });
            
            // CRITICAL: If most IDs don't match, this indicates a backend issue
            if (matchingCount < 3) {
              logger.error(`[Incident Summary] CRITICAL: Only ${matchingCount}/5 simulated incident IDs found in ${incidentViewType} event log!`);
              logger.error(`[Incident Summary] This suggests the optimized simulation is NOT using the same incident IDs as the initial simulation.`);
              logger.error(`[Incident Summary] Backend should use the same incident_log_path for both simulations (line 497 in utilization_optimized.py).`);
              logger.error(`[Incident Summary] Please check the backend simulation engine to ensure it preserves incident IDs when replaying.`);
            }
          }
        }
      }
      
      // Extract data from the selected event log (initial_event_log.csv or optimized_event_log.csv)
      // For "Original Dispatch": uses initial_event_log.csv
      // For "Optimised Dispatch": uses optimized_event_log.csv
      incidentEvents.forEach((event, eventIndex) => {
        // Extract event type to filter out non-dispatch events (e.g., "Incident Generated")
        const eventType = getColumnValue(event, [
          'event', 'EVENT', 'Event',
          'event_type', 'EVENT_TYPE', 'Event_Type',
          'action', 'ACTION', 'Action'
        ]) || '';
        
        // Extract vehicle_type from the selected event log
        // This will be displayed in the "Dispatched Vehicles" column
        const vehicleType = getColumnValue(event, [
          'vehicle_type', 'VEHICLE_TYPE', 'vehicleType', 'Vehicle_Type',
          'type', 'TYPE', 'Type',
          'mapped_vehicle_type', 'MAPPED_VEHICLE_TYPE', 'Mapped Vehicle Type'
        ]);
        
        // Extract vehicle_id from the selected event log
        const vehicleId = getColumnValue(event, [
          'vehicle_id', 'VEHICLE_ID', 'vehicleId', 'Vehicle_ID',
          'vehicle', 'VEHICLE', 'Vehicle'
        ]);
        
        // Extract fire_station from the selected event log
        // This will be displayed in the "Responsible Station(s)" column
        const station = getColumnValue(event, [
          'fire_station', 'FIRE_STATION', 'fireStation', 'Fire_Station',
          'station', 'STATION', 'Station',
          'stn', 'STN', 'Stn'
        ]);
        
        // Debug: Log first few events for first few incidents to see what we're extracting
        if (incidentIndex < 3 && eventIndex < 3) {
          logger.info(`[Incident Summary] Incident ${incidentIdStr}, Event ${eventIndex} (View: ${incidentViewType}) - Event: "${eventType}", Vehicle Type: "${vehicleType}", Vehicle ID: "${vehicleId}", Station: "${station}"`);
        }
        
        // Filter out events that don't have vehicle assignments:
        // - Skip "Incident Generated" events (they don't have vehicle assignments yet)
        // - Only count events that have valid vehicle_type and/or fire_station values
        const isIncidentGenerated = eventType.toLowerCase().includes('incident generated') || 
                                     eventType.toLowerCase().includes('generated');
        
        // Check if this event has actual vehicle dispatch information
        const hasVehicleInfo = vehicleType && vehicleType !== '0' && vehicleType !== '' && vehicleType.toLowerCase() !== 'none';
        const hasVehicleId = vehicleId && vehicleId !== '0' && vehicleId !== '' && vehicleId.toLowerCase() !== 'none';
        const hasStationInfo = station && station !== '0' && station !== '' && station.toLowerCase() !== 'none';
        
        // Count events that have vehicle dispatch information (not just incident generation)
        // IMPORTANT: We need to be more lenient - if an event has ANY dispatch info, include it
        // Some events might have vehicle but no station, or station but no vehicle type
        if (!isIncidentGenerated && (hasVehicleInfo || hasStationInfo)) {
          // Add vehicle ID grouped by type if both exist and are valid
          if (hasVehicleInfo && hasVehicleId) {
            if (!dispatchedVehiclesByType.has(vehicleType)) {
              dispatchedVehiclesByType.set(vehicleType, new Set<string>());
            }
            dispatchedVehiclesByType.get(vehicleType)!.add(vehicleId);
          }
          
          // Add station if it exists and is valid
          if (hasStationInfo) {
            responsibleStations.add(station);
          }
        } else if (incidentIndex < 3 && eventIndex < 3) {
          logger.info(`[Incident Summary] Skipping event ${eventIndex} for incident ${incidentIdStr} (View: ${incidentViewType}) - Event: "${eventType}", Has vehicle: ${hasVehicleInfo}, Has vehicle ID: ${hasVehicleId}, Has station: ${hasStationInfo}`);
        }
      });
      
      // Debug: Log if we found no vehicles or stations for first incident
      if (incidentIndex === 0) {
        logger.info(`[Incident Summary] After processing - Found ${incidentEvents.length} events, ${dispatchedVehiclesByType.size} vehicle types, ${responsibleStations.size} stations`);
        if (dispatchedVehiclesByType.size > 0) {
          logger.info(`[Incident Summary] Dispatched vehicle types found:`, Array.from(dispatchedVehiclesByType.entries()).map(([type, ids]) => [type, Array.from(ids)]));
        }
      }
      
      // Check if incident has events but no vehicle dispatches (fleet insufficient)
      // This helps distinguish between "incident not found" vs "incident found but no vehicles dispatched"
      const hasIncidentGenerated = incidentEvents.some(event => {
        const eventType = getColumnValue(event, [
          'event', 'EVENT', 'Event',
          'event_type', 'EVENT_TYPE', 'Event_Type',
          'action', 'ACTION', 'Action'
        ]) || '';
        return eventType.toLowerCase().includes('incident generated') || 
               eventType.toLowerCase().includes('generated');
      });
      
      // Format dispatched vehicles with vehicle IDs grouped by type
      // Format: "PA: HAT-PA-01, SAX-PA-02; RC: LEW-RC-01"
      const dispatchedVehiclesFormatted = Array.from(dispatchedVehiclesByType.entries())
        .map(([type, vehicleIds]) => {
          // Shorten vehicle type names - try multiple variations and case-insensitive matching
          const typeLower = type.toLowerCase();
          let shortType = type; // Default to original type
          
          // Check for Pumping Appliance / PA
          if (typeLower.includes('pumping') || typeLower.includes('pa') || 
              typeLower === 'pumping appliance' || typeLower === 'pumping' ||
              typeLower.includes('pump') || typeLower === 'pa') {
            shortType = 'PA';
          }
          // Check for Response Car / RC
          else if (typeLower.includes('response car') || typeLower.includes('rc') ||
                   (typeLower.includes('response') && typeLower.includes('car')) ||
                   typeLower === 'rc' || typeLower === 'response car') {
            shortType = 'RC';
          }
          // Check for Small Van / SV
          else if (typeLower.includes('small van') || typeLower.includes('sv') ||
                   (typeLower.includes('small') && typeLower.includes('van')) ||
                   typeLower === 'sv' || typeLower === 'small van') {
            shortType = 'SV';
          }
          // Check for Large Van / LV
          else if (typeLower.includes('large van') || typeLower.includes('lv') ||
                   (typeLower.includes('large') && typeLower.includes('van')) ||
                   typeLower === 'lv' || typeLower === 'large van') {
            shortType = 'LV';
          }
          // Check for Other
          else if (typeLower.includes('other') || typeLower === 'other') {
            shortType = 'Other';
          }
          // If no match, keep original but log for debugging
          else if (incidentIndex === 0) {
            logger.info(`[Incident Summary] Unknown vehicle type: "${type}" - keeping original`);
          }
          
          // Format as "PA: HAT-PA-01, SAX-PA-02"
          const sortedIds = Array.from(vehicleIds).sort();
          return { shortType, sortedIds, full: `${shortType}: ${sortedIds.join(', ')}` };
        });
      
      // Full list for hover
      const dispatchedVehiclesFull = dispatchedVehiclesFormatted.map(v => v.full).join('; ') 
        || (hasIncidentGenerated && incidentViewType === 'optimized' 
          ? 'No vehicles dispatched (fleet insufficient)' 
          : 'None');
      
      // Truncated list for display (max 2 types, 1 vehicle each)
      const dispatchedVehiclesTruncated = dispatchedVehiclesFormatted.length > 0
        ? dispatchedVehiclesFormatted
            .slice(0, 2)
            .map(v => `${v.shortType}: ${v.sortedIds[0]}${v.sortedIds.length > 1 ? ', ...' : ''}`)
            .join('; ') + (dispatchedVehiclesFormatted.length > 2 ? '; ...' : '')
        : (hasIncidentGenerated && incidentViewType === 'optimized' 
          ? 'No vehicles dispatched' 
          : 'None');
      
      // Check if we need to show "..." indicator
      const dispatchNeedsTruncation = dispatchedVehiclesFormatted.length > 2 || 
        dispatchedVehiclesFormatted.some(v => v.sortedIds.length > 1);
      
      // Format incident type - remove "Pumping - " or "No_Pumping - " prefixes
      // Check both incidentType and the combined string with category
      let formattedIncidentType = incidentType;
      let combinedIncident = `${formattedIncidentType}${category ? ` - ${category}` : ''}`;
      
      // Remove prefixes from the combined string (more robust, case-insensitive)
      // Handle variations: "Pumping - ", "Pumping-", "PUMPING - ", etc.
      combinedIncident = combinedIncident.replace(/^Pumping\s*[-–—]\s*/i, '');
      combinedIncident = combinedIncident.replace(/^No[_\s-]?Pumping\s*[-–—]\s*/i, '');
      // Also check if the prefix is in the middle (e.g., "Pumping - Fire - Low")
      combinedIncident = combinedIncident.replace(/\s*Pumping\s*[-–—]\s*/gi, ' ');
      combinedIncident = combinedIncident.replace(/\s*No[_\s-]?Pumping\s*[-–—]\s*/gi, ' ');
      // Remove underscores and replace with spaces
      combinedIncident = combinedIncident.replace(/_/g, ' ');
      // Clean up any double spaces
      combinedIncident = combinedIncident.replace(/\s+/g, ' ').trim();
      
      // Format responsible stations - remove "Community", "Fire", and "Station" words
      const formattedStations = Array.from(responsibleStations).map(station => {
        let cleaned = station;
        // Remove "Community", "Fire", and "Station" (case-insensitive)
        cleaned = cleaned.replace(/\bCommunity\b/gi, '').trim();
        cleaned = cleaned.replace(/\bFire\b/gi, '').trim();
        cleaned = cleaned.replace(/\bStation\b/gi, '').trim();
        // Clean up any extra spaces or dashes
        cleaned = cleaned.replace(/\s+/g, ' ').trim();
        cleaned = cleaned.replace(/^-\s*|\s*-$/g, '').trim();
        return cleaned;
      }).filter(s => s.length > 0); // Remove empty strings
      
      // Use pre-computed dispatch comparison from backend (dispatch_comparison.csv)
      // Normalize incident ID for matching (handle "69753.0" vs "69753" format differences)
      const normalizedIncidentId = incidentIdStr.replace(/\.0$/, ''); // Remove trailing .0
      const preComputedComparison = incidentData.dispatchComparison.find(c => {
        const csvId = String(c.incident_id).replace(/\.0$/, '');
        return csvId === normalizedIncidentId;
      });
      
      // Get comparison data from pre-computed file
      const hasChange = preComputedComparison?.has_change || false;
      const addedVehicles = preComputedComparison?.added_vehicles ? preComputedComparison.added_vehicles.split(', ').filter(v => v) : [];
      const removedVehicles = preComputedComparison?.removed_vehicles ? preComputedComparison.removed_vehicles.split(', ').filter(v => v) : [];
      const originalDispatchList = incidentViewType === 'optimized' ? (preComputedComparison?.initial_dispatch || '') : '';
      
      // Full stations list for hover
      const stationsFull = formattedStations.length > 0 ? formattedStations.join(', ') : 'Unknown';
      const stationsNeedsTruncation = formattedStations.length >= 3;
      
      return {
        id: incidentIdStr, // Add ID column
        incident: combinedIncident,
        severity,
        requiredVehicles: requiredVehicles.length > 0 ? requiredVehicles.join(', ') : 'None',
        responsibleStations: formattedStations.length > 0 
          ? (formattedStations.length >= 3 
              ? `${formattedStations.slice(0, 2).join(', ')}, ...` 
              : formattedStations.join(', '))
          : 'Unknown',
        responsibleStationsFull: stationsFull,
        stationsNeedsTruncation,
        dispatchedVehicles: dispatchedVehiclesTruncated,
        dispatchedVehiclesFull,
        dispatchNeedsTruncation,
        // Comparison data (for optimized mode)
        hasChange,
        addedVehicles,
        removedVehicles,
        originalDispatch: originalDispatchList,
      };
    });
  }, [incidentViewType, incidentData.incidentLog, incidentData.initialEventLog, incidentData.optimizedEventLog, incidentData.dispatchComparison]);

  // Get optimization goal weights (matching backend get_optimization_goal_weights function)
  const getOptimizationGoalWeights = (goal: string): { utilizationWeight: number; compatibilityWeight: number } => {
    const goalWeights: Record<string, { utilizationWeight: number; compatibilityWeight: number }> = {
      'minimise_fleet_size': { utilizationWeight: 30, compatibilityWeight: 70 },      // Remove vehicles, prioritize compatibility
      'maximise_utilisation': { utilizationWeight: 70, compatibilityWeight: 30 },    // Maximize efficiency, prioritize low utilization
      'balance_cost_service': { utilizationWeight: 50, compatibilityWeight: 50 },    // Balanced approach
      'optimise_response_time': { utilizationWeight: 40, compatibilityWeight: 60 },  // Maintain coverage, prioritize compatibility
      'minimise_response_time': { utilizationWeight: 25, compatibilityWeight: 75 },  // Minimize response time, strongly prioritize compatibility
    };
    
    return goalWeights[goal] || goalWeights['minimise_fleet_size']; // Default to minimise_fleet_size if unknown
  };

  // Update weights automatically when optimization goal changes
  useEffect(() => {
    const weights = getOptimizationGoalWeights(optimizationObjective);
    setOptimizationParams(prev => ({
      ...prev,
      utilizationWeight: weights.utilizationWeight,
      compatibilityWeight: weights.compatibilityWeight,
    }));
  }, [optimizationObjective]);

  const getScoreColor = (score: number) => {
    if (score <= 50) {
      const ratio = score / 50;
      const r = 255;
      const g = Math.round(ratio * 255);
      const b = 0;
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      const ratio = (score - 50) / 50;
      const r = Math.round(255 * (1 - ratio));
      const g = 255;
      const b = 0;
      return `rgb(${r}, ${g}, ${b})`;
    }
  };

  // Component for Location Overlap label with info icon
  const LocationOverlapLabel = () => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
    const iconRef = useRef<HTMLDivElement>(null);

    return (
      <>
        <div className="flex items-center gap-1.5">
          <span>Location<br />Overlap (%)</span>
          <div
            ref={iconRef}
            className="relative inline-flex items-center"
            onMouseEnter={() => {
              if (iconRef.current) {
                const rect = iconRef.current.getBoundingClientRect();
                setTooltipPosition({
                  x: rect.left + rect.width / 2,
                  y: rect.top
                });
                setShowTooltip(true);
              }
            }}
            onMouseLeave={() => {
              setShowTooltip(false);
              setTooltipPosition(null);
            }}
          >
            <HelpCircle className="w-4 h-4 text-warning drop-shadow-glow-warning-md hover:drop-shadow-glow-warning-lg transition-all cursor-help" />
          </div>
        </div>
        {showTooltip && tooltipPosition && createPortal(
          <div
            className="fixed px-3 py-2 bg-popover text-white text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y - 8}px`,
              transform: 'translate(-50%, -100%)',
              maxWidth: '280px'
            }}
          >
            <div className="font-semibold mb-1.5 text-warning drop-shadow-glow-warning-lg">Location Overlap</div>
            <div className="text-foreground leading-relaxed">
              How much two vehicles work in the same areas. Higher overlap = better match for swapping.
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-popover"></div>
          </div>,
          document.body
        )}
      </>
    );
  };

  // Component for Schedule Match label with info icon
  const ScheduleMatchLabel = () => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
    const iconRef = useRef<HTMLDivElement>(null);

    return (
      <>
        <div className="flex items-center gap-1.5">
          <span>Schedule<br />Match (%)</span>
          <div
            ref={iconRef}
            className="relative inline-flex items-center"
            onMouseEnter={() => {
              if (iconRef.current) {
                const rect = iconRef.current.getBoundingClientRect();
                setTooltipPosition({
                  x: rect.left + rect.width / 2,
                  y: rect.top
                });
                setShowTooltip(true);
              }
            }}
            onMouseLeave={() => {
              setShowTooltip(false);
              setTooltipPosition(null);
            }}
          >
            <HelpCircle className="w-4 h-4 text-warning drop-shadow-glow-warning-md hover:drop-shadow-glow-warning-lg transition-all cursor-help" />
          </div>
        </div>
        {showTooltip && tooltipPosition && createPortal(
          <div
            className="fixed px-3 py-2 bg-popover text-white text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y - 8}px`,
              transform: 'translate(-50%, -100%)',
              maxWidth: '280px'
            }}
          >
            <div className="font-semibold mb-1.5 text-warning drop-shadow-glow-warning-lg">Schedule Match</div>
            <div className="text-foreground leading-relaxed">
              Compares when each vehicle is active. Different active hours = better for swapping (one can cover when the other is idle).
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-popover"></div>
          </div>,
          document.body
        )}
      </>
    );
  };

  // Component for Activity Match label with info icon
  const ActivityMatchLabel = () => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
    const iconRef = useRef<HTMLDivElement>(null);

    return (
      <>
        <div className="flex items-center gap-1.5">
          <span>Activity<br />Match (%)</span>
          <div
            ref={iconRef}
            className="relative inline-flex items-center"
            onMouseEnter={() => {
              if (iconRef.current) {
                const rect = iconRef.current.getBoundingClientRect();
                setTooltipPosition({
                  x: rect.left + rect.width / 2,
                  y: rect.top
                });
                setShowTooltip(true);
              }
            }}
            onMouseLeave={() => {
              setShowTooltip(false);
              setTooltipPosition(null);
            }}
          >
            <HelpCircle className="w-4 h-4 text-warning drop-shadow-glow-warning-md hover:drop-shadow-glow-warning-lg transition-all cursor-help" />
          </div>
        </div>
        {showTooltip && tooltipPosition && createPortal(
          <div
            className="fixed px-3 py-2 bg-popover text-white text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y - 8}px`,
              transform: 'translate(-50%, -100%)',
              maxWidth: '280px'
            }}
          >
            <div className="font-semibold mb-1.5 text-warning drop-shadow-glow-warning-lg">Activity Match</div>
            <div className="text-foreground leading-relaxed">
              Compares trip patterns (how long, how far, how fast). More similar trips = better match for swapping.
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-popover"></div>
          </div>,
          document.body
        )}
      </>
    );
  };

  // Component for Relocation Impact label with info icon
  const RelocationImpactLabel = () => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
    const iconRef = useRef<HTMLDivElement>(null);

    return (
      <>
        <div className="flex items-center gap-1.5">
          <span>Relocation<br />Impact (%)</span>
          <div
            ref={iconRef}
            className="relative inline-flex items-center"
            onMouseEnter={() => {
              if (iconRef.current) {
                const rect = iconRef.current.getBoundingClientRect();
                setTooltipPosition({
                  x: rect.left + rect.width / 2,
                  y: rect.top
                });
                setShowTooltip(true);
              }
            }}
            onMouseLeave={() => {
              setShowTooltip(false);
              setTooltipPosition(null);
            }}
          >
            <HelpCircle className="w-4 h-4 text-warning drop-shadow-glow-warning-md hover:drop-shadow-glow-warning-lg transition-all cursor-help" />
          </div>
        </div>
        {showTooltip && tooltipPosition && createPortal(
          <div
            className="fixed px-3 py-2 bg-popover text-white text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y - 8}px`,
              transform: 'translate(-50%, -100%)',
              maxWidth: '280px'
            }}
          >
            <div className="font-semibold mb-1.5 text-warning drop-shadow-glow-warning-lg">Relocation Impact</div>
            <div className="text-foreground leading-relaxed">
              Distance between vehicles' work areas. Closer = easier to swap. Lower impact is better.
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-popover"></div>
          </div>,
          document.body
        )}
      </>
    );
  };

  // Component for Overall Score label with info icon
  const OverallScoreLabel = () => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
    const iconRef = useRef<HTMLDivElement>(null);

    return (
      <>
        <div className="flex items-center gap-1.5">
          <span>Overall<br />Score (%)</span>
          <div
            ref={iconRef}
            className="relative inline-flex items-center"
            onMouseEnter={() => {
              if (iconRef.current) {
                const rect = iconRef.current.getBoundingClientRect();
                setTooltipPosition({
                  x: rect.left + rect.width / 2,
                  y: rect.top
                });
                setShowTooltip(true);
              }
            }}
            onMouseLeave={() => {
              setShowTooltip(false);
              setTooltipPosition(null);
            }}
          >
            <HelpCircle className="w-4 h-4 text-warning drop-shadow-glow-warning-md hover:drop-shadow-glow-warning-lg transition-all cursor-help" />
          </div>
        </div>
        {showTooltip && tooltipPosition && createPortal(
          <div
            className="fixed px-3 py-2 bg-popover text-white text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y - 8}px`,
              transform: 'translate(-50%, -100%)',
              maxWidth: '280px'
            }}
          >
            <div className="font-semibold mb-1.5 text-warning drop-shadow-glow-warning-lg">Overall Score</div>
            <div className="text-foreground leading-relaxed">
              Combines all four metrics above. Higher score = better vehicle pair for swapping. Use the sliders below to adjust how much each metric matters.
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-popover"></div>
          </div>,
          document.body
        )}
      </>
    );
  };

  // Component for Incident Scaling Factor label with info icon
  const IncidentScalingFactorLabel = () => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
    const iconRef = useRef<HTMLDivElement>(null);

    return (
      <>
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Incident Scaling Factor</span>
          <div
            ref={iconRef}
            className="relative inline-flex items-center"
            onMouseEnter={() => {
              if (iconRef.current) {
                const rect = iconRef.current.getBoundingClientRect();
                setTooltipPosition({
                  x: rect.left + rect.width / 2,
                  y: rect.top
                });
                setShowTooltip(true);
              }
            }}
            onMouseLeave={() => {
              setShowTooltip(false);
              setTooltipPosition(null);
            }}
          >
            <HelpCircle className="w-4 h-4 text-warning drop-shadow-glow-warning-md hover:drop-shadow-glow-warning-lg transition-all cursor-help" />
          </div>
        </div>
        {showTooltip && tooltipPosition && createPortal(
          <div
            className="fixed px-3 py-2 bg-popover text-white text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y - 8}px`,
              transform: 'translate(-50%, -100%)',
              maxWidth: '280px'
            }}
          >
            <div className="font-semibold mb-1.5 text-warning drop-shadow-glow-warning-lg">Incident Scaling Factor</div>
            <div className="text-foreground leading-relaxed">
              Multiplies the total number of incidents generated during simulation. Use this to test different demand scenarios. For example, 2.0 = double the incidents, 0.5 = half the incidents.
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-popover"></div>
          </div>,
          document.body
        )}
      </>
    );
  };

  // Component for Severe Incident Ratio label with info icon
  const SevereIncidentRatioLabel = () => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
    const labelRef = useRef<HTMLDivElement>(null);

    return (
      <>
        <div ref={labelRef} className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Severe Incident Ratio</span>
          <div
            className="relative inline-flex items-center"
            onMouseEnter={() => {
              if (labelRef.current) {
                const rect = labelRef.current.getBoundingClientRect();
                // Position tooltip to the left of the label, using the label's left edge as reference
                setTooltipPosition({
                  x: rect.left,
                  y: rect.top
                });
                setShowTooltip(true);
              }
            }}
            onMouseLeave={() => {
              setShowTooltip(false);
              setTooltipPosition(null);
            }}
          >
            <HelpCircle className="w-4 h-4 text-warning drop-shadow-glow-warning-md hover:drop-shadow-glow-warning-lg transition-all cursor-help" />
          </div>
        </div>
        {showTooltip && tooltipPosition && createPortal(
          <div
            className="fixed px-3 py-2 bg-popover text-white text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y - 8}px`,
              transform: 'translate(0, -100%)',
              maxWidth: '280px'
            }}
          >
            <div className="font-semibold mb-1.5 text-warning drop-shadow-glow-warning-lg">Severe Incident Ratio</div>
            <div className="text-foreground leading-relaxed">
              Controls the ratio of severe (Pumping) vs normal (No_Pumping) incidents. Values &gt;1.0 increase severe incidents, &lt;1.0 decrease them. Default is 1.0 (no change).
            </div>
            <div className="absolute top-full left-4 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-popover"></div>
          </div>,
          document.body
        )}
      </>
    );
  };

  const getInterchangeabilityVehicleIds = () => {
    const vehicleIds = new Set(interchangeabilityCSV.map(row => row.vehicle_a).filter(Boolean));
    return Array.from(vehicleIds).sort();
  };

  const getInterchangeabilityData = () => {
    const filteredData = selectedInterchangeabilityVehicle
      ? interchangeabilityCSV.filter(csvRow => csvRow.vehicle_a === selectedInterchangeabilityVehicle)
      : interchangeabilityCSV;

    const totalWeight = weights.geographicOverlap + weights.temporalCompatibility +
      weights.activitySimilarity + weights.redistributionImpact || 1;
    const normalizedWeights = {
      geographicOverlap: weights.geographicOverlap / totalWeight,
      temporalCompatibility: weights.temporalCompatibility / totalWeight,
      activitySimilarity: weights.activitySimilarity / totalWeight,
      redistributionImpact: weights.redistributionImpact / totalWeight,
    };

    return filteredData.map(csvRow => {
      const geoOverlap = parseFloat(csvRow.geographic_overlap || '0') * 100;
      const temporalCompat = parseFloat(csvRow.temporal_compatibility || '0') * 100;
      const activitySim = parseFloat(csvRow.activity_similarity || '0') * 100;
      const redistributionImpact = parseFloat(csvRow.redistribution_impact || '0') * 100;

      const customOverallScore = (
        (geoOverlap / 100) * normalizedWeights.geographicOverlap +
        (temporalCompat / 100) * normalizedWeights.temporalCompatibility +
        (activitySim / 100) * normalizedWeights.activitySimilarity +
        (1 - (redistributionImpact / 100)) * normalizedWeights.redistributionImpact
      ) * 100;

      let feasibility: 'Low' | 'Medium' | 'High' = 'Low';
      const allMetricsNearZero = geoOverlap < 10 && temporalCompat < 10 && activitySim < 10;

      if (allMetricsNearZero) {
        feasibility = 'Low';
      } else {
        const hasGoodOverlap = geoOverlap > 40;
        const hasGoodSchedule = temporalCompat > 40;
        const hasGoodActivity = activitySim > 40;
        const hasLowRelocation = redistributionImpact < 40;
        const favorableConditions = [hasGoodOverlap, hasGoodSchedule, hasGoodActivity, hasLowRelocation].filter(Boolean).length;

        if (hasGoodOverlap && hasGoodSchedule && hasGoodActivity && hasLowRelocation) {
          feasibility = 'High';
        } else if (favorableConditions >= 2 && customOverallScore > 45) {
          feasibility = 'Medium';
        } else {
          feasibility = 'Low';
        }
      }

      const isSelectedVehicle = selectedInterchangeabilityVehicle && csvRow.vehicle_a === selectedInterchangeabilityVehicle;
      const scoreColor = getScoreColor(customOverallScore);

      return {
        vehiclePair: (
          <>
            <span className={isSelectedVehicle ? 'text-warning drop-shadow-glow-warning-lg' : ''}>
              {redactVehicleId(csvRow.vehicle_a || '', isDemoMode)}
            </span>
            <br />
            {redactVehicleId(csvRow.vehicle_b || '', isDemoMode)}
          </>
        ),
        vehicleTypePair: (
          <>
            <span className={isSelectedVehicle ? 'text-warning drop-shadow-glow-warning-lg' : ''}>
              {redactVehicleType(csvRow.vehicle_a_type || '', isDemoMode)}
            </span>
            <br />
            {redactVehicleType(csvRow.vehicle_b_type || '', isDemoMode)}
          </>
        ),
        geoOverlap: formatPercentage(geoOverlap),
        temporalCompat: formatPercentage(temporalCompat),
        activitySim: formatPercentage(activitySim),
        redistributionImpact: formatPercentage(redistributionImpact),
        overallScore: formatPercentage(customOverallScore),
        feasibility,
        _overallScoreValue: customOverallScore,
        _overallScoreColor: scoreColor,
        _vehiclePair: `${csvRow.vehicle_a || ''} / ${csvRow.vehicle_b || ''}`,
        _vehicleTypePair: `${csvRow.vehicle_a_type || ''} / ${csvRow.vehicle_b_type || ''}`,
        _vehicleA: csvRow.vehicle_a || '',
        _vehicleB: csvRow.vehicle_b || '',
        _vehicleAType: csvRow.vehicle_a_type || '',
        _vehicleBType: csvRow.vehicle_b_type || '',
      };
    });
  };

  // Calculate derived values from optimization data
  const totalSavings = optimizationData.aggregateCostReport.length > 0
    ? optimizationData.aggregateCostReport.reduce((sum, row) => {
        // Exclude TOTAL row - it's a summary row, not a vehicle type
        const vehicleType = getColumnValue(row, [
          'VEHICLE TYPE',
          'vehicle_type',
          'Vehicle Type',
          'Vehicle_Type',
          'VEHICLE TYPE',
          'type',
          'Type',
          'TYPE'
        ]);
        if (vehicleType?.toUpperCase() === 'TOTAL') {
          return sum; // Skip TOTAL row
        }
        
        const savings = parseFloat(getColumnValue(row, [
          'CAPITAL COST SAVINGS (AVG)',
          'CAPITAL COST SAVINGS',
          'capital_cost_savings_avg',
          'capital_savings',
          'Capital Savings',
          'total_savings',
          'Total Savings',
          'savings',
          'Savings'
        ]).replace(/[£,\s]/g, '')); // Remove currency symbols and commas
        return sum + (isNaN(savings) ? 0 : savings);
      }, 0)
    : 0;

  const totalRemoved = optimizationData.aggregateCostReport.length > 0
    ? optimizationData.aggregateCostReport.reduce((sum, row) => {
        // Exclude TOTAL row - it's a summary row, not a vehicle type
        const vehicleType = getColumnValue(row, [
          'VEHICLE TYPE',
          'vehicle_type',
          'Vehicle Type',
          'Vehicle_Type',
          'VEHICLE TYPE',
          'type',
          'Type',
          'TYPE'
        ]);
        if (vehicleType?.toUpperCase() === 'TOTAL') {
          return sum; // Skip TOTAL row
        }
        
        const removed = parseFloat(getColumnValue(row, [
          'VEHICLES REMOVED (AVG)',
          'VEHICLES REMOVED',
          'vehicles_removed_avg',
          'removed_vehicles',
          'Removed Vehicles',
          'removed',
          'Removed',
          'total_removed',
          'Total Removed'
        ]));
        return sum + (isNaN(removed) ? 0 : removed);
      }, 0)
    : 0;

  // Calculate fuel reduction from simulation summary (final validation metrics)
  // Use the selected run (defaults to best performing run)
  const simulationSummaryObj = optimizationData.simulationSummary.length > 0 
    ? optimizationData.simulationSummary[selectedRunIndex] || optimizationData.simulationSummary[0]
    : null;
  
  // Extract date range information for historical data
  const getDateRange = () => {
    if (!simulationSummaryObj) return null;
    
    // Try to find date range columns
    const startDate = getColumnValue(simulationSummaryObj, [
      'historical_start_date',
      'HISTORICAL_START_DATE',
      'start_date',
      'START_DATE',
      'date_range_start',
      'DATE_RANGE_START'
    ]);
    const endDate = getColumnValue(simulationSummaryObj, [
      'historical_end_date',
      'HISTORICAL_END_DATE',
      'end_date',
      'END_DATE',
      'date_range_end',
      'DATE_RANGE_END'
    ]);
    
    // If we have both dates, format them nicely
    if (startDate && endDate && startDate !== '0' && endDate !== '0') {
      try {
        // Try to parse and format dates
        const start = new Date(startDate);
        const end = new Date(endDate);
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          const formatDate = (date: Date) => {
            return date.toLocaleDateString('en-GB', { 
              day: 'numeric', 
              month: 'short', 
              year: 'numeric' 
            });
          };
          return `${formatDate(start)} to ${formatDate(end)}`;
        }
      } catch (e) {
        // If parsing fails, return raw values
        return `${startDate} to ${endDate}`;
      }
    }
    
    // Try to find a single date_range column
    const dateRange = getColumnValue(simulationSummaryObj, [
      'date_range',
      'DATE_RANGE',
      'historical_date_range',
      'HISTORICAL_DATE_RANGE',
      'simulation_date_range',
      'SIMULATION_DATE_RANGE'
    ]);
    
    if (dateRange && dateRange !== '0') {
      return dateRange;
    }
    
    return null;
  };
  
  const dateRange = getDateRange();
  
  // Calculate fuel reduction from simulation summary (final validation metrics)
  let fuelReduction = 0;
  let fuelConsumed = 0;
  let initialFuel = 0;
  
  // Try to get fuel data from simulation summary (final validation metrics)
  if (simulationSummaryObj) {
    // Try to find fuel consumption in simulation summary
    initialFuel = parseFloat(getColumnValue(simulationSummaryObj, [
      'initial_fuel_consumption',
      'initial_fuel_consumed',
      'initial_total_fuel',
      'initial_fuel_litres',
      'initial_fuel_liters',
      'INITIAL FUEL CONSUMPTION',
      'INITIAL FUEL CONSUMED',
      'INITIAL TOTAL FUEL',
      'INITIAL FUEL LITRES',
      'fuel_consumption_before',
      'fuel_before',
      'FUEL CONSUMPTION (BEFORE)',
      'FUEL (BEFORE)'
    ])) || 0;
    
    const optimizedFuel = parseFloat(getColumnValue(simulationSummaryObj, [
      'optimized_fuel_consumption',
      'optimized_fuel_consumed',
      'optimized_total_fuel',
      'optimized_fuel_litres',
      'optimized_fuel_liters',
      'OPTIMIZED FUEL CONSUMPTION',
      'OPTIMIZED FUEL CONSUMED',
      'OPTIMIZED TOTAL FUEL',
      'OPTIMIZED FUEL LITRES',
      'fuel_consumption_after',
      'fuel_after',
      'FUEL CONSUMPTION (AFTER)',
      'FUEL (AFTER)'
    ])) || 0;
    
    if (initialFuel > 0) {
      fuelConsumed = optimizedFuel;
      fuelReduction = ((initialFuel - optimizedFuel) / initialFuel) * 100;
    }
  }
  
  // Fallback: Try to get fuel data from simulation summary if not found above
  if (fuelReduction === 0 && fuelConsumed === 0 && simulationSummaryObj) {
    initialFuel = parseFloat(getColumnValue(simulationSummaryObj, [
      'initial_fuel_consumption',
      'initial_fuel',
      'fuel_before',
      'INITIAL FUEL CONSUMPTION',
      'INITIAL FUEL',
      'FUEL BEFORE'
    ])) || 0;
    
    const optimizedFuel = parseFloat(getColumnValue(simulationSummaryObj, [
      'final_fuel_consumption',
      'final_fuel',
      'fuel_after',
      'FINAL FUEL CONSUMPTION',
      'FINAL FUEL',
      'FUEL AFTER'
    ])) || 0;
    
    if (initialFuel > 0) {
      fuelConsumed = optimizedFuel;
      fuelReduction = ((initialFuel - optimizedFuel) / initialFuel) * 100;
    }
  }

  // Calculate response time improvement from simulation summary
  let responseTimeImprovement = 0;
  let initialResponseTime = 0;
  let optimizedResponseTime = 0;
  
  if (simulationSummaryObj) {
    initialResponseTime = parseFloat(getColumnValue(simulationSummaryObj, [
      'initial_avg_response_time_minutes',
      'INITIAL_AVG_RESPONSE_TIME_MINUTES'
    ])) || 0;
    
    optimizedResponseTime = parseFloat(getColumnValue(simulationSummaryObj, [
      'optimized_avg_response_time_minutes',
      'OPTIMIZED_AVG_RESPONSE_TIME_MINUTES'
    ])) || 0;
    
    if (initialResponseTime > 0) {
      // Calculate improvement percentage (lower response time is better)
      responseTimeImprovement = ((initialResponseTime - optimizedResponseTime) / initialResponseTime) * 100;
    }
  }

  // Use run-specific initial utilization if available, otherwise use aggregate
  // Initial utilization should be consistent across runs (baseline), but using run-specific ensures
  // we're comparing apples to apples when viewing a specific run
  const initialUtilizationData = runSpecificData.initialUtilization.length > 0 
    ? runSpecificData.initialUtilization 
    : optimizationData.initialUtilization;

  // Load available vehicles/types/stations from sim_env_config.json for ES mode
  useEffect(() => {
    if (serviceType !== 'emergency_services') return;
    
    let isCancelled = false;
    
    import('../config/api').then(({ API_ENDPOINTS }) => {
      fetch(API_ENDPOINTS.simEnvConfig(serviceType))
        .then((res: Response) => {
          if (isCancelled) return;
          if (!res.ok) {
            if (res.status === 404) {
              logger.debug('sim_env_config.json not found, falling back to utilization data');
              return null;
            }
            throw new Error(`Failed to load sim_env_config.json: ${res.status}`);
          }
          return res.json();
        })
        .then((response: any) => {
          if (isCancelled || !response) return;
          
          // The API returns { success, data, source, region }
          const config = response.data || response;
          
          const types = new Set<string>();
          const stations: string[] = [];
          const vehicles: string[] = [];
          
          // Extract from fire_stations section
          // Structure: config.fire_stations.stations is an object where keys are station names
          if (config.fire_stations && typeof config.fire_stations === 'object') {
            const stationsData = config.fire_stations.stations || config.fire_stations;
            Object.entries(stationsData).forEach(([stationName, stationData]: [string, any]) => {
              if (stationData && typeof stationData === 'object' && stationData.vehicles) {
                stations.push(stationName);
                
                // Extract vehicle types from this station
                Object.entries(stationData.vehicles).forEach(([vehicleType, vehicleCounts]: [string, any]) => {
                  if (vehicleCounts && typeof vehicleCounts === 'object') {
                    const diesel = vehicleCounts.diesel || 0;
                    const ev = vehicleCounts.ev || 0;
                    const total = diesel + ev;
                    
                    if (total > 0) {
                      types.add(vehicleType);
                      
                      // Generate vehicle IDs: StationName_VehicleType_1, StationName_VehicleType_2, etc.
                      for (let i = 1; i <= total; i++) {
                        vehicles.push(`${stationName}_${vehicleType}_${i}`);
                      }
                    }
                  }
                });
              }
            });
          }
          
          // Add to shared context
          if (vehicles.length > 0) addAvailableVehicles(Array.from(vehicles));
          if (types.size > 0) addAvailableVehicleTypes(Array.from(types));
          if (stations.length > 0) addAvailableStations(stations);
        })
        .catch(err => {
          if (isCancelled) return;
          logger.debug('Error loading sim_env_config.json, falling back to utilization data:', err.message);
        });
    });
    
    return () => {
      isCancelled = true;
    };
  }, [serviceType, addAvailableVehicles, addAvailableVehicleTypes, addAvailableStations]);

  // Extract available vehicles/types/stations from initial utilization data (fallback for Logistics mode or if config not available)
  useEffect(() => {
    if (serviceType === 'emergency_services') return; // Skip for ES mode - use sim_env_config.json instead
    if (initialUtilizationData.length === 0) return;
    
    const vehicles: string[] = [];
    const types: string[] = [];
    const stations: string[] = [];
    
    initialUtilizationData.forEach(row => {
      const vehicleId = getColumnValue(row, [
        'vehicle_id', 'VEHICLE_ID', 'Vehicle ID', 'vehicleId', 'Vehicle_Id', 'VEHICLE ID'
      ]);
      const vehicleType = getColumnValue(row, [
        'VEHICLE TYPE', 'vehicle_type', 'Vehicle Type', 'vehicleType', 'type', 'Type', 'VEHICLE_TYPE', 'Vehicle_Type'
      ]);
      const station = getColumnValue(row, [
        'fire_station', 'FIRE_STATION', 'Fire Station', 'fireStation', 'station', 'Station', 'STATION'
      ]);
      
      if (vehicleId) vehicles.push(vehicleId);
      if (vehicleType) types.push(vehicleType);
      if (station) stations.push(station);
    });
    
    // Add to shared context (merges with data from other tabs)
    if (vehicles.length > 0) addAvailableVehicles(vehicles);
    if (types.length > 0) addAvailableVehicleTypes(types);
    if (stations.length > 0) addAvailableStations(stations);
  }, [serviceType, initialUtilizationData, addAvailableVehicles, addAvailableVehicleTypes, addAvailableStations]);

  // Count active vehicles per vehicle type from initial utilization data
  // Active vehicles are those with utilization > 0
  const getActiveVehiclesByType = useMemo<{ original: Record<string, number>, normalized: Record<string, { original: string, count: number }> }>(() => {
    const activeCounts: Record<string, number> = {};
    const activeCountsNormalized: Record<string, { original: string, count: number }> = {}; // For case-insensitive matching
    
    if (initialUtilizationData.length === 0) {
      logger.info('No initial utilisation data available for active vehicle counting');
      return { original: activeCounts, normalized: activeCountsNormalized };
    }
    
    // Log first row to see what columns we have
    if (initialUtilizationData.length > 0) {
      logger.info('First initial utilization row keys:', Object.keys(initialUtilizationData[0]));
      logger.info('First initial utilization row:', initialUtilizationData[0]);
    }
    
    initialUtilizationData.forEach((row, index) => {
      const vehicleType = getColumnValue(row, [
        'VEHICLE TYPE',
        'vehicle_type',
        'Vehicle Type',
        'vehicleType',
        'type',
        'Type',
        'VEHICLE_TYPE',
        'Vehicle_Type'
      ]);
      
      if (!vehicleType) {
        if (index < 3) {
          logger.warn(`Row ${index} has no vehicle type. Available keys:`, Object.keys(row));
        }
        return; // Skip rows without vehicle type
      }
      
      const utilization = parseFloat(getColumnValue(row, [
        'UTILIZATION',
        'utilization',
        'Utilization',
        'utilization_percent',
        'Utilization Percent',
        'utilization_percentage',
        'Utilization Percentage',
        'UTILIZATION_PERCENT',
        'Utilization_Percent',
        'utilization%',
        'Utilization%',
        'UTILIZATION (%)',
        'Utilization (%)'
      ])) || 0;
      
      // Count all vehicles in initial_utilization.csv as active
      // (This file typically only contains vehicles that were active/used)
      // If utilization is available and > 0, use that as confirmation, but count all rows
      if (vehicleType) {
        activeCounts[vehicleType] = (activeCounts[vehicleType] || 0) + 1;
        // Also store normalized (lowercase) version for case-insensitive matching
        const normalized = vehicleType.toLowerCase().trim();
        if (activeCountsNormalized[normalized]) {
          activeCountsNormalized[normalized].count += 1;
        } else {
          activeCountsNormalized[normalized] = { original: vehicleType, count: 1 };
        }
        
        if (index < 3) {
          logger.info(`Row ${index}: vehicleType="${vehicleType}", utilization=${utilization}, count=${activeCounts[vehicleType]}`);
        }
      }
    });
    
    logger.info('Active vehicles by type (original):', activeCounts);
    logger.info('Active vehicles by type (normalized):', activeCountsNormalized);
    logger.info('Total initial utilization rows processed:', initialUtilizationData.length);
    
    // Calculate total active vehicles across all types
    const totalActiveVehicles = Object.values(activeCounts).reduce((sum, count) => sum + count, 0);
    logger.info(`📊 Total active vehicles counted: ${totalActiveVehicles}`);
    
    if (Object.keys(activeCounts).length === 0 && initialUtilizationData.length > 0) {
      logger.warn('⚠️ No active vehicles counted! This might indicate a column name mismatch.');
      logger.warn('Available columns in first row:', Object.keys(initialUtilizationData[0]));
    } else if (Object.keys(activeCounts).length > 0) {
      logger.info(`✅ Successfully counted ${Object.keys(activeCounts).length} vehicle types with active vehicles`);
    }
    
    // Return both original and normalized for flexible matching
    return { original: activeCounts, normalized: activeCountsNormalized };
  }, [initialUtilizationData]);

  // Use run-specific final utilization if available, otherwise use aggregate
  // Run-specific data shows what this specific run decided, aggregate shows consensus across all runs
  const finalUtilizationData = runSpecificData.finalUtilization.length > 0 
    ? runSpecificData.finalUtilization 
    : optimizationData.finalUtilization;

  // Use run-specific optimization log if available, otherwise use aggregate
  // Note: Run-specific optimization log may not always be available, so we fall back to aggregate
  const optimizationLogData = runSpecificData.optimizationLog.length > 0 
    ? runSpecificData.optimizationLog 
    : optimizationData.optimizationLog;

  // Count active vehicles per vehicle type from final utilization data (after optimization)
  const getActiveVehiclesAfterByType = useMemo<{ original: Record<string, number>, normalized: Record<string, { original: string, count: number }> }>(() => {
    const activeCounts: Record<string, number> = {};
    const activeCountsNormalized: Record<string, { original: string, count: number }> = {};
    
    if (finalUtilizationData.length === 0) {
      logger.info('No final utilization data available for active vehicle counting');
      return { original: activeCounts, normalized: activeCountsNormalized };
    }
    
    // Log first row to see what columns we have
    if (finalUtilizationData.length > 0) {
      logger.info('First final utilization row keys:', Object.keys(finalUtilizationData[0]));
    }
    
    finalUtilizationData.forEach((row, index) => {
      const vehicleType = getColumnValue(row, [
        'VEHICLE TYPE',
        'vehicle_type',
        'Vehicle Type',
        'vehicleType',
        'type',
        'Type',
        'VEHICLE_TYPE',
        'Vehicle_Type'
      ]);
      
      if (!vehicleType) {
        if (index < 3) {
          logger.warn(`Final row ${index} has no vehicle type. Available keys:`, Object.keys(row));
        }
        return; // Skip rows without vehicle type
      }
      
      // Count all vehicles in final_utilization.csv as active
      if (vehicleType) {
        activeCounts[vehicleType] = (activeCounts[vehicleType] || 0) + 1;
        // Also store normalized (lowercase) version for case-insensitive matching
        const normalized = vehicleType.toLowerCase().trim();
        if (activeCountsNormalized[normalized]) {
          activeCountsNormalized[normalized].count += 1;
        } else {
          activeCountsNormalized[normalized] = { original: vehicleType, count: 1 };
        }
      }
    });
    
    logger.info('Active vehicles after optimization by type (original):', activeCounts);
    logger.info('Active vehicles after optimization by type (normalized):', activeCountsNormalized);
    logger.info('Total final utilization rows processed:', finalUtilizationData.length);
    
    return { original: activeCounts, normalized: activeCountsNormalized };
  }, [finalUtilizationData]);

  // Calculate active vehicles removed per vehicle type
  const getActiveVehiclesRemovedByType = useMemo(() => {
    const removedCounts: Record<string, number> = {};
    
    // Get all unique vehicle types from both initial and final
    const allVehicleTypes = new Set([
      ...Object.keys(getActiveVehiclesByType.original),
      ...Object.keys(getActiveVehiclesByType.normalized).map(k => getActiveVehiclesByType.normalized[k].original)
    ]);
    
    allVehicleTypes.forEach(vehicleType => {
      // Get active count before (from initial)
      let activeBefore = 0;
      if (getActiveVehiclesByType.original[vehicleType] !== undefined) {
        activeBefore = getActiveVehiclesByType.original[vehicleType];
      } else {
        const normalized = vehicleType.toLowerCase().trim();
        if (getActiveVehiclesByType.normalized[normalized]) {
          activeBefore = getActiveVehiclesByType.normalized[normalized].count;
        }
      }
      
      // Get active count after (from final)
      let activeAfter = 0;
      if (getActiveVehiclesAfterByType.original[vehicleType] !== undefined) {
        activeAfter = getActiveVehiclesAfterByType.original[vehicleType];
      } else {
        const normalized = vehicleType.toLowerCase().trim();
        if (getActiveVehiclesAfterByType.normalized[normalized]) {
          activeAfter = getActiveVehiclesAfterByType.normalized[normalized].count;
        }
      }
      
      // Calculate removed = before - after
      const removed = Math.max(0, activeBefore - activeAfter);
      if (removed > 0 || activeBefore > 0) {
        removedCounts[vehicleType] = removed;
      }
    });
    
    logger.info('Active vehicles removed by type:', removedCounts);
    return removedCounts;
  }, [getActiveVehiclesByType, getActiveVehiclesAfterByType]);

  // Calculate average utilization by vehicle type (Before and After)
  // Before: ALL vehicles in initial fleet
  // After: Only REMAINING vehicles (excludes removed vehicles)
  const averageUtilizationByType = useMemo(() => {
    const beforeUtil: Record<string, { sum: number; count: number; activeCount: number }> = {};
    const afterUtilByType: Record<string, { vehicles: Array<{ id: string; utilization: number }>; activeCount: number }> = {};
    const epsilon = 0.1; // Threshold for considering a vehicle active (0.1%)
    
    // Build a map of "after" fleet counts from costDataSource (aggregate_cost_report.csv)
    // This tells us how many vehicles REMAIN per type after optimization
    const afterFleetCountByType: Record<string, number> = {};
    const removedCountByType: Record<string, number> = {}; // Track how many were removed per type
    const costSource = runSpecificData.costReport.length > 0 
      ? runSpecificData.costReport 
      : optimizationData.aggregateCostReport;
    
    costSource.forEach(row => {
      const vehicleType = getColumnValue(row, [
        'VEHICLE TYPE', 'vehicle_type', 'Vehicle Type', 'vehicleType'
      ]);
      if (!vehicleType || vehicleType.toUpperCase() === 'TOTAL') return;
      
      // Get "before" and "removed" to calculate "after" = before - removed
      const beforeFleet = parseFloat(getColumnValue(row, [
        'BEFORE (AVG)', 'BEFORE', 'before_avg', 'before', 'Initial', 'initial', 'initial_fleet'
      ])) || 0;
      const removedVehicles = parseFloat(getColumnValue(row, [
        'VEHICLES REMOVED (AVG)', 'VEHICLES REMOVED', 'vehicles_removed_avg', 'removed_vehicles',
        'Removed Vehicles', 'REMOVED', 'removed', 'Removed', 'vehicles_removed', 
        'total_removed', 'Total Removed'
      ])) || 0;
      
      afterFleetCountByType[vehicleType] = Math.max(0, beforeFleet - removedVehicles);
      removedCountByType[vehicleType] = removedVehicles;
    });
    
    // Calculate average utilization BEFORE (from initial) - include ALL vehicles
    initialUtilizationData.forEach(row => {
      const vehicleType = getColumnValue(row, [
        'vehicle_type', 'VEHICLE_TYPE', 'Vehicle Type', 'vehicleType', 'Vehicle_Type', 'type', 'Type', 'TYPE'
      ]);
      if (!vehicleType) return;
      
      const utilization = parseFloat(getColumnValue(row, [
        'utilization_rate', 'UTILIZATION_RATE', 'Utilization Rate', 'utilizationRate', 'Utilization_Rate',
        'utilization', 'Utilization', 'UTILIZATION', 'utilization_percent', 'Utilization Percent',
        'utilization_percentage', 'Utilization Percentage'
      ])) || 0;
      
      if (!beforeUtil[vehicleType]) {
        beforeUtil[vehicleType] = { sum: 0, count: 0, activeCount: 0 };
      }
      beforeUtil[vehicleType].sum += utilization;
      beforeUtil[vehicleType].count += 1;
      if (utilization > epsilon) {
        beforeUtil[vehicleType].activeCount += 1;
      }
    });
    
    // Collect utilization data from final (includes removed vehicles at 0%)
    finalUtilizationData.forEach(row => {
      const vehicleId = getColumnValue(row, [
        'vehicle_id', 'VEHICLE_ID', 'Vehicle ID', 'vehicleId', 'Vehicle_Id', 'VEHICLE ID'
      ]);
      const vehicleType = getColumnValue(row, [
        'vehicle_type', 'VEHICLE_TYPE', 'Vehicle Type', 'vehicleType', 'Vehicle_Type', 'type', 'Type', 'TYPE'
      ]);
      if (!vehicleId || !vehicleType) return;
      
      const utilization = parseFloat(getColumnValue(row, [
        'utilization_rate', 'UTILIZATION_RATE', 'Utilization Rate', 'utilizationRate', 'Utilization_Rate',
        'utilization', 'Utilization', 'UTILIZATION', 'utilization_percent', 'Utilization Percent',
        'utilization_percentage', 'Utilization Percentage'
      ])) || 0;
      
      if (!afterUtilByType[vehicleType]) {
        afterUtilByType[vehicleType] = { vehicles: [], activeCount: 0 };
      }
      afterUtilByType[vehicleType].vehicles.push({ id: vehicleId, utilization });
      if (utilization > epsilon) {
        afterUtilByType[vehicleType].activeCount += 1;
      }
    });
    
    // Convert to averages
    // CRITICAL: Use afterFleetCountByType for the correct "after" fleet count (excludes removed vehicles)
    const result: Record<string, { before: number; after: number; beforeCount: number; afterCount: number; beforeActive: number; afterActive: number }> = {};
    
    Object.keys(beforeUtil).forEach(type => {
      const beforeAvg = beforeUtil[type].count > 0 ? beforeUtil[type].sum / beforeUtil[type].count : 0;
      
      // Check if this vehicle type was ACTUALLY optimized (had vehicles removed)
      // Types with removed=0 were not changed, so their "after" should equal "before"
      const wasActuallyOptimized = (removedCountByType[type] || 0) > 0;
      
      if (!wasActuallyOptimized) {
        // Vehicle type was NOT optimized (no vehicles removed) - "after" equals "before" (unchanged)
        result[type] = {
          before: beforeAvg,
          after: beforeAvg, // Same as before - no optimization was done
          beforeCount: beforeUtil[type].count,
          afterCount: beforeUtil[type].count, // Same count
          beforeActive: beforeUtil[type].activeCount,
          afterActive: beforeUtil[type].activeCount // Same active count
        };
        return;
      }
      
      // Vehicle type WAS optimized (had vehicles removed) - calculate actual "after" values
      if (!afterUtilByType[type]) return;
      
      // Get the correct after fleet count from cost report (before - removed)
      const afterFleetCount = afterFleetCountByType[type];
      
      // Calculate "after" sum from ALL vehicles in finalUtilizationData
      // Removed vehicles contribute 0% to the sum (they have 0 util in final)
      // Retained inactive vehicles also contribute 0% but ARE counted in afterFleetCount
      // This correctly handles both cases:
      //   - Removed vehicles: 0% util, NOT in afterFleetCount denominator
      //   - Retained inactive: 0% util, YES in afterFleetCount denominator
      const afterSum = afterUtilByType[type].vehicles.reduce((sum, v) => sum + v.utilization, 0);
      
      // Count active vehicles in the remaining fleet (for display purposes)
      // We can only estimate this - active count from final data that would fit in afterFleetCount
      const afterActiveCount = Math.min(
        afterUtilByType[type].activeCount,
        afterFleetCount
      );
      
      result[type] = {
        before: beforeAvg,
        after: afterFleetCount > 0 ? afterSum / afterFleetCount : 0,
        beforeCount: beforeUtil[type].count,
        afterCount: afterFleetCount,
        beforeActive: beforeUtil[type].activeCount,
        afterActive: afterActiveCount
      };
    });
    
    return result;
  }, [initialUtilizationData, finalUtilizationData, runSpecificData.costReport, optimizationData.aggregateCostReport]);

  // Prepare cost data for table - use run-specific cost report if available, otherwise use aggregate
  // Run-specific data shows what this specific run decided, aggregate shows consensus across all runs
  const costDataSource = runSpecificData.costReport.length > 0 
    ? runSpecificData.costReport 
    : optimizationData.aggregateCostReport;
    
  const costData = costDataSource.map(row => {
    const vehicleTypeRaw = getColumnValue(row, [
      'VEHICLE TYPE',
      'vehicle_type',
      'Vehicle Type',
      'vehicleType',
      'type',
      'Type'
    ]);
    const vehicleType = redactVehicleType(vehicleTypeRaw || 'Unknown', isDemoMode);
    const unitCost = getColumnValue(row, [
      'UNIT COST',
      'unit_cost',
      'Unit Cost',
      'unitCost',
      'cost',
      'Cost',
      'price',
      'Price'
    ]);
    // Use active vehicle count if available, otherwise fall back to total fleet
    const beforeFleetTotal = getColumnValue(row, [
      'BEFORE',
      'before',
      'Before',
      'before_fleet',
      'Before Fleet',
      'beforeFleet',
      'initial_fleet',
      'Initial Fleet',
      'baseline_fleet',
      'Baseline Fleet'
    ]);
    // Get active vehicle count for this vehicle type if we have initial utilization data
    // Only use active count if we have data AND found a match, otherwise use 0 (not total fleet)
    // IMPORTANT: Use vehicleTypeRaw (original) for lookups, not vehicleType (redacted)
    const hasInitialUtilizationData = initialUtilizationData.length > 0;
    let activeCount: number | null = null;
    if (hasInitialUtilizationData && vehicleTypeRaw) {
      // Try exact match first with original type
      if (getActiveVehiclesByType.original[vehicleTypeRaw] !== undefined) {
        activeCount = getActiveVehiclesByType.original[vehicleTypeRaw];
      } else {
        // Try case-insensitive match
        const normalized = vehicleTypeRaw.toLowerCase().trim();
        if (getActiveVehiclesByType.normalized[normalized]) {
          activeCount = getActiveVehiclesByType.normalized[normalized].count;
        }
      }
    }
    
    // Use active count if found, otherwise use 0 (don't fall back to total fleet - that's misleading)
    // NEVER use beforeFleetTotal for "Current Fleet" - it includes inactive vehicles
    // The "Current Fleet" column should only show active vehicles
    const beforeFleet = activeCount !== null ? activeCount.toString() : '0';
    
    // Calculate inactive fleet = Total Fleet - Active Fleet
    const beforeFleetTotalNum = parseFloat(beforeFleetTotal) || 0;
    const beforeFleetActiveNum = parseFloat(beforeFleet) || 0;
    const beforeFleetInactiveNum = Math.max(0, beforeFleetTotalNum - beforeFleetActiveNum);
    const beforeFleetInactive = hasInitialUtilizationData
      ? beforeFleetInactiveNum.toString()
      : '0'; // If we don't have initial utilization data, we can't calculate inactive
    
    // Debug logging for all rows to see what's happening
    const rowIndex = costDataSource.indexOf(row);
    logger.info(`[Row ${rowIndex}] Vehicle Type: "${vehicleType}", Active Count: ${activeCount}, Before Fleet Total: ${beforeFleetTotal}, Final Before Fleet: ${beforeFleet}, Before Fleet Inactive: ${beforeFleetInactive}, Has Initial Data: ${hasInitialUtilizationData}`);
    if (rowIndex === 0) {
      logger.info(`Available active vehicle types (original):`, Object.keys(getActiveVehiclesByType.original));
      logger.info(`Available active vehicle types (normalized):`, Object.keys(getActiveVehiclesByType.normalized));
      logger.info(`Total initial utilization rows: ${initialUtilizationData.length}`);
    }
    const afterFleetTotal = getColumnValue(row, [
      'AFTER (AVG)',
      'AFTER',
      'after_avg',
      'after',
      'After',
      'after_fleet',
      'After Fleet',
      'afterFleet',
      'optimized_fleet',
      'Optimized Fleet',
      'final_fleet',
      'Final Fleet'
    ]);
    
    // Calculate active and inactive fleet after optimization
    const hasUtilizationData = initialUtilizationData.length > 0 && finalUtilizationData.length > 0;
    const afterFleetTotalNum = parseFloat(afterFleetTotal) || 0;
    let afterFleetActive: number | null = null;
    let afterFleetInactive: number | null = null;
    
    // IMPORTANT: Use vehicleTypeRaw (original) for lookups, not vehicleType (redacted)
    if (hasUtilizationData && vehicleTypeRaw) {
      // Get active count after (from final utilization)
      if (getActiveVehiclesAfterByType.original[vehicleTypeRaw] !== undefined) {
        afterFleetActive = getActiveVehiclesAfterByType.original[vehicleTypeRaw];
      } else {
        // Try case-insensitive match
        const normalized = vehicleTypeRaw.toLowerCase().trim();
        if (getActiveVehiclesAfterByType.normalized[normalized]) {
          afterFleetActive = getActiveVehiclesAfterByType.normalized[normalized].count;
        }
      }
      
      // Calculate inactive after = Total After - Active After
      if (afterFleetActive !== null) {
        afterFleetInactive = Math.max(0, afterFleetTotalNum - afterFleetActive);
      } else {
        // If we have utilization data but couldn't find active vehicles, assume all are inactive
        // This handles cases where vehicle type names don't match exactly
        if (afterFleetTotalNum > 0) {
          afterFleetActive = 0;
          afterFleetInactive = afterFleetTotalNum;
        }
      }
    }
    
    // Debug logging for first few rows (reuse rowIndex from above)
    if (rowIndex < 3) {
      logger.info(`[After Fleet Calc] Vehicle Type: "${vehicleType}", Total: ${afterFleetTotalNum}, Active: ${afterFleetActive}, Inactive: ${afterFleetInactive}, Has Data: ${hasUtilizationData}`);
    }
    
    const capitalSavings = getColumnValue(row, [
      'CAPITAL COST SAVINGS (AVG)',
      'CAPITAL COST SAVINGS',
      'capital_cost_savings_avg',
      'capital_savings',
      'Capital Savings',
      'capitalSavings',
      'savings',
      'Savings',
      'total_savings',
      'Total Savings'
    ]);
    // Get active vehicles removed for this vehicle type if we have utilization data
    // Fall back to total removed from CSV if data not available
    // IMPORTANT: Use vehicleTypeRaw (original) for lookups, not vehicleType (redacted)
    let activeRemoved: number | null = null;
    if (hasUtilizationData && vehicleTypeRaw) {
      // Try exact match first with original type
      if (getActiveVehiclesRemovedByType[vehicleTypeRaw] !== undefined) {
        activeRemoved = getActiveVehiclesRemovedByType[vehicleTypeRaw];
      } else {
        // Try case-insensitive match
        const normalized = vehicleTypeRaw.toLowerCase().trim();
        // Find the original vehicle type from normalized map
        const normalizedKeys = Object.keys(getActiveVehiclesRemovedByType);
        const matchingKey = normalizedKeys.find(k => k.toLowerCase().trim() === normalized);
        if (matchingKey) {
          activeRemoved = getActiveVehiclesRemovedByType[matchingKey];
        }
      }
    }
    
    // Fall back to total removed from CSV if active removed not available
    const removedVehiclesTotal = getColumnValue(row, [
      'VEHICLES REMOVED (AVG)',
      'VEHICLES REMOVED',
      'vehicles_removed_avg',
      'removed_vehicles',
      'Removed Vehicles',
      'removedVehicles',
      'removed',
      'Removed',
      'total_removed',
      'Total Removed'
    ]);
    
    // Calculate inactive vehicles removed = Total Removed - Active Removed
    const totalRemovedNum = parseFloat(removedVehiclesTotal) || 0;
    const activeRemovedNum = activeRemoved !== null ? activeRemoved : 0;
    const inactiveRemovedNum = Math.max(0, totalRemovedNum - activeRemovedNum);
    
    // Calculate optimized fleet correctly: Initial Fleet - Removed = Final Fleet
    // ALWAYS use this calculation as the source of truth for optimized fleet size
    // The breakdown (active/inactive) is for display only, but the total must be Initial - Removed
    const calculatedAfterFleet = Math.max(0, beforeFleetTotalNum - totalRemovedNum);
    
    // Always use calculatedAfterFleet as the optimized fleet size
    // This ensures consistency regardless of scenario (Optimise the Past vs Optimise for Future)
    const afterFleet = calculatedAfterFleet.toString();
    
    // Debug logging for first few rows
    if (rowIndex < 3) {
      logger.info(`[After Fleet Calc] Vehicle Type: "${vehicleType}", Before Total: ${beforeFleetTotalNum}, Removed: ${totalRemovedNum}, Calculated After: ${calculatedAfterFleet}, CSV After: ${afterFleetTotalNum}, Active: ${afterFleetActive}, Inactive: ${afterFleetInactive}, Final After Fleet: ${afterFleet}`);
    }
    
    // Use active removed if we have utilization data, otherwise use total
    const removedVehicles = (hasUtilizationData && vehicleType && activeRemoved !== null)
      ? activeRemoved.toString()
      : removedVehiclesTotal;
    
    return {
      vehicleType,
      unitCost: formatCurrency(unitCost),
      beforeFleet: formatNumber(beforeFleet, 0),
      beforeFleetInactive: formatNumber(beforeFleetInactive, 0),
      afterFleet: formatNumber(afterFleet, 0),
      afterFleetActive: afterFleetActive !== null ? formatNumber(afterFleetActive, 0) : null,
      afterFleetInactive: afterFleetInactive !== null ? formatNumber(afterFleetInactive, 0) : null,
      // Store breakdown values, ensuring they add up to calculatedAfterFleet
      // Normalize breakdown to ensure it always adds up to calculatedAfterFleet
      // This ensures the breakdown updates correctly when the total changes
      _afterFleetActive: (() => {
        if (afterFleetActive !== null) {
          const active = Math.round(afterFleetActive);
          // Ensure active doesn't exceed the calculated total
          return Math.min(active, Math.max(0, calculatedAfterFleet));
        }
        return null;
      })(),
      _afterFleetInactive: (() => {
        if (afterFleetActive !== null) {
          // Always calculate inactive as: total - active
          // This ensures the breakdown always matches calculatedAfterFleet
          const active = Math.min(Math.round(afterFleetActive), Math.max(0, calculatedAfterFleet));
          return Math.max(0, calculatedAfterFleet - active);
        }
        if (afterFleetInactive !== null) {
          // If we only have inactive, ensure it doesn't exceed total
          return Math.min(Math.round(afterFleetInactive), Math.max(0, calculatedAfterFleet));
        }
        return null;
      })(),
      capitalSavings: (() => {
        const numValue = parseFloat(capitalSavings.replace(/[£,\s]/g, '')) || 0;
        return numValue.toLocaleString('en-GB');
      })(),
      removedVehicles: formatNumber(removedVehicles, 0), // Active removed (or total if no data)
      removedVehiclesActive: hasUtilizationData && activeRemoved !== null ? formatNumber(activeRemoved, 0) : null,
      removedVehiclesInactive: hasUtilizationData && activeRemoved !== null ? formatNumber(inactiveRemovedNum, 0) : null,
      removedVehiclesTotal: formatNumber(totalRemovedNum, 0), // Always show total
      _originalUnitCost: parseFloat(unitCost.replace(/[£,\s]/g, '')) || 0,
      _beforeFleet: Math.round(parseFloat(beforeFleet) || 0), // Round to whole number (fleet size must be integer)
      _beforeFleetInactive: Math.round(beforeFleetInactiveNum), // Round to whole number
      _afterFleet: Math.round(parseFloat(afterFleet) || 0), // Round to whole number (fleet size must be integer)
      _removedVehicles: Math.round(parseFloat(removedVehicles) || 0), // Round to whole number
      _removedVehiclesActive: activeRemovedNum,
      _removedVehiclesInactive: inactiveRemovedNum,
      _removedVehiclesTotal: totalRemovedNum
    };
  });
  
  // Prepare simulation data for table
  // Transform the single simulation summary object into table rows
  // Use useMemo to recalculate when costData changes (for consistency with Vehicle Type Summary)
  const simulationData = useMemo(() => {
    if (!simulationSummaryObj) return [];
    return [
    {
      metric: 'Active Vehicles',
      before: (() => {
        // Use CSV data directly for accuracy - this is the source of truth for active vehicles
        if (simulationSummaryObj) {
          const val = parseFloat(getColumnValue(simulationSummaryObj, ['total_active_fleet', 'TOTAL ACTIVE FLEET'])) || 0;
          return formatNumber(val, 0);
        }
        // Fall back to costData if CSV not available
        const nonTotalRows = costData.filter(r => r.vehicleType?.toUpperCase() !== 'TOTAL');
        const totalActiveBefore = nonTotalRows.reduce((sum, r) => {
          return sum + (r._beforeFleet || 0);
        }, 0);
        return formatNumber(totalActiveBefore, 0);
      })(),
      after: (() => {
        // Use CSV data directly for accuracy
        if (simulationSummaryObj) {
          const before = parseFloat(getColumnValue(simulationSummaryObj, ['total_active_fleet', 'TOTAL ACTIVE FLEET'])) || 0;
          const removed = parseFloat(getColumnValue(simulationSummaryObj, ['active_vehicles_removed', 'ACTIVE VEHICLES REMOVED'])) || 0;
          const totalActiveAfter = before - removed;
          return <span className="text-success font-medium">{formatNumber(totalActiveAfter, 0)}</span>;
        }
        // Fall back to costData if CSV not available
        const nonTotalRows = costData.filter(r => r.vehicleType?.toUpperCase() !== 'TOTAL');
        let totalActiveAfter = 0;
        let hasBreakdown = false;
        nonTotalRows.forEach(r => {
          if (r._afterFleetActive !== null && r._afterFleetActive !== undefined) {
            hasBreakdown = true;
            totalActiveAfter += (r._afterFleetActive || 0);
          }
        });
        if (!hasBreakdown) {
          const totalActiveBefore = nonTotalRows.reduce((sum, r) => {
            return sum + (r._beforeFleet || 0);
          }, 0);
          const totalActiveRemoved = nonTotalRows.reduce((sum, r) => {
            return sum + (r._removedVehiclesActive || 0);
          }, 0);
          totalActiveAfter = Math.max(0, totalActiveBefore - totalActiveRemoved);
        }
        return <span className="text-success font-medium">{formatNumber(totalActiveAfter, 0)}</span>;
      })(),
      change: (() => {
        // Use CSV data directly for accuracy
        if (simulationSummaryObj) {
          const before = parseFloat(getColumnValue(simulationSummaryObj, ['total_active_fleet', 'TOTAL ACTIVE FLEET'])) || 0;
          const removed = parseFloat(getColumnValue(simulationSummaryObj, ['active_vehicles_removed', 'ACTIVE VEHICLES REMOVED'])) || 0;
          const after = before - removed;
          const value = formatNumber(after - before, 0);
          return <span className="text-success font-medium">{value}</span>;
        }
        // Fall back to costData if CSV not available
        const nonTotalRows = costData.filter(r => r.vehicleType?.toUpperCase() !== 'TOTAL');
        const totalActiveBefore = nonTotalRows.reduce((sum, r) => {
          return sum + (r._beforeFleet || 0);
        }, 0);
        let totalActiveAfter = 0;
        let hasBreakdown = false;
        nonTotalRows.forEach(r => {
          if (r._afterFleetActive !== null && r._afterFleetActive !== undefined) {
            hasBreakdown = true;
            totalActiveAfter += (r._afterFleetActive || 0);
          }
        });
        if (!hasBreakdown) {
          const totalActiveRemoved = nonTotalRows.reduce((sum, r) => {
            return sum + (r._removedVehiclesActive || 0);
          }, 0);
          totalActiveAfter = Math.max(0, totalActiveBefore - totalActiveRemoved);
        }
        const value = formatNumber(totalActiveAfter - totalActiveBefore, 0);
        return <span className="text-success font-medium">{value}</span>;
      })()
    },
    {
      metric: 'Inactive Vehicles',
      before: (() => {
        // Use CSV data directly for accuracy - calculate inactive as Total - Active
        if (simulationSummaryObj) {
          const totalAvailable = parseFloat(getColumnValue(simulationSummaryObj, ['total_available_vehicles', 'TOTAL AVAILABLE VEHICLES'])) || 0;
          const activeFleet = parseFloat(getColumnValue(simulationSummaryObj, ['total_active_fleet', 'TOTAL ACTIVE FLEET'])) || 0;
          return formatNumber(totalAvailable - activeFleet, 0);
        }
        // Fall back to costData if CSV not available
        const nonTotalRows = costData.filter(r => r.vehicleType?.toUpperCase() !== 'TOTAL');
        const totalInactiveBefore = nonTotalRows.reduce((sum, r) => {
          return sum + (r._beforeFleetInactive || 0);
        }, 0);
        return formatNumber(totalInactiveBefore, 0);
      })(),
      after: (() => {
        // Use CSV data directly for accuracy
        if (simulationSummaryObj) {
          const totalAvailable = parseFloat(getColumnValue(simulationSummaryObj, ['total_available_vehicles', 'TOTAL AVAILABLE VEHICLES'])) || 0;
          const activeFleet = parseFloat(getColumnValue(simulationSummaryObj, ['total_active_fleet', 'TOTAL ACTIVE FLEET'])) || 0;
          const inactiveRemoved = parseFloat(getColumnValue(simulationSummaryObj, ['inactive_vehicles_removed', 'INACTIVE VEHICLES REMOVED'])) || 0;
          const inactiveBefore = totalAvailable - activeFleet;
          const totalInactiveAfter = inactiveBefore - inactiveRemoved;
          return <span className="text-success font-medium">{formatNumber(totalInactiveAfter, 0)}</span>;
        }
        // Fall back to costData if CSV not available
        const nonTotalRows = costData.filter(r => r.vehicleType?.toUpperCase() !== 'TOTAL');
        let totalInactiveAfter = 0;
        let hasBreakdown = false;
        nonTotalRows.forEach(r => {
          if (r._afterFleetInactive !== null && r._afterFleetInactive !== undefined) {
            hasBreakdown = true;
            totalInactiveAfter += (r._afterFleetInactive || 0);
          }
        });
        if (!hasBreakdown) {
          const totalInactiveBefore = nonTotalRows.reduce((sum, r) => {
            return sum + (r._beforeFleetInactive || 0);
          }, 0);
          const totalInactiveRemoved = nonTotalRows.reduce((sum, r) => {
            return sum + (r._removedVehiclesInactive || 0);
          }, 0);
          totalInactiveAfter = Math.max(0, totalInactiveBefore - totalInactiveRemoved);
        }
        return <span className="text-success font-medium">{formatNumber(totalInactiveAfter, 0)}</span>;
      })(),
      change: (() => {
        // Use CSV data directly for accuracy
        if (simulationSummaryObj) {
          const totalAvailable = parseFloat(getColumnValue(simulationSummaryObj, ['total_available_vehicles', 'TOTAL AVAILABLE VEHICLES'])) || 0;
          const activeFleet = parseFloat(getColumnValue(simulationSummaryObj, ['total_active_fleet', 'TOTAL ACTIVE FLEET'])) || 0;
          const inactiveRemoved = parseFloat(getColumnValue(simulationSummaryObj, ['inactive_vehicles_removed', 'INACTIVE VEHICLES REMOVED'])) || 0;
          const inactiveBefore = totalAvailable - activeFleet;
          const inactiveAfter = inactiveBefore - inactiveRemoved;
          const value = formatNumber(inactiveAfter - inactiveBefore, 0);
          return <span className="text-success font-medium">{value}</span>;
        }
        // Fall back to costData if CSV not available
        const nonTotalRows = costData.filter(r => r.vehicleType?.toUpperCase() !== 'TOTAL');
        const totalInactiveBefore = nonTotalRows.reduce((sum, r) => {
          return sum + (r._beforeFleetInactive || 0);
        }, 0);
        let totalInactiveAfter = 0;
        let hasBreakdown = false;
        nonTotalRows.forEach(r => {
          if (r._afterFleetInactive !== null && r._afterFleetInactive !== undefined) {
            hasBreakdown = true;
            totalInactiveAfter += (r._afterFleetInactive || 0);
          }
        });
        if (!hasBreakdown) {
          const totalInactiveRemoved = nonTotalRows.reduce((sum, r) => {
            return sum + (r._removedVehiclesInactive || 0);
          }, 0);
          totalInactiveAfter = Math.max(0, totalInactiveBefore - totalInactiveRemoved);
        }
        const value = formatNumber(totalInactiveAfter - totalInactiveBefore, 0);
        return <span className="text-success font-medium">{value}</span>;
      })()
    },
    {
      metric: 'Total Fleet Size',
      before: (() => {
        // Calculate from costData to ensure consistency with Vehicle Type Summary
        const nonTotalRows = costData.filter(r => r.vehicleType?.toUpperCase() !== 'TOTAL');
        const totalBefore = nonTotalRows.reduce((sum, r) => {
          return sum + (r._beforeFleet || 0) + (r._beforeFleetInactive || 0);
        }, 0);
        // Fall back to CSV if costData is empty
        if (totalBefore === 0 && simulationSummaryObj) {
          const totalAvailable = parseFloat(getColumnValue(simulationSummaryObj, ['total_available_vehicles', 'TOTAL AVAILABLE VEHICLES'])) || 0;
          return formatNumber(totalAvailable, 0);
        }
        return formatNumber(totalBefore, 0);
      })(),
      after: (() => {
        // Calculate from simulationSummaryObj (selected run) for accuracy - this changes per run
        if (simulationSummaryObj) {
          const totalAvailable = parseFloat(getColumnValue(simulationSummaryObj, ['total_available_vehicles', 'TOTAL AVAILABLE VEHICLES'])) || 0;
          const activeRemoved = parseFloat(getColumnValue(simulationSummaryObj, ['active_vehicles_removed', 'ACTIVE VEHICLES REMOVED'])) || 0;
          const inactiveRemoved = parseFloat(getColumnValue(simulationSummaryObj, ['inactive_vehicles_removed', 'INACTIVE VEHICLES REMOVED'])) || 0;
          // Total Fleet After = Total Available - Active Removed - Inactive Removed
          const totalAfter = totalAvailable - activeRemoved - inactiveRemoved;
          return <span className="text-success font-medium">{formatNumber(totalAfter, 0)}</span>;
        }
        // Fall back to costData if simulationSummaryObj not available
        const nonTotalRows = costData.filter(r => r.vehicleType?.toUpperCase() !== 'TOTAL');
        const totalAfter = nonTotalRows.reduce((sum, r) => {
          return sum + (r._afterFleet || 0);
        }, 0);
        return <span className="text-success font-medium">{formatNumber(totalAfter, 0)}</span>;
      })(),
      change: (() => {
        // Calculate from simulationSummaryObj (selected run) for accuracy - this changes per run
        if (simulationSummaryObj) {
          const totalAvailable = parseFloat(getColumnValue(simulationSummaryObj, ['total_available_vehicles', 'TOTAL AVAILABLE VEHICLES'])) || 0;
          const activeRemoved = parseFloat(getColumnValue(simulationSummaryObj, ['active_vehicles_removed', 'ACTIVE VEHICLES REMOVED'])) || 0;
          const inactiveRemoved = parseFloat(getColumnValue(simulationSummaryObj, ['inactive_vehicles_removed', 'INACTIVE VEHICLES REMOVED'])) || 0;
          // Total Fleet After = Total Available - Active Removed - Inactive Removed
          const totalAfter = totalAvailable - activeRemoved - inactiveRemoved;
          const value = formatNumber(totalAfter - totalAvailable, 0);
          return <span className="text-success font-medium">{value}</span>;
        }
        // Fall back to costData if simulationSummaryObj not available
        const nonTotalRows = costData.filter(r => r.vehicleType?.toUpperCase() !== 'TOTAL');
        const totalBefore = nonTotalRows.reduce((sum, r) => {
          return sum + (r._beforeFleet || 0) + (r._beforeFleetInactive || 0);
        }, 0);
        const totalAfter = nonTotalRows.reduce((sum, r) => {
          return sum + (r._afterFleet || 0);
        }, 0);
        const value = formatNumber(totalAfter - totalBefore, 0);
        return <span className="text-success font-medium">{value}</span>;
      })()
    },
    {
      metric: 'Avg Response Time',
      before: (() => {
        // Use exact field name from simulation_summary.csv
        const initialResponseTime = parseFloat(getColumnValue(simulationSummaryObj, [
          'initial_avg_response_time_minutes',
          'INITIAL_AVG_RESPONSE_TIME_MINUTES'
        ])) || 0;
        
        if (initialResponseTime > 0) {
          return formatNumber(initialResponseTime, 2) + ' min';
        }
        return 'N/A';
      })(),
      after: (() => {
        // Use exact field name from simulation_summary.csv
        const optimizedResponseTime = parseFloat(getColumnValue(simulationSummaryObj, [
          'optimized_avg_response_time_minutes',
          'OPTIMIZED_AVG_RESPONSE_TIME_MINUTES'
        ])) || 0;
        
        if (optimizedResponseTime > 0) {
          return <span className="text-success font-medium">{formatNumber(optimizedResponseTime, 2)} min</span>;
        }
        return <span className="text-muted-foreground">N/A</span>;
      })(),
      change: (() => {
        // Use exact field names from simulation_summary.csv
        const initialResponseTime = parseFloat(getColumnValue(simulationSummaryObj, [
          'initial_avg_response_time_minutes',
          'INITIAL_AVG_RESPONSE_TIME_MINUTES'
        ])) || 0;
        
        const optimizedResponseTime = parseFloat(getColumnValue(simulationSummaryObj, [
          'optimized_avg_response_time_minutes',
          'OPTIMIZED_AVG_RESPONSE_TIME_MINUTES'
        ])) || 0;
        
        // If we have both, calculate change
        if (initialResponseTime > 0 && optimizedResponseTime > 0) {
          const change = optimizedResponseTime - initialResponseTime;
          // Don't show change if it's essentially zero
          if (Math.abs(change) < 0.005) {
            return <span className="text-muted-foreground">—</span>;
          }
          const changeText = (change > 0 ? '+' : '') + formatNumber(change, 2) + ' min';
          // Green if improved (lower response time), red if worsened (higher response time)
          const colorClass = change <= 0 ? 'text-success' : 'text-destructive';
          return <span className={`font-medium ${colorClass}`}>{changeText}</span>;
        }
        
        // Fallback: use improvement field if available
        const improvement = parseFloat(getColumnValue(simulationSummaryObj, [
          'response_time_improvement_minutes',
          'RESPONSE_TIME_IMPROVEMENT_MINUTES'
        ])) || 0;
        
        if (improvement !== 0) {
          // Note: improvement is typically negative when response time improves (gets lower)
          // So negative improvement = better (green), positive improvement = worse (red)
          const changeText = (improvement > 0 ? '+' : '') + formatNumber(improvement, 2) + ' min';
          const colorClass = improvement <= 0 ? 'text-success' : 'text-destructive';
          return <span className={`font-medium ${colorClass}`}>{changeText}</span>;
        }
        
        return <span className="text-muted-foreground">N/A</span>;
      })()
    },
    {
      metric: 'Cost Savings',
      before: '',
      after: (() => {
        const value = formatCurrency(getColumnValue(simulationSummaryObj, ['cost_savings', 'COST SAVINGS']));
        return <span className="text-success font-medium">{value}</span>;
      })(),
      change: (() => {
        const value = formatCurrency(getColumnValue(simulationSummaryObj, ['cost_savings', 'COST SAVINGS']));
        return <span className="text-success font-medium">{value}</span>;
      })()
    }
  ];
  }, [simulationSummaryObj, costData]);
  
  // Process individual vehicle data for Cost Report by Vehicle
  const vehicleData = useMemo(() => {
    if (initialUtilizationData.length === 0) {
      return [];
    }

    // Extract vehicles that received redistributed workload from optimization log
    const vehiclesThatReceivedWorkload = new Set<string>();
    if (optimizationLogData.length > 0) {
      optimizationLogData.forEach(row => {
        const reason = getColumnValue(row, [
          'reason_for_removal',
          'REASON_FOR_REMOVAL',
          'Reason for Removal',
          'reasonForRemoval',
          'Reason_For_Removal',
          'reason',
          'Reason'
        ]);
        
        // Only consider entries where workload was redistributed
        if (reason && reason.toLowerCase().includes('workload redistributed')) {
          const receivingVehicleId = getColumnValue(row, [
            'receiving_vehicle_id',
            'RECEIVING_VEHICLE_ID',
            'Receiving Vehicle ID',
            'receivingVehicleId',
            'Receiving_Vehicle_ID',
            'receiving_vehicle',
            'Receiving Vehicle'
          ]);
          
          if (receivingVehicleId) {
            vehiclesThatReceivedWorkload.add(receivingVehicleId);
          }
        }
      });
      
      logger.info(`[Vehicle Summary] Found ${vehiclesThatReceivedWorkload.size} vehicles that received redistributed workload from optimization log`);
      if (vehiclesThatReceivedWorkload.size > 0) {
        logger.info(`[Vehicle Summary] Vehicles that received workload:`, Array.from(vehiclesThatReceivedWorkload));
      }
    }

    // Create a map of vehicle IDs from final utilization (retained vehicles)
    const retainedVehicleIds = new Set<string>();
    const finalUtilizationMap = new Map<string, CSVRow>();
    
    finalUtilizationData.forEach(row => {
      const vehicleId = getColumnValue(row, [
        'vehicle_id',
        'VEHICLE_ID',
        'Vehicle ID',
        'vehicleId',
        'Vehicle_Id',
        'VEHICLE ID'
      ]);
      if (vehicleId) {
        retainedVehicleIds.add(vehicleId);
        finalUtilizationMap.set(vehicleId, row);
      }
    });

    // Create a map of initial vehicle IDs to track which vehicles existed before
    const initialVehicleIds = new Set<string>();
    const initialUtilizationMap = new Map<string, CSVRow>();

    // Process initial utilization to create vehicle summaries
    const vehicles: Array<{
      vehicleId: string;
      station: string;
      vehicleType: string;
      initialUtilization: number;
      finalUtilization: number | null;
      status: 'Retained' | 'Removed' | 'Received Workload' | 'Replaced';
      capitalSavings: number;
    }> = [];

    initialUtilizationData.forEach(row => {
      const vehicleId = getColumnValue(row, [
        'vehicle_id',
        'VEHICLE_ID',
        'Vehicle ID',
        'vehicleId',
        'Vehicle_Id',
        'VEHICLE ID'
      ]);
      
      if (vehicleId) {
        initialVehicleIds.add(vehicleId);
        initialUtilizationMap.set(vehicleId, row);
      }
    });

    // Process initial utilization vehicles
    initialUtilizationData.forEach(row => {
      const vehicleId = getColumnValue(row, [
        'vehicle_id',
        'VEHICLE_ID',
        'Vehicle ID',
        'vehicleId',
        'Vehicle_Id',
        'VEHICLE ID'
      ]);
      
      const stationFull = getColumnValue(row, [
        'fire_station',
        'FIRE_STATION',
        'Fire Station',
        'fireStation',
        'Fire_Station',
        'station',
        'Station',
        'STATION'
      ]) || 'Unknown';
      
      // Extract only the first word from station name
      const station = stationFull.split(' ')[0] || 'Unknown';

      const vehicleType = getColumnValue(row, [
        'vehicle_type',
        'VEHICLE_TYPE',
        'Vehicle Type',
        'vehicleType',
        'Vehicle_Type',
        'type',
        'Type',
        'TYPE'
      ]) || 'Unknown';

      const initialUtilization = parseFloat(getColumnValue(row, [
        'utilization_rate',
        'UTILIZATION_RATE',
        'Utilization Rate',
        'utilizationRate',
        'Utilization_Rate',
        'utilization',
        'Utilization',
        'UTILIZATION',
        'utilization_percent',
        'Utilization Percent',
        'utilization_percentage',
        'Utilization Percentage'
      ])) || 0;

      // Check if vehicle was retained
      const isRetained = vehicleId ? retainedVehicleIds.has(vehicleId) : false;
      
      // Get final utilization if retained
      let finalUtilization: number | null = null;
      if (isRetained && vehicleId) {
        const finalRow = finalUtilizationMap.get(vehicleId);
        if (finalRow) {
          finalUtilization = parseFloat(getColumnValue(finalRow, [
            'utilization_rate',
            'UTILIZATION_RATE',
            'Utilization Rate',
            'utilizationRate',
            'Utilization_Rate',
            'utilization',
            'Utilization',
            'UTILIZATION',
            'utilization_percent',
            'Utilization Percent',
            'utilization_percentage',
            'Utilization Percentage'
          ])) || 0;
        }
      }

      // Determine status: first check if status is available from backend CSV
      let status: 'Retained' | 'Removed' | 'Received Workload' | 'Replaced';
      const finalRow = finalUtilizationMap.get(vehicleId || '');
      const backendStatus = finalRow ? getColumnValue(finalRow, ['status', 'Status', 'STATUS']) : null;
      
      if (backendStatus && ['Retained', 'Removed', 'Received Workload', 'Replaced'].includes(backendStatus)) {
        // Use status from backend CSV if available
        status = backendStatus as 'Retained' | 'Removed' | 'Received Workload' | 'Replaced';
      } else if (!isRetained) {
        // Vehicle was removed
        status = 'Removed';
      } else if (vehicleId && vehiclesThatReceivedWorkload.has(vehicleId)) {
        // Vehicle is in the optimization log as receiving redistributed workload
        // The optimization log is the ground truth - trust it even if utilization comparison seems inconsistent
        // Note: Now that we're using run-specific initial and final utilization from the same run,
        // the comparison should be accurate. Any inconsistencies would be due to the optimization algorithm's decisions.
        status = 'Received Workload';
      } else if (initialUtilization > 0 && finalUtilization !== null && finalUtilization === 0) {
        // Vehicle was active initially (utilization > 0) but now has 0% final utilization
        // This means its workload was completely redistributed to other vehicles (replaced)
        status = 'Replaced';
      } else {
        // Vehicle was retained but didn't receive redistributed workload (or wasn't in log)
        // Note: A vehicle going from 0% to > 0% utilization doesn't necessarily mean it received
        // redistributed workload - it could be due to natural demand changes, operational changes, etc.
        // We only mark as "Received Workload" if explicitly tracked in the optimization log.
        status = 'Retained';
      }

      // Calculate capital savings if removed
      // Get unit cost for this vehicle type from costData
      const vehicleTypeRow = costData.find(r => 
        r.vehicleType?.toLowerCase() === vehicleType.toLowerCase()
      );
      const unitCost = vehicleTypeRow?._originalUnitCost || 0;
      const capitalSavings = status === 'Removed' ? unitCost : 0;

      vehicles.push({
        vehicleId: redactVehicleId(vehicleId || 'Unknown', isDemoMode),
        station: redactStationName(station, isDemoMode),
        vehicleType: redactVehicleType(vehicleType, isDemoMode),
        initialUtilization,
        finalUtilization,
        status,
        capitalSavings: capitalSavings // Keep as number, format in display
      });
    });

    // Process vehicles from final utilization that weren't in initial (received redistributed workload)
    let receivedWorkloadCount = 0;
    finalUtilizationData.forEach(row => {
      const vehicleId = getColumnValue(row, [
        'vehicle_id',
        'VEHICLE_ID',
        'Vehicle ID',
        'vehicleId',
        'Vehicle_Id',
        'VEHICLE ID'
      ]);
      
      // Skip if this vehicle was already processed (was in initial)
      if (!vehicleId || initialVehicleIds.has(vehicleId)) {
        return;
      }
      
      receivedWorkloadCount++;
      logger.info(`[Vehicle Summary] Found vehicle that received workload: ${vehicleId} (not in initial utilization)`);
      
      // This is a vehicle that received redistributed workload
      const stationFull = getColumnValue(row, [
        'fire_station',
        'FIRE_STATION',
        'Fire Station',
        'fireStation',
        'Fire_Station',
        'station',
        'Station',
        'STATION'
      ]) || 'Unknown';
      
      // Extract only the first word from station name
      const station = stationFull.split(' ')[0] || 'Unknown';

      const vehicleType = getColumnValue(row, [
        'vehicle_type',
        'VEHICLE_TYPE',
        'Vehicle Type',
        'vehicleType',
        'Vehicle_Type',
        'type',
        'Type',
        'TYPE'
      ]) || 'Unknown';

      const finalUtilization = parseFloat(getColumnValue(row, [
        'utilization_rate',
        'UTILIZATION_RATE',
        'Utilization Rate',
        'utilizationRate',
        'Utilization_Rate',
        'utilization',
        'Utilization',
        'UTILIZATION',
        'utilization_percent',
        'Utilization Percent',
        'utilization_percentage',
        'Utilization Percentage'
      ])) || 0;

      // These vehicles weren't in initial fleet - check if they received redistributed workload
      // Only mark as "Received Workload" if explicitly tracked in the optimization log
      // Note: A vehicle appearing in final but not initial with utilization > 0 doesn't necessarily
      // mean it received redistributed workload - it could be due to other operational changes.
      // The optimization log is the authoritative source for tracking workload redistribution.
      const receivedWorkload = vehiclesThatReceivedWorkload.has(vehicleId);
      
      vehicles.push({
        vehicleId,
        station,
        vehicleType,
        initialUtilization: 0, // Wasn't active initially
        finalUtilization,
        status: receivedWorkload ? 'Received Workload' : 'Retained', // Only mark as "Received Workload" if in optimization log
        capitalSavings: 0 // No capital savings, vehicle wasn't removed
      });
    });

    logger.info(`[Vehicle Summary] Total vehicles processed: ${vehicles.length}`);
    logger.info(`[Vehicle Summary] Vehicles that received workload: ${receivedWorkloadCount}`);
    logger.info(`[Vehicle Summary] Initial vehicles: ${initialVehicleIds.size}, Final vehicles: ${retainedVehicleIds.size}`);

    // Sort by status (Removed first, then Replaced, then Received Workload, then Retained), then by vehicle type, then by station
    vehicles.sort((a, b) => {
      const statusOrder = { 'Removed': 0, 'Replaced': 1, 'Received Workload': 2, 'Retained': 3 };
      const aOrder = statusOrder[a.status] ?? 4;
      const bOrder = statusOrder[b.status] ?? 4;
      
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      if (a.vehicleType !== b.vehicleType) {
        return a.vehicleType.localeCompare(b.vehicleType);
      }
      return a.station.localeCompare(b.station);
    });

    return vehicles;
  }, [initialUtilizationData, finalUtilizationData, optimizationLogData, costData]);

  // Initialize unit costs from data if not already set
  useEffect(() => {
    if (costData.length > 0) {
      const initialCosts: Record<string, number> = {};
      costData.forEach(row => {
        if (row.vehicleType && row._originalUnitCost) {
          // Only set if not already in unitCosts
          if (!unitCosts[row.vehicleType]) {
            initialCosts[row.vehicleType] = row._originalUnitCost;
          }
        }
      });
      if (Object.keys(initialCosts).length > 0) {
        setUnitCosts(prev => ({ ...prev, ...initialCosts }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costData.length]);
  
  // Handle unit cost edit
  const handleUnitCostEdit = (vehicleType: string, newValue: string) => {
    const numValue = parseFloat(newValue.replace(/[£,\s]/g, '')) || 0;
    setUnitCosts(prev => ({ ...prev, [vehicleType]: numValue }));
    setEditingUnitCost(null);
  };
  
  // Handle weight percentage edit
  const handleWeightEdit = (weight: 'geographicOverlap' | 'temporalCompatibility' | 'activitySimilarity' | 'redistributionImpact', newValue: string) => {
    const numValue = Math.max(0, Math.min(100, parseInt(newValue.replace(/[%\s]/g, '')) || 0));
    
    if (weight === 'geographicOverlap') {
      const total = weights.temporalCompatibility + weights.activitySimilarity + weights.redistributionImpact;
      const remaining = 100 - numValue;
      if (remaining >= 0 && total > 0) {
        const scale = remaining / total;
        setWeights({
          geographicOverlap: numValue,
          temporalCompatibility: Math.round(weights.temporalCompatibility * scale),
          activitySimilarity: Math.round(weights.activitySimilarity * scale),
          redistributionImpact: remaining - Math.round(weights.temporalCompatibility * scale) - Math.round(weights.activitySimilarity * scale),
        });
      } else {
        setWeights({ ...weights, geographicOverlap: numValue });
      }
    } else if (weight === 'temporalCompatibility') {
      const total = weights.geographicOverlap + weights.activitySimilarity + weights.redistributionImpact;
      const remaining = 100 - numValue;
      if (remaining >= 0 && total > 0) {
        const scale = remaining / total;
        setWeights({
          temporalCompatibility: numValue,
          geographicOverlap: Math.round(weights.geographicOverlap * scale),
          activitySimilarity: Math.round(weights.activitySimilarity * scale),
          redistributionImpact: remaining - Math.round(weights.geographicOverlap * scale) - Math.round(weights.activitySimilarity * scale),
        });
      } else {
        setWeights({ ...weights, temporalCompatibility: numValue });
      }
    } else if (weight === 'activitySimilarity') {
      const total = weights.geographicOverlap + weights.temporalCompatibility + weights.redistributionImpact;
      const remaining = 100 - numValue;
      if (remaining >= 0 && total > 0) {
        const scale = remaining / total;
        setWeights({
          activitySimilarity: numValue,
          geographicOverlap: Math.round(weights.geographicOverlap * scale),
          temporalCompatibility: Math.round(weights.temporalCompatibility * scale),
          redistributionImpact: remaining - Math.round(weights.geographicOverlap * scale) - Math.round(weights.temporalCompatibility * scale),
        });
      } else {
        setWeights({ ...weights, activitySimilarity: numValue });
      }
    } else if (weight === 'redistributionImpact') {
      const total = weights.geographicOverlap + weights.temporalCompatibility + weights.activitySimilarity;
      const remaining = 100 - numValue;
      if (remaining >= 0 && total > 0) {
        const scale = remaining / total;
        setWeights({
          redistributionImpact: numValue,
          geographicOverlap: Math.round(weights.geographicOverlap * scale),
          temporalCompatibility: Math.round(weights.temporalCompatibility * scale),
          activitySimilarity: remaining - Math.round(weights.geographicOverlap * scale) - Math.round(weights.temporalCompatibility * scale),
        });
      } else {
        setWeights({ ...weights, redistributionImpact: numValue });
      }
    }
    setEditingWeight(null);
  };
  
  // Recalculate cost data with editable unit costs
  const costDataWithEditable = costData.map(row => {
    const isTotalRow = row.vehicleType?.toUpperCase() === 'TOTAL';
    
    // For TOTAL row, calculate totals from other rows
    if (isTotalRow) {
      const nonTotalRows = costData.filter(r => r.vehicleType?.toUpperCase() !== 'TOTAL');
      const totalCapitalSavings = nonTotalRows.reduce((sum, r) => {
        const currentUnitCost = unitCosts[r.vehicleType] ?? r._originalUnitCost;
        const removed = r._removedVehiclesTotal || r._removedVehicles; // Use total removed for capital savings
        return sum + (removed * currentUnitCost);
      }, 0);
      const totalUnitCost = nonTotalRows.reduce((sum, r) => {
        const currentUnitCost = unitCosts[r.vehicleType] ?? r._originalUnitCost;
        return sum + currentUnitCost;
      }, 0);
      // Calculate total active vehicles before optimization (use numeric value, not formatted string)
      const totalActiveBefore = nonTotalRows.reduce((sum, r) => {
        return sum + (r._beforeFleet || 0);
      }, 0);
      // Calculate total inactive vehicles before optimization
      const totalInactiveBefore = nonTotalRows.reduce((sum, r) => {
        return sum + (r._beforeFleetInactive || 0);
      }, 0);
      // Calculate total active and inactive vehicles removed
      const totalActiveRemoved = nonTotalRows.reduce((sum, r) => {
        return sum + (r._removedVehiclesActive || 0);
      }, 0);
      const totalInactiveRemoved = nonTotalRows.reduce((sum, r) => {
        return sum + (r._removedVehiclesInactive || 0);
      }, 0);
      const totalRemoved = totalActiveRemoved + totalInactiveRemoved;
      
      // Calculate total active and inactive vehicles after optimization
      // Always use _afterFleet for each row (which is calculated as Initial - Removed)
      // This ensures consistency with the Simulation Summary calculation
      const totalAfter = nonTotalRows.reduce((sum, r) => {
        return sum + (r._afterFleet || 0);
      }, 0);
      
      // Calculate active/inactive breakdown for display if we have breakdown data
      let totalActiveAfter = 0;
      let totalInactiveAfter = 0;
      let hasAnyBreakdown = false;
      
      nonTotalRows.forEach(r => {
        // Check if this row has breakdown data
        const hasBreakdown = (r._afterFleetActive !== null && r._afterFleetActive !== undefined) &&
                            (r._afterFleetInactive !== null && r._afterFleetInactive !== undefined);
        
        if (hasBreakdown) {
          hasAnyBreakdown = true;
          totalActiveAfter += (r._afterFleetActive || 0);
          totalInactiveAfter += (r._afterFleetInactive || 0);
        }
      });
      
      // If we don't have breakdown data for all rows, calculate from totals
      // Total After = Total Before - Total Removed
      if (!hasAnyBreakdown) {
        totalActiveAfter = Math.max(0, totalActiveBefore - totalActiveRemoved);
        totalInactiveAfter = Math.max(0, totalInactiveBefore - totalInactiveRemoved);
      }
      
      logger.info(`📊 TOTAL ROW: Total Active Before = ${totalActiveBefore}, Total Active Removed = ${totalActiveRemoved}, Total Inactive Removed = ${totalInactiveRemoved}`);
      logger.info(`📊 TOTAL ROW: Total Active After = ${totalActiveAfter}, Total Inactive After = ${totalInactiveAfter}, Total After = ${totalAfter}`);
      logger.info(`📊 Individual row _beforeFleet values:`, nonTotalRows.map(r => ({ type: r.vehicleType, count: r._beforeFleet })));
      
      return {
        ...row,
        unitCost: totalUnitCost > 0 ? totalUnitCost.toLocaleString('en-GB') : '-',
        beforeFleet: formatNumber(totalActiveBefore, 0), // Use sum of active vehicles
        beforeFleetInactive: formatNumber(totalInactiveBefore, 0), // Use sum of inactive vehicles
        afterFleet: totalInactiveAfter > 0 || totalActiveAfter === 0 ? (
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-success font-medium">{formatNumber(totalAfter, 0)}</span>
            <span className="text-xs text-muted-foreground">
              ({formatNumber(totalActiveAfter, 0)} Active + {formatNumber(totalInactiveAfter, 0)} Inactive)
            </span>
          </div>
        ) : (
          <span className="text-success font-medium">{formatNumber(totalAfter, 0)}</span>
        ),
        capitalSavings: <span className="text-success font-semibold">{totalCapitalSavings.toLocaleString('en-GB')}</span>,
        removedVehicles: totalInactiveRemoved > 0 ? (
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-success font-medium">{formatNumber(totalRemoved, 0)}</span>
            <span className="text-xs text-muted-foreground">
              ({formatNumber(totalActiveRemoved, 0)} Active + {formatNumber(totalInactiveRemoved, 0)} Inactive)
            </span>
          </div>
        ) : (
          <span className="text-success font-medium">{formatNumber(totalRemoved, 0)}</span>
        ),
        _isStickyRow: true  // Flag to make this row sticky at bottom
      };
    }
    
    // For regular rows
    const currentUnitCost = unitCosts[row.vehicleType] ?? row._originalUnitCost;
    const removed = row._removedVehiclesTotal || row._removedVehicles; // Use total removed for capital savings
    const recalculatedSavings = removed * currentUnitCost;
    
    const isEditing = editingUnitCost?.vehicleType === row.vehicleType;
    
    return {
      ...row,
      unitCost: isEditing ? (
        <input
          type="number"
          step="1000"
          defaultValue={currentUnitCost}
          onBlur={(e) => handleUnitCostEdit(row.vehicleType, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleUnitCostEdit(row.vehicleType, (e.target as HTMLInputElement).value);
            } else if (e.key === 'Escape') {
              setEditingUnitCost(null);
            }
          }}
          onClick={(e) => e.stopPropagation()}
          className="w-24 px-2 py-1 bg-secondary text-white rounded border border-success focus:outline-none focus:ring-2 focus:ring-success box-border text-right"
          style={{ boxSizing: 'border-box' }}
          autoFocus
        />
      ) : (
        <span 
          className="inline-block w-24 px-2 py-1 border border-transparent box-border cursor-pointer text-right hover:text-success transition-all"
          style={{ boxSizing: 'border-box' }}
          onClick={() => setEditingUnitCost({ vehicleType: row.vehicleType })}
        >
          {currentUnitCost.toLocaleString('en-GB')}
        </span>
      ),
      afterFleet: (() => {
        // Always use _afterFleet (calculated as Initial - Removed) to reflect real fleet size
        // This ensures consistency with Simulation Summary
        const totalAfterFleet = row._afterFleet || 0;
        
        // Show breakdown if we have utilization data and can calculate active/inactive
        // Check if the numeric values exist (not null/undefined) - 0 is a valid value
        const hasAfterBreakdown = (row._afterFleetActive !== null && row._afterFleetActive !== undefined) && 
                                  (row._afterFleetInactive !== null && row._afterFleetInactive !== undefined);
        
        if (hasAfterBreakdown) {
          const active = row._afterFleetActive || 0;
          const inactive = row._afterFleetInactive || 0;
          
          // Always show breakdown if we have the data, to be clear about composition
          // Use totalAfterFleet (calculated value) to ensure it matches the real fleet size
          return (
            <div className="flex flex-col items-center gap-0.5">
              <span className="text-success font-medium">{formatNumber(totalAfterFleet, 0)}</span>
              <span className="text-xs text-muted-foreground">
                ({formatNumber(active, 0)} Active + {formatNumber(inactive, 0)} Inactive)
              </span>
            </div>
          );
        }
        // Fallback to simple display when breakdown data not available
        // Use totalAfterFleet (calculated value) to ensure it matches the real fleet size
        return <span className="text-success font-medium">{formatNumber(totalAfterFleet, 0)}</span>;
      })(),
      capitalSavings: <span className="text-success font-semibold">{recalculatedSavings.toLocaleString('en-GB')}</span>,
      removedVehicles: (() => {
        // Show breakdown if available
        if (row.removedVehiclesActive !== null && row.removedVehiclesInactive !== null) {
          const active = row._removedVehiclesActive || 0;
          const inactive = row._removedVehiclesInactive || 0;
          const total = row._removedVehiclesTotal || 0;
          
          if (inactive > 0) {
            // Show breakdown: Total (Active + Inactive)
            return (
              <div className="flex flex-col items-center gap-0.5">
                <span className="text-success font-medium">{formatNumber(total, 0)}</span>
                <span className="text-xs text-muted-foreground">
                  ({formatNumber(active, 0)} Active + {formatNumber(inactive, 0)} Inactive)
                </span>
              </div>
            );
          } else {
            // Only active removed, no breakdown needed
            return <span className="text-success font-medium">{formatNumber(total, 0)}</span>;
          }
        }
        // Fallback to simple display
        return <span className="text-success font-medium">{row.removedVehicles}</span>;
      })()
    };
  });

  // ✅ Show message only if no period is selected (meaning no data available from backend)
  // Allow data fetching to proceed even if isDataReady is false - backend may have data
  if (!selectedPeriod) {
    return (
      <div className="space-y-4">
        <div className="bg-card border border-border rounded-lg p-6 text-center">
          <p className="text-muted-foreground">Please select a period to view optimization data.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: white;
          border: 2px solid #fbbf24;
          cursor: pointer;
          box-shadow: 0 0 4px rgba(251, 191, 36, 0.6), 0 2px 4px rgba(0, 0, 0, 0.3);
        }
        input[type="range"]::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: white;
          border: 2px solid #fbbf24;
          cursor: pointer;
          box-shadow: 0 0 4px rgba(251, 191, 36, 0.6), 0 2px 4px rgba(0, 0, 0, 0.3);
        }
        input[type="range"]::-webkit-slider-thumb:hover {
          background: #fef3c7;
          box-shadow: 0 0 8px rgba(251, 191, 36, 0.8), 0 2px 6px rgba(0, 0, 0, 0.4);
        }
        input[type="range"]::-moz-range-thumb:hover {
          background: #fef3c7;
          box-shadow: 0 0 8px rgba(251, 191, 36, 0.8), 0 2px 6px rgba(0, 0, 0, 0.4);
        }
      `}</style>
      <div className="space-y-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Optimisation</h2>
        <p className="text-muted-foreground text-sm">
          Find ways to move vehicles between locations. Helps improve efficiency and reduce costs.
        </p>
      </div>
      {/* KPI Cards - Only available for Emergency Services */}
      {serviceType === 'emergency_services' && (
      <section>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <FleetStyleKpiCard
            title="Capital Cost Savings"
            value={formatCurrency(totalSavings)}
            subtitle={`${formatNumber(totalRemoved, 0)} vehicles removed`}
            accent="green"
          />
          <FleetStyleKpiCard
            title="Fuel Reduction"
            value={`${fuelReduction >= 0 ? '+' : '-'}${formatNumber(Math.abs(fuelReduction), 1)}%`}
            subtitle={fuelConsumed > 0 && initialFuel > 0
              ? `${formatNumber(fuelConsumed, 0)}L (from ${formatNumber(initialFuel, 0)}L)`
              : fuelConsumed > 0
                ? `${formatNumber(fuelConsumed, 0)}L consumed`
                : 'No data'}
            accent="amber"
            trend={fuelReduction > 0 ? { value: Math.min(fuelReduction, 100), isPositive: true } : undefined}
          />
          <FleetStyleKpiCard
            title="Fleet Reduction"
            value={`${formatNumber(
              (() => {
                if (!simulationSummaryObj || totalRemoved === 0) return 0;
                const beforeActive = parseFloat(getColumnValue(simulationSummaryObj, ['total_active_fleet', 'TOTAL ACTIVE FLEET'])) || 0;
                const beforeInactive = parseFloat(getColumnValue(simulationSummaryObj, ['total_available_vehicles', 'TOTAL AVAILABLE VEHICLES'])) || 0;
                const totalBefore = beforeActive + beforeInactive;
                return totalBefore > 0 ? (totalRemoved / totalBefore) * 100 : 0;
              })(),
              1
            )}%`}
            subtitle={`${formatNumber(totalRemoved, 0)} vehicles removed`}
            accent="blue"
          />
          <FleetStyleKpiCard
            title="Response Time"
            value={responseTimeImprovement > 0
              ? `-${formatNumber(responseTimeImprovement, 1)}%`
              : responseTimeImprovement < 0
                ? `+${formatNumber(Math.abs(responseTimeImprovement), 1)}%`
                : '0%'}
            subtitle={initialResponseTime > 0 && optimizedResponseTime > 0
              ? `${formatNumber(optimizedResponseTime, 2)} min (from ${formatNumber(initialResponseTime, 2)} min)`
              : 'No data'}
            accent="purple"
            trend={responseTimeImprovement > 0 ? { value: Math.min(responseTimeImprovement, 100), isPositive: true } : undefined}
          />
        </div>
      </section>
      )}

      <section className="bg-card/50 rounded-lg p-6 border border-border">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">Vehicle Redistribution</h2>
            <p className="text-muted-foreground text-sm">
              Find vehicles that can be swapped between locations. Helps reduce downtime and improve how vehicles are used.
            </p>
          </div>
          <button
            onClick={() => {
              const data = getInterchangeabilityData();
              const columns = [
                { key: 'vehiclePair', label: 'Vehicle Pair' },
                { key: 'vehicleTypePair', label: 'Type Pair' },
                { key: 'geoOverlap', label: 'Location Overlap (%)' },
                { key: 'temporalCompat', label: 'Schedule Match (%)' },
                { key: 'activitySim', label: 'Activity Match (%)' },
                { key: 'redistributionImpact', label: 'Relocation Impact (%)' },
                { key: 'overallScore', label: 'Overall Score (%)' },
                { key: 'feasibility', label: 'Feasibility' },
              ];
              const headers = columns.map(col => col.label);
              const csv = [
                headers.join(','),
                ...data.map(row => {
                  const vehiclePair = (row as any)._vehiclePair || '';
                  const vehicleTypePair = (row as any)._vehicleTypePair || '';
                  return [
                    vehiclePair,
                    vehicleTypePair,
                    row.geoOverlap,
                    row.temporalCompat,
                    row.activitySim,
                    row.redistributionImpact,
                    row.overallScore,
                    row.feasibility
                  ].map(val => String(val || '').replace(/,/g, ';')).join(',');
                })
              ].join('\n');
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `vehicle_interchangeability_${selectedInterchangeabilityVehicle || 'all'}.csv`;
              link.click();
              window.URL.revokeObjectURL(url);
            }}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground bg-secondary rounded-md hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
          >
            <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-150 transition-all duration-300" />
            <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
          </button>
        </div>
        <div className="bg-card rounded-lg shadow-sm overflow-hidden border border-border w-full max-w-full hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all group">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Vehicle Match Analysis</h3>
            <select
              value={selectedInterchangeabilityVehicle || ''}
              onChange={(e) => setSelectedInterchangeabilityVehicle(e.target.value || null)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border cursor-pointer ${
                selectedInterchangeabilityVehicle
                  ? 'bg-white text-black border-warning shadow-lg shadow-warning/50'
                  : 'bg-secondary text-foreground border-border hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50'
              } focus:outline-none focus:ring-2 focus:ring-warning focus:border-warning`}
              style={{
                appearance: 'none',
                backgroundImage: selectedInterchangeabilityVehicle 
                  ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23000' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`
                  : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                paddingRight: '2.5rem'
              }}
            >
              <option value="" className="bg-card text-foreground">All Vehicles</option>
              {getInterchangeabilityVehicleIds().map(vehicleId => (
                <option key={vehicleId} value={vehicleId} className="bg-card text-foreground">
                  {redactVehicleId(vehicleId, isDemoMode)}
                </option>
              ))}
            </select>
          </div>
          <DataTable
            title=""
            columns={[
              { key: 'vehiclePair', label: 'Vehicle Pair' },
              { key: 'vehicleTypePair', label: 'Type Pair' },
              { key: 'geoOverlap', label: <LocationOverlapLabel />, align: 'right' as const },
              { key: 'temporalCompat', label: <ScheduleMatchLabel />, align: 'right' as const },
              { key: 'activitySim', label: <ActivityMatchLabel />, align: 'right' as const },
              { key: 'redistributionImpact', label: <RelocationImpactLabel />, align: 'right' as const },
              { key: 'overallScore', label: <OverallScoreLabel />, align: 'right' as const },
              { key: 'feasibility', label: 'Feasibility', align: 'center' as const },
            ]}
            data={getInterchangeabilityData()}
            hideExport
            defaultSort={{ column: 'overallScore', direction: 'desc' }}
            customColumnWidths={{ 
              vehiclePair: serviceType === 'logistics' 
                ? { minWidth: 6, maxWidth: 10 } 
                : { minWidth: 4, maxWidth: 5 },
              vehicleTypePair: { minWidth: 6, maxWidth: 7 },
              geoOverlap: { minWidth: 5, maxWidth: 6 },
              temporalCompat: { minWidth: 5, maxWidth: 6 },
              activitySim: { minWidth: 5, maxWidth: 6 },
              redistributionImpact: { minWidth: 5, maxWidth: 6 },
              overallScore: { minWidth: 5, maxWidth: 7 },
              feasibility: { minWidth: 4, maxWidth: 6 }
            }}
          />
        </div>

        <div className="mt-6">
          <h3 className="text-lg font-semibold text-white mb-4">Score Weighting Customisation</h3>
          <div className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-muted-foreground">
                Adjust the relative importance of each factor in the score calculation. Higher values mean that factor is prioritised more.
              </p>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-medium ${
                  (weights.geographicOverlap + weights.temporalCompatibility + weights.activitySimilarity + weights.redistributionImpact) === 100
                    ? 'text-success'
                    : 'text-warning'
                }`}>
                  Total: {weights.geographicOverlap + weights.temporalCompatibility + weights.activitySimilarity + weights.redistributionImpact}%
                </span>
                <button
                  onClick={() => setWeights({ ...defaultWeights })}
                  className="px-3 py-1.5 text-xs font-medium text-foreground bg-secondary rounded-md hover:bg-accent hover:border-warning hover:text-warning hover:shadow-lg hover:shadow-warning/50 transition-all border border-border"
                >
                  Reset to Default
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Location Overlap */}
              <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all">
                <div 
                  className={`text-xs font-medium mb-3 transition-all ${
                    hoveredMetric === 'Location Overlap' 
                      ? 'text-success drop-shadow-glow-success-lg' 
                      : 'text-foreground'
                  }`}
                  onMouseEnter={() => setHoveredMetric('Location Overlap')}
                  onMouseLeave={() => setHoveredMetric(null)}
                >
                  Location Overlap
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={weights.geographicOverlap}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value);
                      const total = weights.temporalCompatibility + weights.activitySimilarity + weights.redistributionImpact;
                      const remaining = 100 - newValue;
                      if (remaining >= 0 && total > 0) {
                        // Scale other values proportionally
                        const scale = remaining / total;
                        setWeights({
                          geographicOverlap: newValue,
                          temporalCompatibility: Math.round(weights.temporalCompatibility * scale),
                          activitySimilarity: Math.round(weights.activitySimilarity * scale),
                          redistributionImpact: remaining - Math.round(weights.temporalCompatibility * scale) - Math.round(weights.activitySimilarity * scale),
                        });
                      } else {
                        setWeights({
                          ...weights,
                          geographicOverlap: newValue,
                        });
                      }
                    }}
                    onMouseEnter={() => setHoveredMetric('Location Overlap')}
                    onMouseLeave={() => setHoveredMetric(null)}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, 
                        rgb(239, 68, 68) 0%, 
                        rgb(250, 204, 21) ${Math.min(50, weights.geographicOverlap)}%, 
                        ${weights.geographicOverlap > 50 ? `rgb(34, 197, 94) ${weights.geographicOverlap}%,` : ''}
                        rgb(75, 85, 99) ${weights.geographicOverlap}%,
                        rgb(75, 85, 99) 100%)`
                    }}
                  />
                  <div className="w-10 text-right">
                    {editingWeight === 'geographicOverlap' ? (
                      <input
                        type="text"
                        defaultValue={weights.geographicOverlap}
                        onBlur={(e) => handleWeightEdit('geographicOverlap', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleWeightEdit('geographicOverlap', (e.target as HTMLInputElement).value);
                          } else if (e.key === 'Escape') {
                            setEditingWeight(null);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-1 py-0 bg-secondary text-white text-xs rounded border border-success focus:outline-none focus:ring-2 focus:ring-success text-right box-border"
                        style={{ minHeight: '1.25rem', lineHeight: '1.25rem' }}
                        autoFocus
                      />
                    ) : (
                      <span 
                        className="text-xs font-medium text-white cursor-pointer hover:text-success transition-all inline-block"
                        style={{ minHeight: '1.25rem', lineHeight: '1.25rem' }}
                        onDoubleClick={() => setEditingWeight('geographicOverlap')}
                      >
                        {weights.geographicOverlap}%
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Schedule Match */}
              <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all">
                <div 
                  className={`text-xs font-medium mb-3 transition-all ${
                    hoveredMetric === 'Schedule Match' 
                      ? 'text-success drop-shadow-glow-success-lg' 
                      : 'text-foreground'
                  }`}
                  onMouseEnter={() => setHoveredMetric('Schedule Match')}
                  onMouseLeave={() => setHoveredMetric(null)}
                >
                  Schedule Match
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={weights.temporalCompatibility}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value);
                      const total = weights.geographicOverlap + weights.activitySimilarity + weights.redistributionImpact;
                      const remaining = 100 - newValue;
                      if (remaining >= 0 && total > 0) {
                        const scale = remaining / total;
                        setWeights({
                          temporalCompatibility: newValue,
                          geographicOverlap: Math.round(weights.geographicOverlap * scale),
                          activitySimilarity: Math.round(weights.activitySimilarity * scale),
                          redistributionImpact: remaining - Math.round(weights.geographicOverlap * scale) - Math.round(weights.activitySimilarity * scale),
                        });
                      } else {
                        setWeights({
                          ...weights,
                          temporalCompatibility: newValue,
                        });
                      }
                    }}
                    onMouseEnter={() => setHoveredMetric('Schedule Match')}
                    onMouseLeave={() => setHoveredMetric(null)}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, 
                        rgb(239, 68, 68) 0%, 
                        rgb(250, 204, 21) ${Math.min(50, weights.temporalCompatibility)}%, 
                        ${weights.temporalCompatibility > 50 ? `rgb(34, 197, 94) ${weights.temporalCompatibility}%,` : ''}
                        rgb(75, 85, 99) ${weights.temporalCompatibility}%,
                        rgb(75, 85, 99) 100%)`
                    }}
                  />
                  <div className="w-10 text-right">
                    {editingWeight === 'temporalCompatibility' ? (
                      <input
                        type="text"
                        defaultValue={weights.temporalCompatibility}
                        onBlur={(e) => handleWeightEdit('temporalCompatibility', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleWeightEdit('temporalCompatibility', (e.target as HTMLInputElement).value);
                          } else if (e.key === 'Escape') {
                            setEditingWeight(null);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-1 py-0 bg-secondary text-white text-xs rounded border border-success focus:outline-none focus:ring-2 focus:ring-success text-right box-border"
                        style={{ minHeight: '1.25rem', lineHeight: '1.25rem' }}
                        autoFocus
                      />
                    ) : (
                      <span 
                        className="text-xs font-medium text-white cursor-pointer hover:text-success transition-all inline-block"
                        style={{ minHeight: '1.25rem', lineHeight: '1.25rem' }}
                        onDoubleClick={() => setEditingWeight('temporalCompatibility')}
                      >
                        {weights.temporalCompatibility}%
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Activity Match */}
              <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all">
                <div 
                  className={`text-xs font-medium mb-3 transition-all ${
                    hoveredMetric === 'Activity Match' 
                      ? 'text-success drop-shadow-glow-success-lg' 
                      : 'text-foreground'
                  }`}
                  onMouseEnter={() => setHoveredMetric('Activity Match')}
                  onMouseLeave={() => setHoveredMetric(null)}
                >
                  Activity Match
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={weights.activitySimilarity}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value);
                      const total = weights.geographicOverlap + weights.temporalCompatibility + weights.redistributionImpact;
                      const remaining = 100 - newValue;
                      if (remaining >= 0 && total > 0) {
                        const scale = remaining / total;
                        setWeights({
                          activitySimilarity: newValue,
                          geographicOverlap: Math.round(weights.geographicOverlap * scale),
                          temporalCompatibility: Math.round(weights.temporalCompatibility * scale),
                          redistributionImpact: remaining - Math.round(weights.geographicOverlap * scale) - Math.round(weights.temporalCompatibility * scale),
                        });
                      } else {
                        setWeights({
                          ...weights,
                          activitySimilarity: newValue,
                        });
                      }
                    }}
                    onMouseEnter={() => setHoveredMetric('Activity Match')}
                    onMouseLeave={() => setHoveredMetric(null)}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, 
                        rgb(239, 68, 68) 0%, 
                        rgb(250, 204, 21) ${Math.min(50, weights.activitySimilarity)}%, 
                        ${weights.activitySimilarity > 50 ? `rgb(34, 197, 94) ${weights.activitySimilarity}%,` : ''}
                        rgb(75, 85, 99) ${weights.activitySimilarity}%,
                        rgb(75, 85, 99) 100%)`
                    }}
                  />
                  <div className="w-10 text-right">
                    {editingWeight === 'activitySimilarity' ? (
                      <input
                        type="text"
                        defaultValue={weights.activitySimilarity}
                        onBlur={(e) => handleWeightEdit('activitySimilarity', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleWeightEdit('activitySimilarity', (e.target as HTMLInputElement).value);
                          } else if (e.key === 'Escape') {
                            setEditingWeight(null);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-1 py-0 bg-secondary text-white text-xs rounded border border-success focus:outline-none focus:ring-2 focus:ring-success text-right box-border"
                        style={{ minHeight: '1.25rem', lineHeight: '1.25rem' }}
                        autoFocus
                      />
                    ) : (
                      <span 
                        className="text-xs font-medium text-white cursor-pointer hover:text-success transition-all inline-block"
                        style={{ minHeight: '1.25rem', lineHeight: '1.25rem' }}
                        onDoubleClick={() => setEditingWeight('activitySimilarity')}
                      >
                        {weights.activitySimilarity}%
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Relocation Impact */}
              <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all">
                <div 
                  className={`text-xs font-medium mb-3 transition-all ${
                    hoveredMetric === 'Relocation Impact' 
                      ? 'text-success drop-shadow-glow-success-lg' 
                      : 'text-foreground'
                  }`}
                  onMouseEnter={() => setHoveredMetric('Relocation Impact')}
                  onMouseLeave={() => setHoveredMetric(null)}
                >
                  Relocation Impact
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={weights.redistributionImpact}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value);
                      const total = weights.geographicOverlap + weights.temporalCompatibility + weights.activitySimilarity;
                      const remaining = 100 - newValue;
                      if (remaining >= 0 && total > 0) {
                        const scale = remaining / total;
                        setWeights({
                          redistributionImpact: newValue,
                          geographicOverlap: Math.round(weights.geographicOverlap * scale),
                          temporalCompatibility: Math.round(weights.temporalCompatibility * scale),
                          activitySimilarity: remaining - Math.round(weights.geographicOverlap * scale) - Math.round(weights.temporalCompatibility * scale),
                        });
                      } else {
                        setWeights({
                          ...weights,
                          redistributionImpact: newValue,
                        });
                      }
                    }}
                    onMouseEnter={() => setHoveredMetric('Relocation Impact')}
                    onMouseLeave={() => setHoveredMetric(null)}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, 
                        rgb(239, 68, 68) 0%, 
                        rgb(250, 204, 21) ${Math.min(50, weights.redistributionImpact)}%, 
                        ${weights.redistributionImpact > 50 ? `rgb(34, 197, 94) ${weights.redistributionImpact}%,` : ''}
                        rgb(75, 85, 99) ${weights.redistributionImpact}%,
                        rgb(75, 85, 99) 100%)`
                    }}
                  />
                  <div className="w-10 text-right">
                    {editingWeight === 'redistributionImpact' ? (
                      <input
                        type="text"
                        defaultValue={weights.redistributionImpact}
                        onBlur={(e) => handleWeightEdit('redistributionImpact', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleWeightEdit('redistributionImpact', (e.target as HTMLInputElement).value);
                          } else if (e.key === 'Escape') {
                            setEditingWeight(null);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-1 py-0 bg-secondary text-white text-xs rounded border border-success focus:outline-none focus:ring-2 focus:ring-success text-right box-border"
                        style={{ minHeight: '1.25rem', lineHeight: '1.25rem' }}
                        autoFocus
                      />
                    ) : (
                      <span 
                        className="text-xs font-medium text-white cursor-pointer hover:text-success transition-all inline-block"
                        style={{ minHeight: '1.25rem', lineHeight: '1.25rem' }}
                        onDoubleClick={() => setEditingWeight('redistributionImpact')}
                      >
                        {weights.redistributionImpact}%
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-lg shadow-sm p-4 border border-border hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all">
            <div className="text-xs text-muted-foreground space-y-2">
              <p className="font-medium text-foreground mb-2">How scores are calculated:</p>
              <div className="space-y-1.5 pl-2 border-l-2 border-border">
                <p>
                  <span 
                    className={`font-medium transition-all ${
                      hoveredMetric === 'Location Overlap' 
                        ? 'text-success drop-shadow-glow-success-lg' 
                        : 'text-foreground'
                    }`}
                    onMouseEnter={() => setHoveredMetric('Location Overlap')}
                    onMouseLeave={() => setHoveredMetric(null)}
                  >
                    Location Overlap:
                  </span> Measures how much the operational areas of two vehicles overlap based on their GPS coordinates. <span className="text-success text-xl font-black drop-shadow-glow-success-sm" style={{ lineHeight: '1' }}>↑</span> overlap = <span className="text-success text-xl font-black drop-shadow-glow-success-sm" style={{ lineHeight: '1' }}>↑</span> score.
                </p>
                <p>
                  <span 
                    className={`font-medium transition-all ${
                      hoveredMetric === 'Schedule Match' 
                        ? 'text-success drop-shadow-glow-success-lg' 
                        : 'text-foreground'
                    }`}
                    onMouseEnter={() => setHoveredMetric('Schedule Match')}
                    onMouseLeave={() => setHoveredMetric(null)}
                  >
                    Schedule Match:
                  </span> Compares the hours when each vehicle is active. Vehicles with different active hours score <span className="text-success text-xl font-black drop-shadow-glow-success-sm" style={{ lineHeight: '1' }}>↑</span> (better for redistribution).
                </p>
                <p>
                  <span 
                    className={`font-medium transition-all ${
                      hoveredMetric === 'Activity Match' 
                        ? 'text-success drop-shadow-glow-success-lg' 
                        : 'text-foreground'
                    }`}
                    onMouseEnter={() => setHoveredMetric('Activity Match')}
                    onMouseLeave={() => setHoveredMetric(null)}
                  >
                    Activity Match:
                  </span> Compares average trip patterns (duration, distance, speed) between vehicles. <span className="text-success text-xl font-black drop-shadow-glow-success-sm" style={{ lineHeight: '1' }}>↑</span> similar trips = <span className="text-success text-xl font-black drop-shadow-glow-success-sm" style={{ lineHeight: '1' }}>↑</span> score.
                </p>
                <p>
                  <span 
                    className={`font-medium transition-all ${
                      hoveredMetric === 'Relocation Impact' 
                        ? 'text-success drop-shadow-glow-success-lg' 
                        : 'text-foreground'
                    }`}
                    onMouseEnter={() => setHoveredMetric('Relocation Impact')}
                    onMouseLeave={() => setHoveredMetric(null)}
                  >
                    Relocation Impact:
                  </span> Calculates the distance between vehicles' operational areas. <span className="text-destructive text-xl font-black drop-shadow-glow-critical-sm" style={{ lineHeight: '1' }}>↓</span> distance = <span className="text-destructive text-xl font-black drop-shadow-glow-critical-sm" style={{ lineHeight: '1' }}>↓</span> impact (better). This is <b>inverted</b> in the overall score.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Optimised Utilisation section - Only available for Emergency Services */}
      {serviceType === 'emergency_services' && (
      <section>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">Optimised Utilisation</h2>
          <p className="text-muted-foreground text-sm">
            Compare current and optimised scenarios to see potential savings from fleet optimisation. Results are generated by analysing your fleet's historical usage patterns and identifying opportunities to reduce vehicle count while maintaining service levels. The system runs multiple simulations to validate recommendations.
          </p>
        </div>

        {/* User Configuration Section */}
        <div className="mb-6 bg-gradient-to-br from-card/60 to-card/40 rounded-lg p-6 border-2 border-warning/40 hover:border-warning/60 hover:shadow-lg hover:shadow-warning/20 transition-all">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">
              User Configuration
            </h3>
            <div className="flex items-center gap-2">
              {/* Info icon with tooltip about simulation time */}
              <div className="relative group">
                <HelpCircle 
                  className="w-5 h-5 text-muted-foreground hover:text-warning cursor-help transition-colors" 
                  aria-label="Simulation time information"
                />
                <div className="absolute right-0 top-6 w-64 p-3 bg-popover border border-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none">
                  <div className="text-xs text-foreground space-y-2">
                    <div className="font-semibold text-white mb-2">Simulation Time Factors:</div>
                    <div className="space-y-1">
                      <div>• <strong>Number of incidents:</strong> More incidents = longer simulation time</div>
                      <div>• <strong>Incident scaling factor:</strong> Higher scaling = more incidents = longer time</div>
                      <div>• <strong>Number of simulation runs:</strong> More runs = longer total time</div>
                      <div>• <strong>Fleet size:</strong> Larger fleet = more calculations</div>
                      <div>• <strong>Optimisation scope:</strong> More vehicles/types/stations = longer time</div>
                    </div>
                    <div className="text-warning text-xs mt-2 pt-2 border-t border-border">
                      💡 Tip: Start with smaller scope or lower scaling to test faster
                    </div>
                  </div>
                </div>
              </div>
            <button
              onClick={() => {
                const resultsSection = document.getElementById('optimisation-results-section');
                if (resultsSection) {
                  resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className="px-4 py-2 bg-warning hover:bg-warning text-background text-sm font-medium rounded-md border border-warning/60 hover:border-warning hover:shadow-lg hover:shadow-warning/60 transition-all"
            >
              Optimise Your Fleet
            </button>
            </div>
          </div>
          
          <div className="space-y-6">
            {/* Basic Settings Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 hover:shadow-lg hover:shadow-warning/40 hover:border-warning/40 border border-transparent rounded-lg p-2 transition-all">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-muted-foreground">Scenario</label>
                <select
                  value={optimizationScenario}
                  onChange={(e) => {
                    const newScenario = e.target.value as 'historical' | 'future';
                    setOptimizationScenario(newScenario);
                    // Update incident scaling factor default when scenario changes
                    if (newScenario === 'historical') {
                      setIncidentScalingFactor(0.05); // 5% for historical mode
                    } else {
                      setIncidentScalingFactor(1.0); // 100% for future mode
                    }
                  }}
                  className="px-3 py-2 bg-secondary text-white text-sm rounded border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all"
                >
                  <option value="historical">1. Optimise the Past</option>
                  <option value="future">2. Optimise for Future</option>
                </select>
              </div>
              
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-muted-foreground">Optimisation Goal</label>
                <select
                  value={optimizationObjective}
                  onChange={(e) => {
                    const newObjective = e.target.value;
                    setOptimizationObjective(newObjective);
                    // Auto-adjust Utilization Rate Weight to 30% when first option is selected
                    if (newObjective === 'minimise_fleet_size') {
                      setOptimizationParams({
                        ...optimizationParams,
                        utilizationWeight: 30,
                        compatibilityWeight: 70,
                      });
                    }
                  }}
                  className="px-3 py-2 bg-secondary text-white text-sm rounded border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all"
                >
                  <option value="minimise_fleet_size">1. Minimise fleet size</option>
                  <option value="maximise_utilisation">2. Maximise utilisation (prioritise efficiency)</option>
                  <option value="balance_cost_service">3. Balance cost and service level</option>
                  <option value="optimise_response_time">4. Maintain response time coverage (ensures response times stay within target)</option>
                  <option value="minimise_response_time">5. Minimise response time (prioritises fastest response times)</option>
                </select>
              </div>
              
              {optimizationScenario === 'future' ? (
                <>
                  <div className="flex flex-col gap-1.5">
                    <IncidentScalingFactorLabel />
                    <input
                      type="number"
                      min="0.1"
                      max="10"
                      step="0.1"
                      value={incidentScalingFactor}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 1.0;
                        setIncidentScalingFactor(Math.max(0.1, Math.min(10, value)));
                      }}
                      className="px-3 py-2 bg-secondary text-white text-sm rounded border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <SevereIncidentRatioLabel />
                    <input
                      type="number"
                      min="0.1"
                      max="5"
                      step="0.1"
                      value={pumpingIncidentRatio}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 1.0;
                        setPumpingIncidentRatio(Math.max(0.1, Math.min(5, value)));
                      }}
                      className="px-3 py-2 bg-secondary text-white text-sm rounded border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="flex flex-col gap-1.5">
                    <IncidentScalingFactorLabel />
                    <input
                      type="number"
                      min="0.1"
                      max="10"
                      step="0.1"
                      value={incidentScalingFactor}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 1.0;
                        setIncidentScalingFactor(Math.max(0.1, Math.min(10, value)));
                      }}
                      className="px-3 py-2 bg-secondary text-white text-sm rounded border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all"
                    />
                  </div>
                  {dateRange && (
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-muted-foreground">Data Period</label>
                      <div className="px-3 py-2 bg-secondary/50 rounded border border-border">
                        <span className="text-sm text-success font-medium">{dateRange}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Optimization Scope Configuration */}
            <div className="border-t border-border pt-4 hover:shadow-lg hover:shadow-warning/40 hover:border-warning/40 border border-transparent rounded-lg p-2 -mx-2 transition-all">
              <h4 className="text-sm font-semibold text-white mb-3">Optimisation Scope</h4>
              <p className="text-xs text-muted-foreground mb-4">
                Select specific vehicles, vehicle types, or stations to include in the optimisation. Leave empty to optimise the entire fleet.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Vehicle Selection - Uses shared context */}
                <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-warning/60 hover:shadow-lg hover:shadow-warning/40 transition-all">
                  <label className="text-sm font-medium text-white mb-2 block">Vehicles</label>
                  <div className="max-h-48 overflow-y-auto border border-border rounded p-2 bg-background/50">
                    {availableVehicles.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No vehicles available</p>
                    ) : (
                      <>
                        <div className="mb-2 pb-2 border-b border-border flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() => {
                              if (selectedVehicles.length === availableVehicles.length) {
                                setSelectedVehicles([]);
                              } else {
                                setSelectedVehicles([...availableVehicles]);
                              }
                            }}
                            className="text-xs text-success hover:text-success"
                          >
                            {selectedVehicles.length === availableVehicles.length ? 'Deselect All' : 'Select All'}
                          </button>
                          {selectedVehicles.length > 0 && (
                            <span className="text-xs text-success">
                              ({selectedVehicles.length} vehicle{selectedVehicles.length !== 1 ? 's' : ''} selected)
                            </span>
                          )}
                        </div>
                        {availableVehicles.map(vehicleId => (
                          <label key={vehicleId} className="flex items-center gap-2 py-1 text-xs text-foreground hover:text-white cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedVehicles.includes(vehicleId)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedVehicles([...selectedVehicles, vehicleId]);
                                } else {
                                  setSelectedVehicles(selectedVehicles.filter(v => v !== vehicleId));
                                }
                              }}
                              className="w-4 h-4 text-success bg-secondary border-border rounded focus:ring-success focus:ring-2"
                            />
                            <span>{isDemoMode ? redactVehicleId(vehicleId, isDemoMode) : vehicleId}</span>
                          </label>
                        ))}
                      </>
                    )}
                  </div>
                </div>

                {/* Vehicle Type Selection - Uses shared context */}
                <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-warning/60 hover:shadow-lg hover:shadow-warning/40 transition-all">
                  <label className="text-sm font-medium text-white mb-2 block">Vehicle Types</label>
                  <div className="max-h-48 overflow-y-auto border border-border rounded p-2 bg-background/50">
                    {availableVehicleTypes.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No vehicle types available</p>
                    ) : (
                      <>
                        <div className="mb-2 pb-2 border-b border-border flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() => {
                              if (selectedVehicleTypes.length === availableVehicleTypes.length) {
                                setSelectedVehicleTypes([]);
                              } else {
                                setSelectedVehicleTypes([...availableVehicleTypes]);
                              }
                            }}
                            className="text-xs text-success hover:text-success"
                          >
                            {selectedVehicleTypes.length === availableVehicleTypes.length ? 'Deselect All' : 'Select All'}
                          </button>
                          {selectedVehicleTypes.length > 0 && (
                            <span className="text-xs text-success">
                              ({selectedVehicleTypes.length} type{selectedVehicleTypes.length !== 1 ? 's' : ''} selected)
                            </span>
                          )}
                        </div>
                        {availableVehicleTypes.map(vehicleType => (
                          <label key={vehicleType} className="flex items-center gap-2 py-1 text-xs text-foreground hover:text-white cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedVehicleTypes.includes(vehicleType)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedVehicleTypes([...selectedVehicleTypes, vehicleType]);
                                } else {
                                  setSelectedVehicleTypes(selectedVehicleTypes.filter(t => t !== vehicleType));
                                }
                              }}
                              className="w-4 h-4 text-success bg-secondary border-border rounded focus:ring-success focus:ring-2"
                            />
                            <span>{isDemoMode ? redactVehicleType(vehicleType, isDemoMode) : vehicleType}</span>
                          </label>
                        ))}
                      </>
                    )}
                  </div>
                </div>

                {/* Station Selection - Uses shared context */}
                <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-warning/60 hover:shadow-lg hover:shadow-warning/40 transition-all">
                  <label className="text-sm font-medium text-white mb-2 block">Stations</label>
                  <div className="max-h-48 overflow-y-auto border border-border rounded p-2 bg-background/50">
                    {availableStations.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No stations available</p>
                    ) : (
                      <>
                        <div className="mb-2 pb-2 border-b border-border flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() => {
                              if (selectedStations.length === availableStations.length) {
                                setSelectedStations([]);
                              } else {
                                setSelectedStations([...availableStations]);
                              }
                            }}
                            className="text-xs text-success hover:text-success"
                          >
                            {selectedStations.length === availableStations.length ? 'Deselect All' : 'Select All'}
                          </button>
                          {selectedStations.length > 0 && (
                            <span className="text-xs text-success">
                              ({selectedStations.length} station{selectedStations.length !== 1 ? 's' : ''} selected)
                            </span>
                          )}
                        </div>
                        {availableStations.map(station => (
                          <label key={station} className="flex items-center gap-2 py-1 text-xs text-foreground hover:text-white cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedStations.includes(station)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedStations([...selectedStations, station]);
                                } else {
                                  setSelectedStations(selectedStations.filter(s => s !== station));
                                }
                              }}
                              className="w-4 h-4 text-success bg-secondary border-border rounded focus:ring-success focus:ring-2"
                            />
                            <span>{isDemoMode ? redactStationName(station, isDemoMode) : station}</span>
                          </label>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Optimisation Weightings */}
            <div className="border-t border-border pt-4 hover:shadow-lg hover:shadow-warning/40 hover:border-warning/40 border border-transparent rounded-lg p-2 -mx-2 transition-all">
              <h4 className="text-sm font-semibold text-white mb-3">Optimisation Weightings</h4>
              <p className="text-xs text-muted-foreground mb-4">
                Adjust how the algorithm balances different factors when selecting vehicles for removal. Higher weights mean that factor is more important in the decision.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Utilization vs Compatibility Weight */}
                <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-warning/60 hover:shadow-lg hover:shadow-warning/40 transition-all">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-white">Utilisation Rate Weight</label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        How much to prioritise removing vehicles with lower utilisation rates
                      </p>
                    </div>
                    <span className="text-lg font-semibold text-success min-w-12 text-right ml-2">
                      {optimizationParams.utilizationWeight}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={optimizationParams.utilizationWeight}
                    onChange={(e) => {
                      const utilWeight = parseInt(e.target.value);
                      setOptimizationParams({
                        ...optimizationParams,
                        utilizationWeight: utilWeight,
                        compatibilityWeight: 100 - utilWeight, // Keep total at 100%
                      });
                    }}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, 
                        rgb(239, 68, 68) 0%, 
                        rgb(250, 204, 21) ${Math.min(50, optimizationParams.utilizationWeight)}%, 
                        ${optimizationParams.utilizationWeight > 50 ? `rgb(34, 197, 94) ${optimizationParams.utilizationWeight}%,` : ''}
                        rgb(75, 85, 99) ${optimizationParams.utilizationWeight}%,
                        rgb(75, 85, 99) 100%)`
                    }}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>

                <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-warning/60 hover:shadow-lg hover:shadow-warning/40 transition-all">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-white">Compatibility Score Weight</label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        How much to prioritise vehicles that are easily interchangeable with others
                      </p>
                    </div>
                    <span className="text-lg font-semibold text-ring min-w-12 text-right ml-2">
                      {optimizationParams.compatibilityWeight}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={optimizationParams.compatibilityWeight}
                    onChange={(e) => {
                      const compatWeight = parseInt(e.target.value);
                      setOptimizationParams({
                        ...optimizationParams,
                        compatibilityWeight: compatWeight,
                        utilizationWeight: 100 - compatWeight, // Keep total at 100%
                      });
                    }}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, 
                        rgb(239, 68, 68) 0%, 
                        rgb(250, 204, 21) ${Math.min(50, optimizationParams.compatibilityWeight)}%, 
                        ${optimizationParams.compatibilityWeight > 50 ? `rgb(34, 197, 94) ${optimizationParams.compatibilityWeight}%,` : ''}
                        rgb(75, 85, 99) ${optimizationParams.compatibilityWeight}%,
                        rgb(75, 85, 99) 100%)`
                    }}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Operational Constraints */}
            <div className="border-t border-border pt-4 hover:shadow-lg hover:shadow-warning/40 hover:border-warning/40 border border-transparent rounded-lg p-2 -mx-2 transition-all">
              <h4 className="text-sm font-semibold text-white mb-3">Operational Constraints</h4>
              <p className="text-xs text-muted-foreground mb-4">
                Set safety limits to ensure optimisation doesn't compromise service levels or response times.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-warning/60 hover:shadow-lg hover:shadow-warning/40 transition-all">
                  <label className="text-sm font-medium text-white block mb-2">
                    Minimum Vehicles per Station (Total)
                  </label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Minimum <strong>total</strong> vehicles at each station (any types combined). Use Critical Vehicle Types below to ensure specific types are maintained.
                  </p>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={optimizationParams.minVehiclesPerStation}
                    onChange={(e) => setOptimizationParams({
                      ...optimizationParams,
                      minVehiclesPerStation: parseInt(e.target.value) || 0
                    })}
                    className="w-full px-3 py-2 bg-secondary text-white text-sm rounded border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all"
                  />
                </div>

                <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-warning/60 hover:shadow-lg hover:shadow-warning/40 transition-all">
                  <label className="text-sm font-medium text-white block mb-2">
                    Target Response Time (minutes)
                  </label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Target response time threshold. Optimisation ensures all incidents can be reached within this time when vehicles are removed.
                  </p>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    step="0.5"
                    value={optimizationParams.targetResponseTimeMinutes}
                    onChange={(e) => setOptimizationParams({
                      ...optimizationParams,
                      targetResponseTimeMinutes: parseFloat(e.target.value) || 10.0
                    })}
                    className="w-full px-3 py-2 bg-secondary text-white text-sm rounded border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all"
                  />
                </div>

                <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-warning/60 hover:shadow-lg hover:shadow-warning/40 transition-all">
                  <label className="text-sm font-medium text-white block mb-2">
                    Maximum Station Closures
                  </label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Limit how many single-vehicle stations can be closed during optimisation. Prevents over-aggressive removal in one region.
                  </p>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={optimizationParams.maxStationClosures}
                    onChange={(e) => setOptimizationParams({
                      ...optimizationParams,
                      maxStationClosures: parseInt(e.target.value) || 3
                    })}
                    className="w-full px-3 py-2 bg-secondary text-white text-sm rounded border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all"
                  />
                </div>

                <div className="bg-card/50 rounded-lg p-4 border border-border hover:border-warning/60 hover:shadow-lg hover:shadow-warning/40 transition-all">
                  <label className="text-sm font-medium text-white block mb-2">
                    Critical Vehicle Types 🚒
                  </label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Vehicle types that <strong>must</strong> maintain at least 1 per station. Ensures core emergency response capability.
                  </p>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {(() => {
                      // Extract unique vehicle types from initial utilization data
                      const vehicleTypesSet = new Set<string>();
                      initialUtilizationData.forEach(row => {
                        const vehicleType = getColumnValue(row, [
                          'vehicle_type', 'VEHICLE_TYPE', 'Vehicle Type', 'vehicleType', 'Vehicle_Type', 'type', 'Type', 'TYPE'
                        ]);
                        if (vehicleType) {
                          vehicleTypesSet.add(vehicleType);
                        }
                      });
                      // Prioritize Pumping Appliance at top
                      const typeArray = Array.from(vehicleTypesSet);
                      const sortedTypes = typeArray.sort((a, b) => {
                        if (a.toLowerCase().includes('pumping')) return -1;
                        if (b.toLowerCase().includes('pumping')) return 1;
                        return a.localeCompare(b);
                      });
                      
                      if (sortedTypes.length === 0) {
                        return (
                          <p className="text-xs text-muted-foreground italic">No vehicle types available. Load utilization data first.</p>
                        );
                      }
                      
                      return sortedTypes.map((type) => (
                        <label key={type} className="flex items-center gap-2 text-xs text-foreground hover:text-white cursor-pointer">
                          <input
                            type="checkbox"
                            checked={optimizationParams.criticalVehicleTypes.includes(type)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setOptimizationParams({
                                  ...optimizationParams,
                                  criticalVehicleTypes: [...optimizationParams.criticalVehicleTypes, type]
                                });
                              } else {
                                setOptimizationParams({
                                  ...optimizationParams,
                                  criticalVehicleTypes: optimizationParams.criticalVehicleTypes.filter(t => t !== type)
                                });
                              }
                            }}
                            className="w-3.5 h-3.5 rounded border-border text-success focus:ring-success focus:ring-offset-card"
                          />
                          <span className={type.toLowerCase().includes('pumping') ? 'text-warning font-medium' : ''}>
                            {type}
                            {type.toLowerCase().includes('pumping') && ' (recommended)'}
                          </span>
                        </label>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Simulation Assumptions */}
            <div className="border-t border-border pt-4 hover:shadow-lg hover:shadow-warning/40 hover:border-warning/40 border border-transparent rounded-lg p-2 -mx-2 transition-all">
              <h4 className="text-sm font-semibold text-white mb-3">
                Simulation Assumptions
              </h4>
              <div className="bg-card/30 rounded-lg p-4 border border-border/50">
                <ul className="space-y-1.5 text-xs text-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-success mt-0.5">•</span>
                    <span><strong className="text-white">Multi-run validation:</strong> Runs {isDemoMode ? 'xx' : '5'} simulations to ensure consistent recommendations.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-success mt-0.5">•</span>
                    <span><strong className="text-white">Consensus-based removal:</strong> Vehicles only removed if appearing in ≥{isDemoMode ? 'xx' : '70'}% of runs.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-success mt-0.5">•</span>
                    <span><strong className="text-white">Vehicle type matching:</strong> Workload only redistributed to vehicles of the same type to maintain operational compatibility.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-success mt-0.5">•</span>
                    <span><strong className="text-white">Service level maintenance:</strong> Normal service levels maintained throughout optimisation via response time and capacity checks.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-success mt-0.5">•</span>
                    <span><strong className="text-white">Workload redistribution:</strong> Verifies journeys can be redistributed without exceeding capacity.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-success mt-0.5">•</span>
                    <span><strong className="text-white">Response time validation:</strong> Ensures removal won't compromise response time coverage.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-success mt-0.5">•</span>
                    <span><strong className="text-white">Critical vehicle types:</strong> Stations always retain at least 1 vehicle of each critical type (e.g., Pumping Appliance).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-success mt-0.5">•</span>
                    <span><strong className="text-white">Station closure limits:</strong> Prevents closing too many single-vehicle stations in one optimisation run.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-success mt-0.5">•</span>
                    <span><strong className="text-white">Station minimums:</strong> Each station maintains minimum vehicles as specified above.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Visual Divider and Results Section Header */}
        <div id="optimisation-results-section" className="relative my-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center">
            <div className="bg-background px-4">
              <h3 className="text-lg font-semibold text-white">
                Optimisation Results
              </h3>
            </div>
          </div>
        </div>

        {/* Results Folder Selection */}
        <div className="mb-6 flex items-center gap-4">
          <label className="text-sm font-medium text-muted-foreground">Results Folder:</label>
          {foldersLoading ? (
            <div className="px-3 py-2 bg-secondary text-muted-foreground text-sm rounded border border-border">
              Loading folders...
            </div>
          ) : availableFolders.length === 0 ? (
            <div className="px-3 py-2 bg-secondary text-warning text-sm rounded border border-warning">
              No {optimizationScenario === 'historical' ? '_past' : '_future'} results found
            </div>
          ) : (
            <select
              value={selectedFolder || ''}
              onChange={(e) => setSelectedFolder(e.target.value || null)}
              className="px-3 py-2 bg-secondary text-white text-sm rounded border border-border focus:outline-none focus:ring-2 focus:ring-success hover:border-border transition-all min-w-75"
            >
              {availableFolders.map((folder) => (
                <option key={folder} value={folder}>
                  {folder}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Optimization Metadata (User Configuration) - Displayed first under Results */}
        {optimizationMetadata?.optimization_metadata && (
          <div className="mb-6 bg-card/50 rounded-lg p-6 border border-border">
            <h4 className="text-md font-semibold text-white mb-4 flex items-center gap-2">
              <span className="text-ring">⚙️</span>
              Optimisation Configuration
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Optimization Goal */}
              <div className="bg-background/50 rounded p-3 border border-border/50">
                <div className="text-sm font-semibold text-foreground mb-2">Optimisation Goal</div>
                <div className="text-sm font-medium text-white">
                  {optimizationMetadata.optimization_metadata.optimization_goal_description || 
                   optimizationMetadata.optimization_metadata.optimization_goal || 
                   'Not specified'}
                </div>
              </div>

              {/* Optimization Scope */}
              <div className="bg-background/50 rounded p-3 border border-border/50">
                <div className="text-sm font-semibold text-foreground mb-2">Optimisation Scope</div>
                <div className="text-sm text-foreground space-y-1">
                  <div>
                    <span className="text-muted-foreground">Vehicles:</span>{' '}
                    {Array.isArray(optimizationMetadata.optimization_metadata.optimization_scope?.selected_vehicles)
                      ? optimizationMetadata.optimization_metadata.optimization_scope.selected_vehicles.length > 0
                        ? `${optimizationMetadata.optimization_metadata.optimization_scope.selected_vehicles.length} selected`
                        : 'All vehicles'
                      : optimizationMetadata.optimization_metadata.optimization_scope?.selected_vehicles || 'All vehicles'}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Types:</span>{' '}
                    {Array.isArray(optimizationMetadata.optimization_metadata.optimization_scope?.selected_vehicle_types)
                      ? optimizationMetadata.optimization_metadata.optimization_scope.selected_vehicle_types.length > 0
                        ? optimizationMetadata.optimization_metadata.optimization_scope.selected_vehicle_types.join(', ')
                        : 'All types'
                      : optimizationMetadata.optimization_metadata.optimization_scope?.selected_vehicle_types || 'All types'}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Stations:</span>{' '}
                    {Array.isArray(optimizationMetadata.optimization_metadata.optimization_scope?.selected_stations)
                      ? optimizationMetadata.optimization_metadata.optimization_scope.selected_stations.length > 0
                        ? optimizationMetadata.optimization_metadata.optimization_scope.selected_stations.join(', ')
                        : 'All stations'
                      : optimizationMetadata.optimization_metadata.optimization_scope?.selected_stations || 'All stations'}
                  </div>
                </div>
              </div>

              {/* Optimization Weights */}
              <div className="bg-background/50 rounded p-3 border border-border/50">
                <div className="text-sm font-semibold text-foreground mb-2">Optimisation Weights</div>
                <div className="text-sm text-foreground space-y-1">
                  <div>
                    <span className="text-muted-foreground">Utilisation:</span>{' '}
                    <span className="text-success font-medium">
                      {optimizationMetadata.optimization_metadata.optimization_weights?.utilization_weight_percentage || 
                       (optimizationMetadata.optimization_metadata.optimization_weights?.utilization_weight 
                        ? Math.round(optimizationMetadata.optimization_metadata.optimization_weights.utilization_weight * 100) 
                        : 'N/A')}%
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Compatibility:</span>{' '}
                    <span className="text-ring font-medium">
                      {optimizationMetadata.optimization_metadata.optimization_weights?.compatibility_weight_percentage || 
                       (optimizationMetadata.optimization_metadata.optimization_weights?.compatibility_weight 
                        ? Math.round(optimizationMetadata.optimization_metadata.optimization_weights.compatibility_weight * 100) 
                        : 'N/A')}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Operational Constraints */}
              <div className="bg-background/50 rounded p-3 border border-border/50">
                <div className="text-sm font-semibold text-foreground mb-2">Operational Constraints</div>
                <div className="text-sm text-foreground space-y-1">
                  <div>
                    <span className="text-muted-foreground">Min vehicles/station:</span>{' '}
                    {optimizationMetadata.optimization_metadata.operational_constraints?.min_vehicles_per_station ?? 'N/A'}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Target response time:</span>{' '}
                    {optimizationMetadata.optimization_metadata.operational_constraints?.target_response_time_minutes 
                      ? `${optimizationMetadata.optimization_metadata.operational_constraints.target_response_time_minutes} min`
                      : 'N/A'}
                  </div>
                </div>
              </div>

              {/* Simulation Settings */}
              <div className="bg-background/50 rounded p-3 border border-border/50">
                <div className="text-sm font-semibold text-foreground mb-2">Simulation Settings</div>
                <div className="text-sm text-foreground space-y-1">
                  <div>
                    <span className="text-muted-foreground">Mode:</span>{' '}
                    {optimizationMetadata.optimization_metadata.simulation_settings?.use_historical_data 
                      ? 'Historical' 
                      : 'Future'}
                  </div>
                  {optimizationMetadata.optimization_metadata.simulation_settings?.use_historical_data && (
                    <div>
                      <span className="text-muted-foreground">Date range:</span>{' '}
                      {optimizationMetadata.optimization_metadata.simulation_settings.historical_start_date && 
                       optimizationMetadata.optimization_metadata.simulation_settings.historical_end_date
                        ? `${optimizationMetadata.optimization_metadata.simulation_settings.historical_start_date} to ${optimizationMetadata.optimization_metadata.simulation_settings.historical_end_date}`
                        : 'N/A'}
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Incident scaling:</span>{' '}
                    {optimizationMetadata.optimization_metadata.simulation_settings?.incident_scaling_factor ?? 'N/A'}x
                  </div>
                  <div>
                    <span className="text-muted-foreground">Pumping ratio:</span>{' '}
                    {optimizationMetadata.optimization_metadata.simulation_settings?.pumping_incident_ratio ?? 'N/A'}
                  </div>
                </div>
              </div>

              {/* Run Information */}
              <div className="bg-background/50 rounded p-3 border border-border/50">
                <div className="text-sm font-semibold text-foreground mb-2">Run Information</div>
                <div className="text-sm text-foreground space-y-1">
                  <div>
                    <span className="text-muted-foreground">Number of runs:</span>{' '}
                    {optimizationMetadata.optimization_metadata.num_runs ?? 'N/A'}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Region:</span>{' '}
                    {optimizationMetadata.optimization_metadata.region ?? 'N/A'}
                  </div>
                  {optimizationMetadata.optimization_metadata.timestamp && (
                    <div>
                      <span className="text-muted-foreground">Generated:</span>{' '}
                      {new Date(optimizationMetadata.optimization_metadata.timestamp).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {optimizationData.loading && (
          <div className="mb-4 p-4 bg-card rounded-lg border border-border">
            <div className="flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-warning mb-4"></div>
              <p className="text-muted-foreground">Loading optimisation data...</p>
            </div>
          </div>
        )}
        
        {optimizationData.error && (
          <div className="mb-4 p-4 bg-destructive/20 rounded-lg border border-destructive">
            <p className="text-destructive">{optimizationData.error}</p>
          </div>
        )}
        
        <div className="mt-6 space-y-6">
            {optimizationData.simulationSummary.length === 0 ? (
              <div className="bg-card/50 rounded-lg p-6 border border-border">
                <p className="text-muted-foreground text-sm">
                  No simulation summary data available. {optimizationData.loading ? 'Loading...' : 'Please ensure optimization results are available.'}
                </p>
                {process.env.NODE_ENV === 'development' && optimizationData.simulationSummary.length === 0 && (
                  <p className="text-muted-foreground text-xs mt-2">
                    Debug: Check console for fetched data structure.
                  </p>
        )}
              </div>
            ) : (
          <div>
            {/* Run Selector - Only show if there are multiple runs */}
            {optimizationData.simulationSummary.length > 1 && (
              <div className="mb-4 flex items-center gap-3">
                <label htmlFor="run-selector" className="text-sm text-muted-foreground">
                  Simulation Run:
                </label>
                <select
                  id="run-selector"
                  value={selectedRunIndex}
                  onChange={(e) => setSelectedRunIndex(Number(e.target.value))}
                  className="bg-secondary border border-border rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                >
                  {optimizationData.simulationSummary.map((run, index) => {
                    const runId = getColumnValue(run, ['run_id', 'RUN_ID', 'runId']) || (index + 1).toString();
                    
                    // Get metrics based on optimization objective
                    const costSavings = parseFloat(getColumnValue(run, ['cost_savings', 'COST SAVINGS', 'total_cost_savings', 'TOTAL COST SAVINGS'])) || 0;
                    const activeRemoved = parseFloat(getColumnValue(run, ['active_vehicles_removed', 'ACTIVE VEHICLES REMOVED'])) || 0;
                    const inactiveRemoved = parseFloat(getColumnValue(run, ['inactive_vehicles_removed', 'INACTIVE VEHICLES REMOVED'])) || 0;
                    const totalRemoved = activeRemoved + inactiveRemoved;
                    const vehiclesRemoved = parseFloat(getColumnValue(run, ['vehicles_removed', 'VEHICLES REMOVED', 'num_vehic', 'num_vehicles'])) || totalRemoved;
                    
                    // Calculate final fleet as: total_active_fleet - active_vehicles_removed
                    const totalActiveFleet = parseFloat(getColumnValue(run, ['total_active_fleet', 'TOTAL ACTIVE FLEET'])) || 0;
                    const finalFleet = totalActiveFleet - activeRemoved;
                    
                    let label = `Run ${runId}`;
                    let metricText = '';
                    
                    // Show relevant metric based on optimization objective
                    switch (optimizationObjective) {
                      case 'minimise_fleet_size':
                        metricText = `Final fleet: ${formatNumber(finalFleet, 0)}`;
                        break;
                      case 'maximise_utilisation':
                        metricText = `${formatNumber(vehiclesRemoved, 0)} vehicles removed`;
                        break;
                      case 'balance_cost_service':
                        metricText = costSavings > 0 ? `${formatCurrency(costSavings.toString())} savings` : '';
                        break;
                      case 'optimise_response_time':
                      case 'minimise_response_time': {
                        // Show response time if available
                        const optimizedResponseTime = parseFloat(getColumnValue(run, [
                          'optimized_avg_response_time_minutes',
                          'OPTIMIZED_AVG_RESPONSE_TIME_MINUTES',
                          'avg_response_time_minutes',
                          'AVG_RESPONSE_TIME_MINUTES'
                        ]));
                        
                        if (!isNaN(optimizedResponseTime) && optimizedResponseTime > 0) {
                          metricText = `Avg response: ${formatNumber(optimizedResponseTime, 1)} min`;
                        } else {
                          // Fallback to cost savings if response time not available
                          metricText = costSavings > 0 ? `${formatCurrency(costSavings.toString())} savings` : '';
                        }
                        break;
                      }
                      default:
                        if (costSavings > 0) {
                          metricText = `${formatCurrency(costSavings.toString())} savings`;
                        } else if (vehiclesRemoved > 0) {
                          metricText = `${formatNumber(vehiclesRemoved, 0)} vehicles removed`;
                        }
                    }
                    
                    if (metricText) {
                      label += ` (${metricText})`;
                    }
                    
                    // Show star only for the best run based on optimization objective
                    // Use bestRunIndexForDisplay which is computed separately
                    if (index === bestRunIndexForDisplay) {
                      label += ' ⭐ Best';
                    }
                    
                    return (
                      <option key={index} value={index}>
                        {label}
                      </option>
                    );
                  })}
                </select>
                <span className="text-xs text-muted-foreground">
                  (Run {bestRunIndexForDisplay + 1} is the best for "{optimizationObjective === 'minimise_fleet_size' ? 'Minimise fleet size' : optimizationObjective === 'maximise_utilisation' ? 'Maximise utilisation' : optimizationObjective === 'balance_cost_service' ? 'Balance cost and service' : optimizationObjective === 'optimise_response_time' ? 'Maintain response time coverage' : optimizationObjective === 'minimise_response_time' ? 'Minimise response time' : 'Optimise response time'}")
                </span>
              </div>
            )}
          <div className="w-full">
            <DataTable
              title="Simulation Summary"
              columns={[
                { key: 'metric', label: 'Metric' },
                { key: 'before', label: 'Current Fleet', align: 'right' as const },
                { key: 'after', label: 'Optimised Fleet', align: 'right' as const },
                { key: 'change', label: 'Change', align: 'right' as const },
              ]}
              data={simulationData}
              customColumnWidths={{
                metric: { minWidth: 25, maxWidth: 25 },
                before: { minWidth: 25, maxWidth: 25 },
                after: { minWidth: 25, maxWidth: 25 },
                change: { minWidth: 25, maxWidth: 25 },
              }}
            />
          </div>
          </div>
            )}

            {/* Incident Summary Section */}
            {(incidentData.incidentLog.length > 0 || incidentData.loading || incidentData.error) && (
              <div className="space-y-4">
                {incidentData.loading && (
                  <div className="p-8 bg-card rounded-lg border border-border text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-warning mb-2"></div>
                      <p className="text-muted-foreground">Loading incident data for run {selectedRunIndex + 1}...</p>
                    </div>
                  </div>
                )}
                {incidentData.error && !incidentData.loading && (
                  <div className="p-8 bg-destructive/20 rounded-lg border border-destructive text-center">
                    <p className="text-destructive font-semibold">Error loading incident data</p>
                    <p className="text-destructive text-sm mt-2">{incidentData.error}</p>
                    <p className="text-muted-foreground text-xs mt-2">Please check the console for more details.</p>
                  </div>
                )}
                {!incidentData.loading && !incidentData.error && incidentData.incidentLog.length === 0 && (
                  <div className="p-8 bg-card rounded-lg border border-border text-center">
                    <p className="text-muted-foreground">No incident data available for run {selectedRunIndex + 1}.</p>
                    <p className="text-muted-foreground text-sm mt-2">The event logs may not have been generated for this run.</p>
                  </div>
                )}
                {!incidentData.loading && !incidentData.error && incidentData.incidentLog.length > 0 && (
                <DataTable
                    key={`incident-summary-${selectedRunIndex}-${incidentViewType}`}
                    title="Incident Summary"
                    headerRightContent={
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">
                          {incidentSummaryData.length} incidents
                          {incidentViewType === 'optimized' && (() => {
                            const changedCount = incidentSummaryData.filter(row => row.hasChange).length;
                            return changedCount > 0 
                              ? <span className="text-success ml-2">({changedCount} changed)</span>
                              : <span className="text-muted-foreground ml-2">(no changes)</span>;
                          })()}
                        </span>
                        <select
                          value={incidentViewType}
                          onChange={(e) => setIncidentViewType(e.target.value as 'original' | 'optimized')}
                          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border cursor-pointer ${
                            incidentViewType === 'optimized'
                              ? 'bg-success text-white border-success shadow-lg shadow-success/50'
                              : 'bg-accent text-white border-border shadow-lg shadow-accent/50'
                          } focus:outline-none focus:ring-2 focus:ring-success focus:border-success`}
                          style={{
                            appearance: 'none',
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23fff' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 0.75rem center',
                            paddingRight: '2.5rem'
                          }}
                        >
                          <option value="original">Original Dispatch</option>
                          <option value="optimized">Optimised Dispatch</option>
                        </select>
                        <button
                          onClick={() => {
                            const headers = incidentViewType === 'optimized' 
                              ? ['ID', 'Incident', 'Required', 'Station(s)', 'Optimised Dispatch', 'Changed?', 'Original Dispatch']
                              : ['ID', 'Incident', 'Required', 'Station(s)', 'Original Dispatch'];
                            const csvContent = [
                              headers.join(','),
                              ...incidentSummaryData.map(row => {
                                const baseData = [
                                  row.id,
                                  `"${row.incident}"`,
                                  `"${row.requiredVehicles}"`,
                                  `"${row.responsibleStations}"`,
                                  `"${row.dispatchedVehicles}"`
                                ];
                                if (incidentViewType === 'optimized') {
                                  baseData.push(row.hasChange ? 'Yes' : 'No');
                                  baseData.push(`"${row.originalDispatch || ''}"`);
                                }
                                return baseData.join(',');
                              })
                            ].join('\n');
                            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                            const link = document.createElement('a');
                            const url = URL.createObjectURL(blob);
                            link.setAttribute('href', url);
                            link.setAttribute('download', `incident_summary_${incidentViewType}_${new Date().toISOString().split('T')[0]}.csv`);
                            link.style.visibility = 'hidden';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all border group bg-secondary text-foreground border-border hover:bg-accent hover:border-success hover:text-success hover:shadow-lg hover:shadow-success/50"
                        >
                          <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-125 transition-all duration-300" />
                          <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
                        </button>
                      </div>
                    }
                    columns={[
                      { key: 'id', label: 'ID' },
                      { key: 'incident', label: 'Incident' },
                      { key: 'requiredVehicles', label: 'Required' },
                      { key: 'responsibleStations', label: 'Station(s)' },
                      { key: 'dispatchedVehicles', label: incidentViewType === 'original' ? 'Original Dispatch' : 'Optimised Dispatch' },
                      ...(incidentViewType === 'optimized' ? [{ key: 'changeIndicator', label: 'Changed?' }] : []),
                    ]}
                    data={incidentSummaryData.map(row => ({
                      ...row,
                      // Station(s) column with hover tooltip for full list
                      responsibleStations: row.stationsNeedsTruncation ? (
                        <span className="cursor-help relative group/stations">
                          {row.responsibleStations}
                          <span className="invisible group-hover/stations:visible absolute z-float left-full ml-2 top-1/2 -translate-y-1/2 px-3 py-2 bg-popover text-xs text-foreground rounded-lg shadow-xl border border-border whitespace-nowrap pointer-events-none">
                            <div className="text-ring font-medium mb-1">All Stations:</div>
                            <div>{row.responsibleStationsFull}</div>
                          </span>
                        </span>
                      ) : row.responsibleStations,
                      // Dispatch column with hover tooltip for full vehicle list
                      dispatchedVehicles: row.dispatchNeedsTruncation ? (
                        <span className="cursor-help relative group/dispatch">
                          {row.dispatchedVehicles}
                          <span className="invisible group-hover/dispatch:visible absolute z-float right-full mr-2 top-1/2 -translate-y-1/2 px-3 py-2 bg-popover text-xs text-foreground rounded-lg shadow-xl border border-border whitespace-nowrap pointer-events-none">
                            <div className="text-ring font-medium mb-1">All Vehicles:</div>
                            <div>{row.dispatchedVehiclesFull}</div>
                          </span>
                        </span>
                      ) : row.dispatchedVehicles,
                      changeIndicator: incidentViewType === 'optimized' ? (
                        row.hasChange ? (
                          <span className="text-success font-medium cursor-help relative group/tooltip">
                            Yes
                            <span className="invisible group-hover/tooltip:visible absolute z-float right-full mr-2 top-1/2 -translate-y-1/2 px-3 py-2 bg-popover text-xs text-foreground rounded-lg shadow-xl border border-border whitespace-nowrap pointer-events-none">
                              <div className="text-success font-medium mb-1">Changes from Original:</div>
                              {row.addedVehicles && row.addedVehicles.length > 0 && (
                                <div className="text-success">+ {row.addedVehicles.join(', ')}</div>
                              )}
                              {row.removedVehicles && row.removedVehicles.length > 0 && (
                                <div className="text-destructive">− {row.removedVehicles.join(', ')}</div>
                              )}
                              {row.originalDispatch && (
                                <div className="text-muted-foreground mt-1 pt-1 border-t border-border">Original: {row.originalDispatch}</div>
                              )}
                            </span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">No</span>
                        )
                      ) : null,
                    }))}
                    customColumnWidths={{
                      id: { minWidth: 4, maxWidth: 5 },
                      incident: { minWidth: 6, maxWidth: 8 },
                      requiredVehicles: { minWidth: 8, maxWidth: 10 },
                      responsibleStations: { minWidth: 8, maxWidth: 10 },
                      dispatchedVehicles: { minWidth: 14, maxWidth: 18 },
                      ...(incidentViewType === 'optimized' ? { changeIndicator: { minWidth: 6, maxWidth: 8 } } : {}),
                    }}
                  />
                )}
              </div>
            )}

            {/* Fleet Size and Utilization Plots - Right after Simulation Summary */}
            {optimizationData.aggregateCostReport.length > 0 && costData.length > 0 && (
              <div className="space-y-6">
                {/* Fleet Size After Optimisation */}
                <div>
                  <HorizontalBarChart
                    data={(() => {
                      // Use costData for accurate fleet sizes (costData uses Initial - Removed for "after")
                      // This ensures consistency with the Simulation Summary section
                      const nonTotalRows = costData.filter(r => r.vehicleType?.toUpperCase() !== 'TOTAL');
                      
                      return nonTotalRows
                        .map(row => {
                          // Before = _beforeFleet (active) + _beforeFleetInactive
                          const beforeTotal = (row._beforeFleet || 0) + (row._beforeFleetInactive || 0);
                          // After = _afterFleet (calculated as Initial - Removed, ensures consistency)
                          const afterTotal = row._afterFleet || 0;
                          
                          return {
                            label: redactVehicleType(row.vehicleType || '', isDemoMode),
                            values: [
                              {
                                vehicle: 'Before',
                                value: beforeTotal,
                                color: STATUS_COLORS.info // Blue for Before
                              },
                              {
                                vehicle: 'After',
                                value: afterTotal,
                                color: STATUS_COLORS.success // Green for After
                              }
                            ],
                            count: beforeTotal,
                            reduction: beforeTotal - afterTotal
                          };
                        })
                        .filter(item => item.count > 0 || item.values.some(v => v.value > 0))
                        .sort((a, b) => {
                          // Sort by reduction amount (most reduced first)
                          return b.reduction - a.reduction;
                        });
                    })()}
                    title="Fleet Size After Optimisation"
                    description="Total fleet size by vehicle type before and after optimisation (includes active and inactive vehicles)"
                    xAxisLabel="Number of Vehicles"
                    yAxisLabel="Vehicle Type"
                    height="h-auto"
                    showPercentage={false}
                  />
                </div>

                {/* Average Utilisation by Vehicle Type (%) */}
                {Object.keys(averageUtilizationByType).length > 0 && (
                  <div>
                    <HorizontalBarChart
                      data={Object.entries(averageUtilizationByType)
                        .filter(([type]) => type.toUpperCase() !== 'TOTAL')
                        .filter(([, util]) => (util.before || 0) > 0 || (util.after || 0) > 0)
                        .map(([type, util]) => ({
                          label: redactVehicleType(type, isDemoMode),
                          values: [
                            {
                              vehicle: 'Before',
                              value: util.before || 0,
                              color: STATUS_COLORS.info
                            },
                            {
                              vehicle: 'After',
                              value: util.after || 0,
                              color: STATUS_COLORS.success
                            }
                          ]
                        }))
                        .sort((a, b) => {
                          // Sort by improvement (after - before), descending
                          const aImprovement = (a.values[1]?.value || 0) - (a.values[0]?.value || 0);
                          const bImprovement = (b.values[1]?.value || 0) - (b.values[0]?.value || 0);
                          return bImprovement - aImprovement;
                        })}
                      title="Average Utilisation by Vehicle Type (%)"
                      description="Fleet-wide average utilisation including all vehicles (active + inactive). Removing inactive vehicles improves fleet efficiency."
                      xAxisLabel="Utilisation (%)"
                      yAxisLabel="Vehicle Type"
                      height="h-auto"
                      showPercentage={false}
                    />
                  </div>
                )}

                {/* Estimated Fuel Consumption by Vehicle Type (L) */}
                {fuelConsumed > 0 && (() => {
                  const initialFuelValue = (() => {
                    if (fuelReduction !== 0 && fuelConsumed > 0) {
                      return fuelConsumed / (1 - fuelReduction / 100);
                    }
                    const initialFuel = parseFloat(getColumnValue(simulationSummaryObj || {}, [
                      'initial_fuel_consumption', 'initial_fuel_consumed', 'initial_total_fuel', 'initial_fuel_litres',
                      'INITIAL FUEL CONSUMPTION', 'INITIAL FUEL CONSUMED', 'INITIAL TOTAL FUEL', 'INITIAL FUEL LITRES'
                    ])) || 0;
                    return initialFuel > 0 ? initialFuel : fuelConsumed;
                  })();

                  // Estimate fuel by vehicle type based on fleet size and utilization
                  // Assumption: Fuel consumption is proportional to (fleet size × average utilization)
                  const fuelByType = costData
                    .filter(row => row.vehicleType?.toUpperCase() !== 'TOTAL')
                    .map(row => {
                      const vehicleTypeRaw = row.vehicleType || 'Unknown';
                      const vehicleType = redactVehicleType(vehicleTypeRaw, isDemoMode);
                      const beforeFleet = row._beforeFleet || 0;
                      const afterFleet = row._afterFleet || 0;
                      
                      // Get average utilization for this vehicle type (use original type for lookup)
                      const beforeUtil = averageUtilizationByType[vehicleTypeRaw]?.before || 0;
                      const afterUtil = averageUtilizationByType[vehicleTypeRaw]?.after || 0;
                      
                      // Calculate weighted utilization (fleet size × utilization)
                      const beforeWeight = beforeFleet * (beforeUtil / 100);
                      const afterWeight = afterFleet * (afterUtil / 100);
                      
                      return { vehicleType, beforeWeight, afterWeight, beforeFleet, afterFleet };
                    })
                    .filter(item => item.beforeWeight > 0 || item.afterWeight > 0);

                  // Calculate total weight for proportion calculation
                  const totalBeforeWeight = fuelByType.reduce((sum, item) => sum + item.beforeWeight, 0);
                  const totalAfterWeight = fuelByType.reduce((sum, item) => sum + item.afterWeight, 0);

                  if (totalBeforeWeight > 0 && fuelByType.length > 0) {
                    return (
                      <HorizontalBarChart
                        data={fuelByType.map(item => ({
                          label: redactVehicleType(item.vehicleType, isDemoMode),
                          values: [
                            {
                              vehicle: 'Before',
                              value: totalBeforeWeight > 0 ? (item.beforeWeight / totalBeforeWeight) * initialFuelValue : 0,
                              color: STATUS_COLORS.info
                            },
                            {
                              vehicle: 'After',
                              value: totalAfterWeight > 0 ? (item.afterWeight / totalAfterWeight) * fuelConsumed : 0,
                              color: STATUS_COLORS.success
                            }
                          ]
                        }))}
                        title="Estimated Fuel Consumption by Vehicle Type (L)"
                        description="Estimated based on fleet size and utilisation"
                        xAxisLabel="Fuel Consumption (L)"
                        yAxisLabel="Vehicle Type"
                        height="h-auto"
                        showPercentage={false}
                      />
                    );
                  }
                  return null;
                })()}
              </div>
            )}

            {costData.length === 0 ? (
              <div className="bg-card/50 rounded-lg p-6 border border-border">
                <p className="text-muted-foreground text-sm">
                  No cost analysis data available. {optimizationData.loading ? 'Loading...' : 'Please ensure optimization results are available.'}
                </p>
                {process.env.NODE_ENV === 'development' && (
                  <p className="text-muted-foreground text-xs mt-2">
                    Debug: Check console for fetched data structure. Aggregate Cost Report rows: {optimizationData.aggregateCostReport.length}
                  </p>
                )}
              </div>
            ) : (
          <div>
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-white mb-4">Cost Report for Optimisation</h2>
              <p className="text-muted-foreground text-sm mb-4">
                Individual vehicle summary showing which vehicles were retained or removed during optimisation.
              </p>
            </div>
          <DataTable
              title="Vehicle Type Summary"
              equalWidthColumns
            columns={[
              { key: 'vehicleType', label: 'Vehicle Type' },
                  { 
                    key: 'unitCost', 
                    label: (
                      <div className="flex items-center justify-between w-full" style={{ paddingRight: '8px', paddingLeft: '8px' }}>
                        <span>Unit Cost (£)</span>
                        <Pencil className="w-3.5 h-3.5 text-warning drop-shadow-glow-warning-md hover:drop-shadow-glow-warning-lg transition-all cursor-help flex-shrink-0" />
                      </div>
                    ), 
                    align: 'right' as const 
                  },
              { key: 'beforeFleet', label: 'Current Active Fleet', align: 'right' as const },
              { key: 'beforeFleetInactive', label: 'Current Inactive Fleet', align: 'right' as const },
              { key: 'afterFleet', label: 'Optimised Fleet', align: 'center' as const },
              { key: 'capitalSavings', label: 'Capital Savings (£)', align: 'right' as const },
              { key: 'removedVehicles', label: 'Removed', align: 'center' as const },
            ]}
              data={costDataWithEditable}
            onExport={() => console.log('Export clicked')}
          />
          </div>
            )}

        </div>

        {/* Cost Report by Vehicle Section */}
        <div className="mt-6"> 
          {vehicleData.length === 0 ? (
            <div className="bg-card/50 rounded-lg p-6 border border-border">
              <p className="text-muted-foreground text-sm">
                No vehicle data available. {optimizationData.loading ? 'Loading...' : 'Please ensure optimization results are available.'}
              </p>
            </div>
          ) : (
            <div className="bg-card rounded-lg shadow-sm overflow-visible border border-border w-full max-w-full hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all group">
              <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-transparent">
                <h3 className="text-lg font-semibold text-white">
                  {fleetFilter === 'active' ? 'Active Fleet Summary' : 'Inactive Fleet Summary'}
                </h3>
                <div className="flex items-center gap-2">
                  <select
                    value={fleetFilter}
                    onChange={(e) => setFleetFilter(e.target.value as 'active' | 'inactive')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all border cursor-pointer ${
                      fleetFilter === 'active'
                        ? 'bg-success text-white border-success shadow-lg shadow-success/50'
                        : 'bg-secondary text-foreground border-border hover:bg-accent hover:border-success hover:text-success hover:shadow-lg hover:shadow-success/50'
                    } focus:outline-none focus:ring-2 focus:ring-success focus:border-success`}
                    style={{
                      appearance: 'none',
                      backgroundImage: fleetFilter === 'active'
                        ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='${fleetFilter === 'active' ? '%23fff' : '%239ca3af'}' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`
                        : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.75rem center',
                      paddingRight: '2.5rem'
                    }}
                  >
                    <option value="active" className="bg-card text-foreground">Active Fleet</option>
                    <option value="inactive" className="bg-card text-foreground">Inactive Fleet</option>
                  </select>
                  <button
                    onClick={() => {
                      const csvContent = [
                        // Header row
                        ['Vehicle ID', 'Station', 'Vehicle Type', 'Status', 'Initial Utilisation (%)', 'Final Utilisation (%)', 'Capital Savings (£)'].join(','),
                        // Data rows
                        ...(fleetFilter === 'active' 
                          ? vehicleData.filter(v => {
                              const epsilon = 0.1;
                              return (v.initialUtilization > epsilon || (v.finalUtilization !== null && v.finalUtilization > epsilon));
                            })
                          : vehicleData.filter(v => {
                              const epsilon = 0.1;
                              return (v.initialUtilization <= epsilon && (v.finalUtilization === null || v.finalUtilization <= epsilon));
                            })
                        ).map(vehicle => {
                          const initialUtil = vehicle.initialUtilization > 0 
                            ? formatPercentage(vehicle.initialUtilization, 1)
                            : '-';
                          const finalUtil = vehicle.finalUtilization !== null && vehicle.finalUtilization > 0.1
                            ? formatPercentage(vehicle.finalUtilization, 1)
                            : '-';
                          const capitalSavings = vehicle.status === 'Removed' && vehicle.capitalSavings > 0
                            ? vehicle.capitalSavings.toLocaleString('en-GB')
                            : '-';
                          return [
                            vehicle.vehicleId,
                            vehicle.station,
                            vehicle.vehicleType,
                            vehicle.status,
                            initialUtil,
                            finalUtil,
                            capitalSavings
                          ].map(val => String(val || '').replace(/,/g, ';')).join(',');
                        })
                      ].join('\n');
                      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                      const link = document.createElement('a');
                      const url = URL.createObjectURL(blob);
                      link.setAttribute('href', url);
                      link.setAttribute('download', `fleet_summary_${fleetFilter}_${new Date().toISOString().split('T')[0]}.csv`);
                      link.style.visibility = 'hidden';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground bg-secondary rounded-md hover:bg-accent hover:border-success hover:shadow-lg hover:shadow-success/50 transition-all border border-transparent group"
                  >
                    <Download className="w-4 h-4 group-hover:text-success group-hover:drop-shadow-glow-success-lg group-hover:scale-125 transition-all duration-300" />
                    <span className="group-hover:text-success group-hover:drop-shadow-glow-success-md transition-all">Export</span>
                  </button>
                </div>
              </div>
              {fleetFilter === 'inactive' && (() => {
                // Calculate total inactive vehicles from aggregate data
                const totalInactiveBefore = costData
                  .filter(row => row.vehicleType?.toUpperCase() !== 'TOTAL')
                  .reduce((sum, row) => sum + (row._beforeFleetInactive || 0), 0);
                const totalInactiveRemoved = costData
                  .filter(row => row.vehicleType?.toUpperCase() !== 'TOTAL')
                  .reduce((sum, row) => sum + (row._removedVehiclesInactive || 0), 0);
                
                // Count inactive vehicles in vehicleData
                const epsilon = 0.1;
                const inactiveVehiclesInData = vehicleData.filter(v => 
                  v.initialUtilization <= epsilon && (v.finalUtilization === null || v.finalUtilization <= epsilon)
                ).length;
                
                // Show note if there's a discrepancy
                if (totalInactiveBefore > inactiveVehiclesInData) {
                  return (
                    <div className="px-6 py-3 bg-warning/20 border-b border-warning/50">
                      <p className="text-xs text-warning">
                        <strong>Note:</strong> The Vehicle Type Summary shows {formatNumber(totalInactiveBefore, 0)} inactive vehicles before optimisation, 
                        but only {inactiveVehiclesInData} individual inactive vehicle records are available in the utilization data. 
                        {totalInactiveRemoved > 0 && ` ${formatNumber(totalInactiveRemoved, 0)} inactive vehicles were removed.`}
                        {' '}Inactive vehicles with 0% utilization may not appear in the utilization CSVs.
                      </p>
                    </div>
                  );
                }
                return null;
              })()}
            <DataTable
                title=""
                hideExport
                equalWidthColumns
              columns={[
                  { key: 'vehicleId', label: 'Vehicle ID' },
                  { key: 'station', label: 'Station' },
                  { key: 'vehicleType', label: 'Vehicle Type' },
                  { key: 'status', label: 'Status', align: 'center' as const },
                  { key: 'initialUtilization', label: 'Initial Utilisation (%)', align: 'right' as const },
                  { key: 'finalUtilization', label: 'Final Utilisation (%)', align: 'right' as const },
                  { key: 'capitalSavings', label: 'Capital Savings (£)', align: 'right' as const },
                ]}
                data={(fleetFilter === 'active' 
                  ? vehicleData.filter(v => {
                      const epsilon = 0.1;
                      return (v.initialUtilization > epsilon || (v.finalUtilization !== null && v.finalUtilization > epsilon));
                    })
                  : vehicleData.filter(v => {
                      const epsilon = 0.1;
                      return (v.initialUtilization <= epsilon && (v.finalUtilization === null || v.finalUtilization <= epsilon));
                    })
                ).map(vehicle => {
                // For vehicles that received workload:
                // - If they were in initial fleet (initialUtilization > 0), show actual percentage
                // - If they were NOT in initial fleet (initialUtilization === 0), show "-"
                const initialUtilization = vehicle.initialUtilization > 0 
                  ? formatPercentage(vehicle.initialUtilization, 1)
                  : '-';
                
                // Calculate change for final utilization display
                let finalUtilizationDisplay: string | JSX.Element = '-';
                
                // Removed vehicles should always show "-" for final utilization, regardless of CSV value
                if (vehicle.status === 'Removed') {
                  finalUtilizationDisplay = '-';
                } else if (vehicle.finalUtilization !== null) {
                  const epsilon = 0.1; // Threshold for considering change as zero (0.1% - hide subtle changes like ±0.1%)
                  
                  // If final utilization is 0 (or effectively 0), show "-" instead of "0%"
                  if (vehicle.finalUtilization <= epsilon) {
                    finalUtilizationDisplay = '-';
                  } else {
                    const finalUtil = formatPercentage(vehicle.finalUtilization, 1);
                    const change = vehicle.finalUtilization - vehicle.initialUtilization;
                    
                    // For vehicles that received workload:
                    // - If they had initial utilization > 0, show the change with correct sign (only if significant)
                    // - If they had initial utilization === 0 (weren't active initially), show the final value with increase indicator (only if significant)
                    if (vehicle.status === 'Received Workload' && vehicle.finalUtilization > 0) {
                      if (vehicle.initialUtilization > 0) {
                        // Vehicle was active initially, show change with correct sign only if significant
                        if (Math.abs(change) > epsilon) {
                          const changeFormatted = formatNumber(Math.abs(change), 1);
                          if (change > 0) {
                            // Utilization increased (expected for vehicles receiving workload)
                            finalUtilizationDisplay = (
                              <span>
                                {finalUtil} <span className="text-ring text-xs">(+{changeFormatted}%)</span>
                              </span>
                            );
                          } else {
                            // Utilization decreased (unexpected but possible if redistribution didn't fully compensate)
                            finalUtilizationDisplay = (
                              <span>
                                {finalUtil} <span className="text-warning text-xs">(-{changeFormatted}%)</span>
                              </span>
                            );
                          }
                        } else {
                          // Change is effectively zero (too subtle to display)
                          finalUtilizationDisplay = finalUtil;
                        }
                      } else {
                        // Vehicle wasn't active initially, show final value with indicator only if significant
                        if (vehicle.finalUtilization > epsilon) {
                          const changeFormatted = formatNumber(vehicle.finalUtilization, 1);
                          finalUtilizationDisplay = (
                            <span>
                              {finalUtil} <span className="text-ring text-xs">(+{changeFormatted}%)</span>
                            </span>
                          );
                        } else {
                          // Final utilization is effectively zero
                          finalUtilizationDisplay = '-';
                        }
                      }
                    }
                    // Only show change if it's significant (above threshold)
                    else if (Math.abs(change) > epsilon) {
                      const changeFormatted = formatNumber(Math.abs(change), 1);
                      if (change > 0) {
                        finalUtilizationDisplay = (
                          <span>
                            {finalUtil} <span className="text-success text-xs">(+{changeFormatted}%)</span>
                          </span>
                        );
                      } else {
                        finalUtilizationDisplay = (
                          <span>
                            {finalUtil} <span className="text-destructive text-xs">(-{changeFormatted}%)</span>
                          </span>
                        );
                      }
                    } else {
                      // Change is effectively zero (within threshold - too subtle to display)
                      finalUtilizationDisplay = finalUtil;
                    }
                  }
                }
                
                // Define custom sort order for status column (lower number = higher priority)
                const statusSortOrder: Record<string, number> = {
                  'Removed': 0,
                  'Replaced': 1,
                  'Received Workload': 2,
                  'Retained': 3
                };

                return {
                  ...vehicle,
                  initialUtilization,
                  finalUtilization: finalUtilizationDisplay,
                  status_sortKey: statusSortOrder[vehicle.status] ?? 99, // Custom sort key for status column
                  status: (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      vehicle.status === 'Retained'
                        ? 'bg-success/30 text-success border border-success/50'
                        : vehicle.status === 'Received Workload'
                        ? 'bg-ring/30 text-ring border border-ring/50'
                        : vehicle.status === 'Replaced'
                        ? 'bg-warning/30 text-warning border border-warning/50'
                        : 'bg-destructive/30 text-destructive border border-destructive/50'
                    }`}>
                      {vehicle.status}
                    </span>
                  ),
                  capitalSavings: vehicle.status === 'Removed' && vehicle.capitalSavings > 0
                    ? <span className="text-success font-semibold">{vehicle.capitalSavings.toLocaleString('en-GB')}</span>
                    : <span className="text-muted-foreground">-</span>
                };
              })}
            />
            </div>
          )}
        </div>

      </section>
      )}
    </div>
    </>
  );
}
